import * as i1 from '@angular/common';
import { CommonModule, DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, Input, HostBinding, NgModule, EventEmitter, Output, Directive, HostListener, ElementRef, ViewChild, TemplateRef, ContentChild, ContentChildren, Injectable, InjectionToken, Pipe, Optional, Inject, ChangeDetectionStrategy, Host } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i1$1 from '@angular/platform-browser';
import { v4 } from 'uuid';
import { Subscription, BehaviorSubject, timer, of, merge, fromEvent } from 'rxjs';
import { addMonths } from 'date-fns';
import Cookies from 'js-cookie';
import { ComponentPortal } from '@angular/cdk/portal';
import * as i1$2 from '@angular/cdk/overlay';
import { OverlayModule, CdkOverlayOrigin } from '@angular/cdk/overlay';
import * as i2 from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import * as i1$3 from '@angular/cdk/collections';
import { UniqueSelectionDispatcher } from '@angular/cdk/collections';
import { map, delay, takeUntil, repeat } from 'rxjs/operators';

const ICON_SIZES_TO_PIXELS = {
    xs: 16,
    s: 24,
    m: 36,
    l: 48,
};
/**
 * The {@link IconComponent} displays icons via its icon key property.
 * If the {@param iconKey} is an empty string, invalid or undefined the space is collapsed.
 */
class IconComponent {
    constructor() {
        /** @ignore */
        this.PREFIX = '#';
        /** @ignore */
        this.BASE_PATH = 'assets/xguide/icons.svg';
        /**
         * The icons can have different sizes (but all are multiples of 12px), common are 24x24 (s), 36x36 (m) and 48x48 (l).
         * https://trumpf-corp.gitlab.io/Styleguide/responsive-html-styleguide/#!/section/8.3
         * The default is M, so 36x36px.
         */
        this.size = 'm';
        /**
         * The color is used to change to color of the stroke and fill of the icon. The default is black.
         */
        this.color = '#000';
        /**
         * The outlined flag is used to draw an outlined version of the icon, e.g.
         * for usage as title icon in a dialog header. The default is false.
         */
        this.outlined = false;
    }
    /** @ignore */
    get iconSize() {
        return ICON_SIZES_TO_PIXELS[this.size];
    }
    get iconStroke() {
        return this.color;
    }
    get iconFill() {
        return !this.outlined ? this.color : 'none';
    }
    get icon() {
        return this.BASE_PATH + this.PREFIX + this.iconKey;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: IconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: IconComponent, selector: "xui-icon", inputs: { size: "size", iconKey: "iconKey", color: "color", outlined: "outlined" }, host: { properties: { "style.width.px": "this.iconSize", "style.min-width.px": "this.iconSize", "style.height.px": "this.iconSize", "style.min-height.px": "this.iconSize", "style.--icon-stroke": "this.iconStroke", "style.--icon-fill": "this.iconFill" } }, ngImport: i0, template: "<!-- The viewBox must be the default size. -->\n<svg class=\"icon\" *ngIf=\"iconKey != undefined\" viewBox=\"0 0 48 48\">\n  <use [attr.xlink:href]=\"icon\"></use>\n</svg>\n", styles: [":host{display:inline-flex;align-items:center;justify-content:center}:host .icon{flex-shrink:0;width:100%;height:100%}:host svg{fill:var(--icon-fill, none);stroke:var(--icon-stroke, #000)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: IconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-icon', template: "<!-- The viewBox must be the default size. -->\n<svg class=\"icon\" *ngIf=\"iconKey != undefined\" viewBox=\"0 0 48 48\">\n  <use [attr.xlink:href]=\"icon\"></use>\n</svg>\n", styles: [":host{display:inline-flex;align-items:center;justify-content:center}:host .icon{flex-shrink:0;width:100%;height:100%}:host svg{fill:var(--icon-fill, none);stroke:var(--icon-stroke, #000)}\n"] }]
        }], propDecorators: { size: [{
                type: Input
            }], iconKey: [{
                type: Input
            }], color: [{
                type: Input
            }], outlined: [{
                type: Input
            }], iconSize: [{
                type: HostBinding,
                args: ['style.width.px']
            }, {
                type: HostBinding,
                args: ['style.min-width.px']
            }, {
                type: HostBinding,
                args: ['style.height.px']
            }, {
                type: HostBinding,
                args: ['style.min-height.px']
            }], iconStroke: [{
                type: HostBinding,
                args: ['style.--icon-stroke']
            }], iconFill: [{
                type: HostBinding,
                args: ['style.--icon-fill']
            }] } });

const exportedDeclarations$1 = [IconComponent];
class IconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: IconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: IconModule, declarations: [IconComponent], imports: [CommonModule], exports: [IconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: IconModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: IconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [exportedDeclarations$1],
                    exports: [exportedDeclarations$1],
                }]
        }] });

/**
 * Renders a styleguide-styled button.
 *
 * **Example**
 *
 * ~~~html
 * <xui-button type="primary" iconKey="Cross">
 *  Button Label
 * </xui-button>
 * ~~~
 */
class ButtonComponent {
    constructor() {
        /** The role of the button affecting its visual appearance. */
        this.type = 'primary';
        /** Whether the control ignores user-interaction. */
        this.disabled = false;
    }
    /** @ignore */
    isEmpty(text) {
        return text?.trim() === '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: ButtonComponent, selector: "xui-button", inputs: { type: "type", iconKey: "iconKey", disabled: "disabled" }, host: { properties: { "class.disabled": "this.disabled" } }, ngImport: i0, template: "<button\n  [ngClass]=\"{\n    'interactive-element': true,\n    'btn-header': type === 'primary',\n    'basic-atom': type !== 'primary',\n    'primary-hover': iconKey !== undefined && type === 'primary',\n    'secondary-hover': iconKey !== undefined && type === 'secondary',\n    'trumpf-homepage': type === 'trumpf-homepage',\n    'icon-label-btn': iconKey !== undefined && !isEmpty(label.textContent),\n    'icon-btn': iconKey !== undefined && isEmpty(label.textContent)\n  }\"\n  [disabled]=\"disabled\"\n>\n  <xui-icon\n    *ngIf=\"iconKey !== undefined\"\n    class=\"icon\"\n    [iconKey]=\"iconKey\"\n    [color]=\"type === 'primary' ? '#fff' : '#000'\"\n  ></xui-icon>\n\n  <span #label class=\"button-label\" data-ngtx=\"button:ng-content\">\n    <ng-content></ng-content>\n  </span>\n</button>\n", styles: ["@charset \"UTF-8\";:host{display:inline-block;padding:8px}:host.disabled{pointer-events:none}:host button{display:block;overflow:hidden;position:relative;padding:0 16px;font-size:1rem;font-weight:700;text-overflow:ellipsis;white-space:nowrap;cursor:pointer;width:100%;height:100%}:host button .button-label{position:relative}:host button:not(:hover){border:1px solid #a1a1a1}:host button.icon-label-btn{display:flex}:host button.primary-hover:hover ::ng-deep svg{fill:#74b9ef;stroke:#74b9ef}:host button.secondary-hover:hover ::ng-deep svg{fill:#177ac9;stroke:#177ac9}:host button.trumpf-homepage{background:#bbd03a;transition:color .5s ease,background .5s ease;color:#fff}:host button.trumpf-homepage:hover{color:#bbd03a;background:#fff;border:1px solid #bbd03a}:host button.trumpf-homepage:focus{border:1px solid #bbd03a}:host button.trumpf-homepage:active:before{background:unset}@media only screen and (max-device-width: 1919px){:host{min-width:136px;height:40px}}@media only screen and (min-device-width: 1920px){:host{min-width:163px;height:48px}}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconComponent, selector: "xui-icon", inputs: ["size", "iconKey", "color", "outlined"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-button', template: "<button\n  [ngClass]=\"{\n    'interactive-element': true,\n    'btn-header': type === 'primary',\n    'basic-atom': type !== 'primary',\n    'primary-hover': iconKey !== undefined && type === 'primary',\n    'secondary-hover': iconKey !== undefined && type === 'secondary',\n    'trumpf-homepage': type === 'trumpf-homepage',\n    'icon-label-btn': iconKey !== undefined && !isEmpty(label.textContent),\n    'icon-btn': iconKey !== undefined && isEmpty(label.textContent)\n  }\"\n  [disabled]=\"disabled\"\n>\n  <xui-icon\n    *ngIf=\"iconKey !== undefined\"\n    class=\"icon\"\n    [iconKey]=\"iconKey\"\n    [color]=\"type === 'primary' ? '#fff' : '#000'\"\n  ></xui-icon>\n\n  <span #label class=\"button-label\" data-ngtx=\"button:ng-content\">\n    <ng-content></ng-content>\n  </span>\n</button>\n", styles: ["@charset \"UTF-8\";:host{display:inline-block;padding:8px}:host.disabled{pointer-events:none}:host button{display:block;overflow:hidden;position:relative;padding:0 16px;font-size:1rem;font-weight:700;text-overflow:ellipsis;white-space:nowrap;cursor:pointer;width:100%;height:100%}:host button .button-label{position:relative}:host button:not(:hover){border:1px solid #a1a1a1}:host button.icon-label-btn{display:flex}:host button.primary-hover:hover ::ng-deep svg{fill:#74b9ef;stroke:#74b9ef}:host button.secondary-hover:hover ::ng-deep svg{fill:#177ac9;stroke:#177ac9}:host button.trumpf-homepage{background:#bbd03a;transition:color .5s ease,background .5s ease;color:#fff}:host button.trumpf-homepage:hover{color:#bbd03a;background:#fff;border:1px solid #bbd03a}:host button.trumpf-homepage:focus{border:1px solid #bbd03a}:host button.trumpf-homepage:active:before{background:unset}@media only screen and (max-device-width: 1919px){:host{min-width:136px;height:40px}}@media only screen and (min-device-width: 1920px){:host{min-width:163px;height:48px}}\n"] }]
        }], propDecorators: { type: [{
                type: Input
            }], iconKey: [{
                type: Input
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.disabled']
            }] } });

const exportedDeclarations = [ButtonComponent];
class ButtonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: ButtonModule, declarations: [ButtonComponent], imports: [CommonModule, IconModule], exports: [ButtonComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ButtonModule, imports: [CommonModule, IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, IconModule],
                    declarations: [exportedDeclarations],
                    exports: [exportedDeclarations],
                }]
        }] });

/** Renders a list of `CaptionLabelComponent`s. */
class CaptionLabelListComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CaptionLabelListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: CaptionLabelListComponent, selector: "xui-caption-label-list", ngImport: i0, template: "<ng-content></ng-content>\n", styles: [":host{display:flex;flex-direction:column;gap:16px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CaptionLabelListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-caption-label-list', template: "<ng-content></ng-content>\n", styles: [":host{display:flex;flex-direction:column;gap:16px}\n"] }]
        }] });

/** Renders a label consisting of a "caption"-text being above a separator line and a "label"-text being below this separator. */
class CaptionLabelComponent {
    constructor() {
        /** The caption text (i.e. "headline") for this caption-label. */
        this.caption = '';
        /** The label text for this caption-label. */
        this.label = '';
        /** Whether the caption-label should be rendered in dark colors. */
        this.dark = false;
        /** Whether the caption-label blinks blueish after its caption or label was updated. */
        this.highlightOnUpdate = false;
        /** The duration as a css-time value (e.g. `0.3s`). Only takes effect if `highlightOnUpdate` is set to `true`. */
        this.highlightDuration = '0.3s';
        /** @ignore */
        this.highlighted = 'false';
    }
    /** @ignore */
    ngOnChanges(changes) {
        const label = 'label';
        const shouldBlink = label in changes && this.highlightOnUpdate;
        this.highlighted = shouldBlink ? 'true' : 'false';
    }
    /** @ignore */
    resetHighlight() {
        requestAnimationFrame(() => (this.highlighted = 'false'));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CaptionLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: CaptionLabelComponent, selector: "xui-caption-label", inputs: { caption: "caption", label: "label", dark: "dark", highlightOnUpdate: "highlightOnUpdate", highlightDuration: "highlightDuration" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"option-block\" [class.dark]=\"dark\">\n  <div\n    class=\"option-caption\"\n    [@highlightText]=\"{ value: highlighted, params: { duration: highlightDuration } }\"\n    (@highlightText.done)=\"resetHighlight()\"\n  >\n    {{ caption }}\n  </div>\n  <div [@highlightLine]=\"{ value: highlighted, params: { duration: highlightDuration } }\" class=\"hr-line\"></div>\n  <div class=\"option-label\" [@highlightText]=\"{ value: highlighted, params: { duration: highlightDuration } }\">\n    <div>{{ label }}</div>\n\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: ["@charset \"UTF-8\";:host{display:flex;flex-direction:column}.option-block{width:260px}.option-caption{font-size:.875em}.option-label{display:flex;align-items:center;justify-content:space-between;width:100%;font-size:1.25em;font-weight:700;margin-top:3px;word-break:break-all}.hr-line{margin-top:5px;margin-bottom:8px;border-top:1px solid #a1a1a1}.option-block.dark .hr-line{border-color:#fff}.option-block.dark .option-label{color:#fff}\n"], animations: [
            trigger('highlightText', [
                state('false', style({ color: 'inherit' })),
                state('true', style({ color: '#177ac9' })),
                transition('false => true', animate('0.3s')),
                transition('true => false', animate('0.3s {{ duration }}')),
            ]),
            trigger('highlightLine', [
                state('false', style({})),
                state('true', style({ outlineColor: '#177ac9' })),
                transition('false => true', animate('0.3s')),
                transition('true => false', animate('0.3s {{ duration }}')),
            ]),
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CaptionLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-caption-label', animations: [
                        trigger('highlightText', [
                            state('false', style({ color: 'inherit' })),
                            state('true', style({ color: '#177ac9' })),
                            transition('false => true', animate('0.3s')),
                            transition('true => false', animate('0.3s {{ duration }}')),
                        ]),
                        trigger('highlightLine', [
                            state('false', style({})),
                            state('true', style({ outlineColor: '#177ac9' })),
                            transition('false => true', animate('0.3s')),
                            transition('true => false', animate('0.3s {{ duration }}')),
                        ]),
                    ], template: "<div class=\"option-block\" [class.dark]=\"dark\">\n  <div\n    class=\"option-caption\"\n    [@highlightText]=\"{ value: highlighted, params: { duration: highlightDuration } }\"\n    (@highlightText.done)=\"resetHighlight()\"\n  >\n    {{ caption }}\n  </div>\n  <div [@highlightLine]=\"{ value: highlighted, params: { duration: highlightDuration } }\" class=\"hr-line\"></div>\n  <div class=\"option-label\" [@highlightText]=\"{ value: highlighted, params: { duration: highlightDuration } }\">\n    <div>{{ label }}</div>\n\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: ["@charset \"UTF-8\";:host{display:flex;flex-direction:column}.option-block{width:260px}.option-caption{font-size:.875em}.option-label{display:flex;align-items:center;justify-content:space-between;width:100%;font-size:1.25em;font-weight:700;margin-top:3px;word-break:break-all}.hr-line{margin-top:5px;margin-bottom:8px;border-top:1px solid #a1a1a1}.option-block.dark .hr-line{border-color:#fff}.option-block.dark .option-label{color:#fff}\n"] }]
        }], propDecorators: { caption: [{
                type: Input
            }], label: [{
                type: Input
            }], dark: [{
                type: Input
            }], highlightOnUpdate: [{
                type: Input
            }], highlightDuration: [{
                type: Input
            }] } });

const declarations$h = [CaptionLabelListComponent, CaptionLabelComponent];
class CaptionLabelModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CaptionLabelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: CaptionLabelModule, declarations: [CaptionLabelListComponent, CaptionLabelComponent], imports: [CommonModule, BrowserAnimationsModule], exports: [CaptionLabelListComponent, CaptionLabelComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CaptionLabelModule, imports: [CommonModule, BrowserAnimationsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CaptionLabelModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, BrowserAnimationsModule],
                    declarations: [declarations$h],
                    exports: [declarations$h],
                }]
        }] });

/**
 * The card component displays a title, a description and optional a preview image.
 * The description must be provided as DOM content of the component.
 *
 * ```html
 * <xui-card title="TheTitle">
 *  Here is some content...
 * </xui-card>
 * ```
 */
class CardComponent {
    get hasPreviewImage() {
        return this.preview !== undefined;
    }
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
        /**
         * The title text of the card.
         */
        this.title = '';
    }
    /** @ignore */
    ngOnChanges(changes) {
        const previewKey = 'preview';
        if (changes[previewKey] && this.preview) {
            this.safePreview = this.sanitizer.bypassSecurityTrustUrl(this.preview);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CardComponent, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: CardComponent, selector: "xui-card", inputs: { preview: "preview", title: "title" }, usesOnChanges: true, ngImport: i0, template: "<img\n  class=\"preview non-draggable\"\n  data-ngtx=\"card:image\"\n  [style.visibility]=\"hasPreviewImage ? 'visible' : 'hidden'\"\n  [src]=\"safePreview\"\n/>\n<section class=\"content\">\n  <div class=\"title\" data-ngtx=\"card:title\">{{ title }}</div>\n  <div class=\"description\" data-ngtx=\"card:content\"><ng-content></ng-content></div>\n</section>\n", styles: [":host{display:inline-flex;flex-direction:column;height:333px;width:256px;margin:16px;cursor:pointer;-webkit-user-select:none;user-select:none;box-shadow:0 2px 4px #0000000d,0 2px 8px #a1a1a166}:host:hover{box-shadow:0 2px 4px #2f5a7f66,0 2px 8px #51a7eb1a}:host:active{transform:translateY(2px);box-shadow:0 2px 4px #2f5a7f66,0 2px 8px #51a7eb1a}.preview{align-self:center;width:100%;height:205px;object-fit:cover;object-position:center}.content{padding:12px 10px 13px 15px}.title{margin-bottom:8px;font-size:16px;font-weight:700;line-height:initial;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.description{font-size:14px;font-weight:400;line-height:19px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;word-break:break-word}.non-draggable{-webkit-user-drag:none}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-card', template: "<img\n  class=\"preview non-draggable\"\n  data-ngtx=\"card:image\"\n  [style.visibility]=\"hasPreviewImage ? 'visible' : 'hidden'\"\n  [src]=\"safePreview\"\n/>\n<section class=\"content\">\n  <div class=\"title\" data-ngtx=\"card:title\">{{ title }}</div>\n  <div class=\"description\" data-ngtx=\"card:content\"><ng-content></ng-content></div>\n</section>\n", styles: [":host{display:inline-flex;flex-direction:column;height:333px;width:256px;margin:16px;cursor:pointer;-webkit-user-select:none;user-select:none;box-shadow:0 2px 4px #0000000d,0 2px 8px #a1a1a166}:host:hover{box-shadow:0 2px 4px #2f5a7f66,0 2px 8px #51a7eb1a}:host:active{transform:translateY(2px);box-shadow:0 2px 4px #2f5a7f66,0 2px 8px #51a7eb1a}.preview{align-self:center;width:100%;height:205px;object-fit:cover;object-position:center}.content{padding:12px 10px 13px 15px}.title{margin-bottom:8px;font-size:16px;font-weight:700;line-height:initial;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.description{font-size:14px;font-weight:400;line-height:19px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;word-break:break-word}.non-draggable{-webkit-user-drag:none}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.DomSanitizer }], propDecorators: { preview: [{
                type: Input
            }], title: [{
                type: Input
            }] } });

const declarations$g = [CardComponent];
class CardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: CardModule, declarations: [CardComponent], imports: [CommonModule], exports: [CardComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CardModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: declarations$g,
                    exports: declarations$g,
                }]
        }] });

/**
 * A styled checkbox accepting content as label.
 *
 * **Example**
 *
 * ~~~html
 * <xui-checkbox> Accept cookies </xui-checkbox>
 * ~~~
 */
class CheckboxComponent {
    constructor() {
        /**
         * Whether the control ignores user-interaction.
         */
        this.disabled = false;
        /**
         * Whether the checkbox is checked.
         */
        this.checked = false;
        /**
         * Emitted whenever the user changes the checkbox's checked-state.
         */
        this.checkedChange = new EventEmitter();
    }
    /** @ignore */
    change(checked) {
        this.checked = checked;
        this.checkedChange.emit(this.checked);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CheckboxComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: CheckboxComponent, selector: "xui-checkbox", inputs: { disabled: "disabled", checked: "checked" }, outputs: { checkedChange: "checkedChange" }, host: { properties: { "class.disabled": "this.disabled" } }, ngImport: i0, template: "<label class=\"checkbox show-label\">\n  <div class=\"container interactive-element\">\n    <input #cb type=\"checkbox\" [checked]=\"checked\" [disabled]=\"disabled\" (change)=\"change(cb.checked)\" />\n    <span class=\"checkmark basic-atom\"></span>\n  </div>\n  <span class=\"label\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: ["@charset \"UTF-8\";:host{display:inline-block}:host:hover .label,:host:active .label{color:#51a7eb}:host:hover .checkmark,:host:active .checkmark{color:#177ac9;border:1px solid #177ac9}:host:active .checkmark{background:#51a7eb1a}:host.disabled{pointer-events:none;opacity:.5}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-checkbox', template: "<label class=\"checkbox show-label\">\n  <div class=\"container interactive-element\">\n    <input #cb type=\"checkbox\" [checked]=\"checked\" [disabled]=\"disabled\" (change)=\"change(cb.checked)\" />\n    <span class=\"checkmark basic-atom\"></span>\n  </div>\n  <span class=\"label\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: ["@charset \"UTF-8\";:host{display:inline-block}:host:hover .label,:host:active .label{color:#51a7eb}:host:hover .checkmark,:host:active .checkmark{color:#177ac9;border:1px solid #177ac9}:host:active .checkmark{background:#51a7eb1a}:host.disabled{pointer-events:none;opacity:.5}\n"] }]
        }], propDecorators: { disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }, {
                type: Input
            }], checked: [{
                type: Input
            }], checkedChange: [{
                type: Output
            }] } });

const declarations$f = [CheckboxComponent];
class CheckboxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: CheckboxModule, declarations: [CheckboxComponent], imports: [CommonModule], exports: [CheckboxComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CheckboxModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [declarations$f],
                    exports: [declarations$f],
                }]
        }] });

class ClickOutsideDirective {
    constructor(element) {
        this.element = element;
        this.xuiClickOutside = new EventEmitter();
    }
    onClick(event, targetElement) {
        if (!targetElement) {
            return;
        }
        const clickedInside = this.element.nativeElement.contains(targetElement);
        if (!clickedInside) {
            this.xuiClickOutside.emit(event);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ClickOutsideDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.5", type: ClickOutsideDirective, selector: "[xuiClickOutside]", outputs: { xuiClickOutside: "xuiClickOutside" }, host: { listeners: { "document:click": "onClick($event,$event.target)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ClickOutsideDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[xuiClickOutside]',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { xuiClickOutside: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event', '$event.target']]
            }] } });

/**
 * Drop-down item to be used as child of a `Dropdown` or `Combobox` component.
 *
 * **Example:**
 *
 * ~~~html
 * <xui-dropdown>
 *  <xui-dropdown-item value="1"> </xui-dropdown-item>
 *  <xui-dropdown-item> {{ 2 }} </xui-dropdown-item>
 * </xui-dropdown>
 * ~~~
 */
class DropdownItemComponent {
    constructor() {
        /**
         * Event emitted whenever the item gets activated (e.g. clicked).
         */
        this.activate = new EventEmitter();
    }
    get textContent() {
        return this.textContentRef?.nativeElement.textContent ?? '';
    }
    /** @ignore */
    onClick() {
        this.activate.emit(this.value ?? this.textContent);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DropdownItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: DropdownItemComponent, selector: "xui-dropdown-item", inputs: { value: "value" }, outputs: { activate: "activate" }, viewQueries: [{ propertyName: "textContentRef", first: true, predicate: ["textContent"], descendants: true, read: ElementRef }], ngImport: i0, template: `
    <li class="basic-atom" data-ngtx="dropdown-item:body" (click)="onClick()">
      <span #textContent style="white-space: nowrap">
        <ng-content></ng-content>
      </span>

      <span *ngIf="value !== undefined">{{ value }}</span>
    </li>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DropdownItemComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'xui-dropdown-item',
                    template: `
    <li class="basic-atom" data-ngtx="dropdown-item:body" (click)="onClick()">
      <span #textContent style="white-space: nowrap">
        <ng-content></ng-content>
      </span>

      <span *ngIf="value !== undefined">{{ value }}</span>
    </li>
  `,
                }]
        }], propDecorators: { value: [{
                type: Input
            }], activate: [{
                type: Output
            }], textContentRef: [{
                type: ViewChild,
                args: ['textContent', { read: ElementRef }]
            }] } });

class DropdownComponent {
    constructor() {
        /**
         * The label to be shown when no item is selected.
         */
        this.label = '';
        /**
         * Whether the dropdown component should appear flat.
         */
        this.flat = false;
        /**
         * Whether this control ignores
         */
        this.disabled = false;
        /** @ignore */
        this._isOpen = false;
        /** @ignore */
        this.hasFocus = false;
    }
    /** @ignore */
    onFocusIn() {
        this.hasFocus = true;
    }
    /** @ignore */
    onFocusOut() {
        this.hasFocus = false;
    }
    get isOpen() {
        return this._isOpen && !this.disabled;
    }
    /**
     * Programmatically opens the dropdown component.
     */
    open() {
        this._isOpen = true;
    }
    /**
     * Programmatically toggles the dropdown component.
     */
    toggle() {
        this._isOpen = !this._isOpen;
    }
    /**
     * Programmatically closes the dropdown component.
     */
    close() {
        this._isOpen = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DropdownComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: DropdownComponent, selector: "xui-dropdown", inputs: { label: "label", flat: "flat", disabled: "disabled" }, host: { listeners: { "focusin": "onFocusIn()", "focusout": "onFocusOut()" } }, queries: [{ propertyName: "toggleTemplate", first: true, predicate: ["toggleTemplate"], descendants: true, read: TemplateRef }], ngImport: i0, template: "<div class=\"dropdown interactive-element\" [class.open]=\"isOpen\" [class.disabled]=\"disabled\" (xuiClickOutside)=\"close()\">\n  <button\n    class=\"basic-atom dropdown-toggle\"\n    [class.flat]=\"flat\"\n    [class.focus]=\"hasFocus\"\n    [disabled]=\"disabled\"\n    (click)=\"toggle()\"\n  >\n    <ng-container\n      [ngTemplateOutlet]=\"toggleTemplate || defaultToggleTemplate\"\n      [ngTemplateOutletContext]=\"{ $implicit: label }\"\n    ></ng-container>\n  </button>\n  <ul class=\"dropdown-list interactive-element\" [class.open]=\"isOpen\">\n    <ng-content></ng-content>\n  </ul>\n</div>\n\n<ng-template #defaultToggleTemplate let-label>\n  <span class=\"button-label\" data-ngtx=\"drop-down:label\">{{ label }}</span>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:inline-flex}.open{display:block}.dropdown{position:relative;min-width:unset!important;box-sizing:border-box;width:100%}.dropdown.disabled{cursor:default}.dropdown-toggle{width:100%;min-width:180px;font-size:16px;padding-right:34px;background:#fff!important;box-shadow:none;height:38px}.dropdown-toggle.flat{border:2px solid transparent;box-shadow:none}.dropdown-toggle:after{font-size:32px!important;right:8px;top:50%;transform:translateY(-50%)}.dropdown-toggle:focus,.dropdown-toggle.focus{border:2px solid #51a7eb!important}.dropdown-list{position:absolute;min-width:calc(100% - 18px);background:#fff;z-index:10}\n"], dependencies: [{ kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: ClickOutsideDirective, selector: "[xuiClickOutside]", outputs: ["xuiClickOutside"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-dropdown', template: "<div class=\"dropdown interactive-element\" [class.open]=\"isOpen\" [class.disabled]=\"disabled\" (xuiClickOutside)=\"close()\">\n  <button\n    class=\"basic-atom dropdown-toggle\"\n    [class.flat]=\"flat\"\n    [class.focus]=\"hasFocus\"\n    [disabled]=\"disabled\"\n    (click)=\"toggle()\"\n  >\n    <ng-container\n      [ngTemplateOutlet]=\"toggleTemplate || defaultToggleTemplate\"\n      [ngTemplateOutletContext]=\"{ $implicit: label }\"\n    ></ng-container>\n  </button>\n  <ul class=\"dropdown-list interactive-element\" [class.open]=\"isOpen\">\n    <ng-content></ng-content>\n  </ul>\n</div>\n\n<ng-template #defaultToggleTemplate let-label>\n  <span class=\"button-label\" data-ngtx=\"drop-down:label\">{{ label }}</span>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:inline-flex}.open{display:block}.dropdown{position:relative;min-width:unset!important;box-sizing:border-box;width:100%}.dropdown.disabled{cursor:default}.dropdown-toggle{width:100%;min-width:180px;font-size:16px;padding-right:34px;background:#fff!important;box-shadow:none;height:38px}.dropdown-toggle.flat{border:2px solid transparent;box-shadow:none}.dropdown-toggle:after{font-size:32px!important;right:8px;top:50%;transform:translateY(-50%)}.dropdown-toggle:focus,.dropdown-toggle.focus{border:2px solid #51a7eb!important}.dropdown-list{position:absolute;min-width:calc(100% - 18px);background:#fff;z-index:10}\n"] }]
        }], propDecorators: { label: [{
                type: Input
            }], flat: [{
                type: Input
            }], disabled: [{
                type: Input
            }], toggleTemplate: [{
                type: ContentChild,
                args: ['toggleTemplate', { read: TemplateRef }]
            }], onFocusIn: [{
                type: HostListener,
                args: ['focusin']
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout']
            }] } });

const declarations$e = [DropdownComponent, DropdownItemComponent];
const internals = [ClickOutsideDirective];
class DropDownModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DropDownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: DropDownModule, declarations: [DropdownComponent, DropdownItemComponent, ClickOutsideDirective], imports: [CommonModule], exports: [DropdownComponent, DropdownItemComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DropDownModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DropDownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [declarations$e, internals],
                    exports: [declarations$e],
                }]
        }] });

/**
 * A simple input field to allow the user to enter text values.
 */
class InputFieldComponent {
    constructor() {
        /**
         * The text value of this control.
         */
        this.text = '';
        /**
         * The placeholder text to be shown whenever the `text` is empty.
         */
        this.placeholder = '';
        /**
         * A hint text that can be set to show additional information, e.g. for validation purposes, when the user entered something wrong.
         * Defaults to an empty string.
         */
        this.hint = '';
        /**
         * Whether the user can only read from this control (e.g. copy the `text`-value).
         */
        this.readonly = false;
        /**
         * Whether the control ignores user-interactions.
         */
        this.disabled = false;
        /**
         * Event emitted whenever the text has changed and the control lost focus.
         */
        this.textChange = new EventEmitter();
        /** @ignore */
        this.inputId = v4();
    }
    /** @ignore */
    get classes() {
        const cssClasses = {
            readonly: this.readonly,
            disabled: this.disabled,
        };
        if (this.icon) {
            cssClasses[this.icon] = true;
        }
        return cssClasses;
    }
    /** @ignore */
    get input() {
        return this.inputRef.nativeElement;
    }
    /** @ignore */
    onChange(text) {
        this.text = text;
        this.textChange.emit(text);
    }
    /** @ignore for internal purposes only. */
    forceUpdateView(updated) {
        if (!this.input) {
            return;
        }
        this.input.value = updated;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: InputFieldComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: InputFieldComponent, selector: "xui-input-field", inputs: { text: "text", placeholder: "placeholder", label: "label", hint: "hint", icon: "icon", readonly: "readonly", disabled: "disabled" }, outputs: { textChange: "textChange" }, viewQueries: [{ propertyName: "inputRef", first: true, predicate: ["txt"], descendants: true, static: true }], ngImport: i0, template: "<!-- ATOM: Input Field -->\n<div class=\"input-field\" [ngClass]=\"classes\">\n  <section class=\"title-section\" *ngIf=\"label != null\">\n    <label [for]=\"inputId\" class=\"title\">{{ label }}</label>\n    <ng-content></ng-content>\n  </section>\n\n  <div class=\"input-container align-top\">\n    <input\n      #txt\n      [id]=\"inputId\"\n      [value]=\"text\"\n      [attr.disabled]=\"disabled ? true : null\"\n      [attr.placeholder]=\"placeholder\"\n      [attr.readOnly]=\"readonly ? true : null\"\n      (change)=\"onChange(txt.value)\"\n    />\n  </div>\n  <span *ngIf=\"hint\" class=\"helper-text\">{{ hint }}</span>\n</div>\n", styles: [":host{display:block}:host .title-section{display:flex;align-items:center;justify-content:space-between;min-height:24px;padding-bottom:3px}:host .title-section .title{flex:1 1 0}:host .input-container{display:flex;flex-direction:column}:host .input-container>*{flex:1 1 0}:host input{text-align:left;height:100%;padding-right:12px}:host .input-field.readonly label{border-bottom:1px solid black}:host .input-field.readonly input{border:1px solid transparent}:host .input-field.readonly input:hover,:host .input-field.readonly input:active{color:inherit;background:none}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: InputFieldComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-input-field', template: "<!-- ATOM: Input Field -->\n<div class=\"input-field\" [ngClass]=\"classes\">\n  <section class=\"title-section\" *ngIf=\"label != null\">\n    <label [for]=\"inputId\" class=\"title\">{{ label }}</label>\n    <ng-content></ng-content>\n  </section>\n\n  <div class=\"input-container align-top\">\n    <input\n      #txt\n      [id]=\"inputId\"\n      [value]=\"text\"\n      [attr.disabled]=\"disabled ? true : null\"\n      [attr.placeholder]=\"placeholder\"\n      [attr.readOnly]=\"readonly ? true : null\"\n      (change)=\"onChange(txt.value)\"\n    />\n  </div>\n  <span *ngIf=\"hint\" class=\"helper-text\">{{ hint }}</span>\n</div>\n", styles: [":host{display:block}:host .title-section{display:flex;align-items:center;justify-content:space-between;min-height:24px;padding-bottom:3px}:host .title-section .title{flex:1 1 0}:host .input-container{display:flex;flex-direction:column}:host .input-container>*{flex:1 1 0}:host input{text-align:left;height:100%;padding-right:12px}:host .input-field.readonly label{border-bottom:1px solid black}:host .input-field.readonly input{border:1px solid transparent}:host .input-field.readonly input:hover,:host .input-field.readonly input:active{color:inherit;background:none}\n"] }]
        }], propDecorators: { text: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], label: [{
                type: Input
            }], hint: [{
                type: Input
            }], icon: [{
                type: Input
            }], readonly: [{
                type: Input
            }], disabled: [{
                type: Input
            }], textChange: [{
                type: Output
            }], inputRef: [{
                type: ViewChild,
                args: ['txt', { static: true }]
            }] } });

const declarations$d = [InputFieldComponent];
class InputFieldModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: InputFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: InputFieldModule, declarations: [InputFieldComponent], imports: [CommonModule], exports: [InputFieldComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: InputFieldModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: InputFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [declarations$d],
                    exports: [declarations$d],
                }]
        }] });

/**
 * A simple combobox allowing the user to choose from given options.
 * Options can be passed in as ng-content in form of `xui-dropdown-item`s.
 *
 * ~~~html
 * <xui-combobox>
 *    <xui-dropdown-item *ngFor="let i of items">{{ i }}</xui-dropdown-item>
 * </xui-combobox>
 * ~~~
 */
class ComboboxComponent {
    constructor() {
        /**
         * Whether the control ignores user-interaction.
         */
        this.disabled = false;
        /**
         * The text shown in the combobox.
         */
        this.text = '';
        /**
         * The placeholder shown when the combobox's text is empty.
         */
        this.placeholder = '';
        /**
         * Emitted whenever the text changed and the control loses focus.
         */
        this.textChange = new EventEmitter();
        /** @ignore */
        this.active = false;
    }
    /** @ignore */
    ngAfterViewInit() {
        this.itemsChangeSub = new Subscription();
        this.itemsChangeSub.add(this.items.changes.subscribe(() => {
            this.subscribeToItemClicks();
        }));
        this.subscribeToItemClicks();
    }
    /** @ignore */
    ngOnDestroy() {
        this.itemsChangeSub?.unsubscribe();
        this.itemClickSub?.unsubscribe();
    }
    /** @ignore */
    onOutsideClick() {
        this.dropDown.close();
    }
    /** @ignore */
    onTextChange(text) {
        this.textChange.emit(text);
        this.dropDown?.close();
    }
    /** @ignore */
    subscribeToItemClicks() {
        this.itemClickSub?.unsubscribe();
        this.itemClickSub = new Subscription();
        this.items.forEach(item => {
            this.itemClickSub?.add(item.activate.subscribe(value => this.select(value)));
        });
    }
    /** @ignore */
    forceUpdateView(value) {
        setTimeout(() => this.inputField?.forceUpdateView(value));
    }
    /** @ignore */
    select(text) {
        this.text = text ?? '';
        this.textChange.emit(text);
        this.dropDown?.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ComboboxComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: ComboboxComponent, selector: "xui-combobox", inputs: { disabled: "disabled", text: "text", placeholder: "placeholder" }, outputs: { textChange: "textChange" }, queries: [{ propertyName: "items", predicate: DropdownItemComponent }], viewQueries: [{ propertyName: "dropDown", first: true, predicate: DropdownComponent, descendants: true }, { propertyName: "inputField", first: true, predicate: InputFieldComponent, descendants: true }], ngImport: i0, template: "<xui-dropdown\n  class=\"dropdown\"\n  [class.active]=\"active\"\n  [disabled]=\"disabled\"\n  (xuiClickOutside)=\"active = false\"\n  (pointerdown)=\"active = true\"\n  (pointerup)=\"active = false\"\n>\n  <ng-template #toggleTemplate>\n    <xui-input-field\n      class=\"dropdown-toggle\"\n      [disabled]=\"disabled\"\n      [placeholder]=\"placeholder\"\n      [(text)]=\"text\"\n      (textChange)=\"onTextChange($event)\"\n      data-ngtx=\"combobox:dropdown-toggle\"\n    ></xui-input-field>\n  </ng-template>\n\n  <ng-content></ng-content>\n</xui-dropdown>\n", styles: ["@charset \"UTF-8\";:host{display:block;box-sizing:border-box;height:44px}.dropdown::ng-deep{padding-right:28px;box-sizing:border-box;margin-top:2px}.dropdown::ng-deep,.dropdown::ng-deep .dropdown,.dropdown::ng-deep:active .dropdown{padding:0;min-width:unset!important}.dropdown::ng-deep .dropdown{padding-top:2px}.dropdown::ng-deep .dropdown-toggle{height:100%;padding-top:2px;padding-bottom:2px}.dropdown::ng-deep .dropdown-toggle:after{right:10px}.dropdown::ng-deep .dropdown-list{width:calc(100% - 2px)!important}.dropdown::ng-deep .input-field input{border:none;padding-top:0;padding-bottom:0}.dropdown::ng-deep .input-field-value,.dropdown::ng-deep .input-field{padding:0}.dropdown::ng-deep .input-container:after{display:none}.dropdown::ng-deep:focus-within button{border:2px solid #51a7eb}.active::ng-deep .dropdown-toggle,.active::ng-deep input{background:#f1f6fd!important;color:#177ac9!important}\n"], dependencies: [{ kind: "component", type: DropdownComponent, selector: "xui-dropdown", inputs: ["label", "flat", "disabled"] }, { kind: "component", type: InputFieldComponent, selector: "xui-input-field", inputs: ["text", "placeholder", "label", "hint", "icon", "readonly", "disabled"], outputs: ["textChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ComboboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-combobox', template: "<xui-dropdown\n  class=\"dropdown\"\n  [class.active]=\"active\"\n  [disabled]=\"disabled\"\n  (xuiClickOutside)=\"active = false\"\n  (pointerdown)=\"active = true\"\n  (pointerup)=\"active = false\"\n>\n  <ng-template #toggleTemplate>\n    <xui-input-field\n      class=\"dropdown-toggle\"\n      [disabled]=\"disabled\"\n      [placeholder]=\"placeholder\"\n      [(text)]=\"text\"\n      (textChange)=\"onTextChange($event)\"\n      data-ngtx=\"combobox:dropdown-toggle\"\n    ></xui-input-field>\n  </ng-template>\n\n  <ng-content></ng-content>\n</xui-dropdown>\n", styles: ["@charset \"UTF-8\";:host{display:block;box-sizing:border-box;height:44px}.dropdown::ng-deep{padding-right:28px;box-sizing:border-box;margin-top:2px}.dropdown::ng-deep,.dropdown::ng-deep .dropdown,.dropdown::ng-deep:active .dropdown{padding:0;min-width:unset!important}.dropdown::ng-deep .dropdown{padding-top:2px}.dropdown::ng-deep .dropdown-toggle{height:100%;padding-top:2px;padding-bottom:2px}.dropdown::ng-deep .dropdown-toggle:after{right:10px}.dropdown::ng-deep .dropdown-list{width:calc(100% - 2px)!important}.dropdown::ng-deep .input-field input{border:none;padding-top:0;padding-bottom:0}.dropdown::ng-deep .input-field-value,.dropdown::ng-deep .input-field{padding:0}.dropdown::ng-deep .input-container:after{display:none}.dropdown::ng-deep:focus-within button{border:2px solid #51a7eb}.active::ng-deep .dropdown-toggle,.active::ng-deep input{background:#f1f6fd!important;color:#177ac9!important}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }], text: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], textChange: [{
                type: Output
            }], dropDown: [{
                type: ViewChild,
                args: [DropdownComponent]
            }], inputField: [{
                type: ViewChild,
                args: [InputFieldComponent]
            }], items: [{
                type: ContentChildren,
                args: [DropdownItemComponent]
            }] } });

const declarations$c = [ComboboxComponent];
class ComboboxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ComboboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: ComboboxModule, declarations: [ComboboxComponent], imports: [CommonModule, DropDownModule, InputFieldModule], exports: [ComboboxComponent, DropDownModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ComboboxModule, imports: [CommonModule, DropDownModule, InputFieldModule, DropDownModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ComboboxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, DropDownModule, InputFieldModule],
                    declarations: [declarations$c],
                    exports: [declarations$c, DropDownModule],
                }]
        }] });

const XGUIDE_ELEMENT_IDS = {
    DASHBOARD_NEWS_IMAGE: 'DASHBOARD_NEWS_IMAGE',
    DASHBOARD_NEWS_TITLE: 'DASHBOARD_NEWS_TITLE',
    DASHBOARD_NEWS_DESCRIPTION: 'DASHBOARD_NEWS_DESCRIPTION',
    DASHBOARD_NEWS_PAGINATION: 'DASHBOARD_NEWS_PAGINATION',
    DASHBOARD_FIRST_STEPS: 'DASHBOARD_FIRST_STEPS',
    DASHBOARD_CALCULATION_EDIT: 'DASHBOARD_CALCULATION_EDIT',
    DASHBOARD_CALCULATION_SELECT: 'DASHBOARD_CALCULATION_SELECT',
    DASHBOARD_CALCULATION_NEW: 'DASHBOARD_CALCULATION_NEW',
};

function reverseString(value) {
    return value.split('').reverse().join('');
}

const translations = {
    XGUIDE: {
        DASHBOARD: {
            NEWS: {
                NO_NEWS: '',
            },
            GETTING_STARTED: {
                TAKE_THE_TOUR: '',
                TITLE: '',
            },
            WELCOME: {
                DESCRIPTION: '',
            },
        },
        DIALOG: {
            BACK: '',
            NEXT: '',
            CANCEL: '',
            FINISH: '',
            SAVE: '',
            OK: '',
        },
        MEDIA: {
            VIDEO_NOT_SUPPORTED: '',
        },
        PRIVACY: {
            IMPRINT: '',
            TITLE: '',
            DETAILED: {
                PARAGRAPH1: '',
                PARAGRAPH2: '',
                PARAGRAPH3: '',
                ALLOW_COOKIES_LABEL: '',
                FURTHER_DETAILS: '',
            },
            PRIVACY_POLICY_HREF: '',
            PRIVACY_POLICY: '',
            CHANGE_SETTINGS_LINK: '',
        },
        MENU: { CONTACT_US_BUTTON: '' },
    },
};
class Translations extends GenericClass() {
    constructor() {
        super();
        Object.assign(this, transformObjectToPath('', translations));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: Translations, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: Translations }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: Translations, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });
// ---------------------------
// Module internals
// ---------------------------
function GenericClass() {
    return class {
    };
}
function concatIfExistsPath(path, suffix) {
    return path ? `${path}.${suffix}` : suffix;
}
function transformObjectToPath(suffix, objectToTransformOrEndOfPath, path = '') {
    return typeof objectToTransformOrEndOfPath === 'object'
        ? Object.entries(objectToTransformOrEndOfPath).reduce((objectToTransform, [key, value]) => {
            objectToTransform[key] = transformObjectToPath(key, value, concatIfExistsPath(path, suffix));
            return objectToTransform;
        }, {})
        : concatIfExistsPath(path, suffix);
}

const UNKNOWN = 'UNKNOWN';

class UsageTrackingSendImplementationService {
}
const ClickElementMessageTypeId = new InjectionToken('ClickElementMessageTypeId token: Defining the message type to use when publishing click events.');

var DialogResult;
(function (DialogResult) {
    DialogResult["Ok"] = "Ok";
    DialogResult["Cancel"] = "Cancel";
})(DialogResult || (DialogResult = {}));

// TODO: should this be given from outside?
const XGUIDE_PRIVACY_COOKIE_NAME = 'XGuide.TrumpfPrivacySettings';

const Regex = {
    /** Regex matching every instance of `\"` within a string. */
    escapedDoubleQuotes: /\\\"/gi,
};
/**
 * Some cookie-json strings are saved like `'\"{ \"varName\": \"string-value\" }\"'`,
 * which causes `JSON.parse` to return a string, rather than an parsed object.
 *
 * This function turns the string into one that returns an object when passed to `JSON.parse`.
 *
 * ---
 * Example
 *
 * ~~~ts
 * const cookieJson = '\"{ \"varName\": \"string-value\" }\"';
 * JSON.parse(cookieJson); // => string
 * const parsableString = toJsonObjectString(cookieJson);
 * // => "{"varName": "string-value"}"
 * JSON.parse(parsableString); // => object
 * ~~~
 * @param cookie The cookie holding the value to convert into a parsable json string.
 */
function toJsonObjectString(value) {
    const jsonObjectString = trimCharacter('"', value).replace(Regex.escapedDoubleQuotes, '"');
    return jsonObjectString;
}
function trimCharacter(char, text) {
    if (!char || !text) {
        return text;
    }
    let result = text;
    while (result.startsWith(char)) {
        result = result.substring(char.length);
    }
    while (result.endsWith(char)) {
        result = result.substring(0, result.length - char.length);
    }
    return result;
}
function isValidCookie(cookie) {
    const trimmed = cookie?.trim() ?? '';
    return cookie !== undefined && trimmed.length > 0 && cookie.includes('=') && !trimmed.endsWith('=');
}
/**
 * Creates a Trumpf privacy cookie object which can be set via the CookieService.
 * @param allowTracking Whether or not usage tracking should be allowed.
 * @param created The timestamp to be set as cookie creation date.
 * @param expires The timestamp defining when the cookie expires.
 */
function createPrivacyCookie(allowTracking, created, expires) {
    return {
        consentDate: created,
        consentExpiry: expires,
        optionalCookies: {
            cookies: allowTracking,
        },
    };
}
/** A cookie lifespan calcluation function returning a timespan of three month (relative to `Date.now`). */
function threeMonthsLifespan(from) {
    return createLifespan(3, from);
}
/** A cookie lifespan calcluation function returning a timespan of twelve month (relative to `Date.now`). */
function twelveMonthsLifespan(from) {
    return createLifespan(12, from);
}
function createLifespan(months, from) {
    if (from === undefined) {
        from = Date.now();
    }
    return {
        created: from,
        expires: addMonthsTo(from, months),
    };
}
/**
 * Returns a timestamp relative to `Date.now`, which adds the specified number of months.
 * Just a simple adapter, that uses the date-fns `addMonths` function internally and
 * converting the result from `Date` to `Timestamp` (number).
 * @param now The timestamp representing now.
 * @param amount The number of months to add to the timestamp.
 */
function addMonthsTo(now, amount) {
    return addMonths(now, amount).getTime();
}

class TranslationPipeImplementation {
}
class ImageUrlPipeImplementation {
}
class ApplicationInfo {
}

/**
 * For XGuide internal use only. Please don't use it in any guide frontend.
 * @internal
 */
class TranslationBridgePipe {
    constructor(translationImpl) {
        this.translationImpl = translationImpl;
    }
    transform(value, ...args) {
        return this.translationImpl?.transform(value, ...args) ?? value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TranslationBridgePipe, deps: [{ token: TranslationPipeImplementation, optional: true }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: TranslationBridgePipe, name: "translate", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TranslationBridgePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'translate',
                    pure: false, // pure = false required to update the value when the promise is resolved
                }]
        }], ctorParameters: () => [{ type: TranslationPipeImplementation, decorators: [{
                    type: Optional
                }] }] });

/** DialogHostedView showing a privacy popup asking for permission to set cookies. */
class PrivacyCookiePopupComponent {
    constructor(translations) {
        this.translations = translations;
        this.closeDialog = new EventEmitter();
    }
    initDialog(context) {
        this.showSettingsPopup = context.showSettingsPopup;
    }
    accept() {
        this.closeDialog.emit({
            result: DialogResult.Ok,
            payload: 'accepted',
        });
    }
    reject() {
        this.closeDialog.emit({
            result: DialogResult.Ok,
            payload: 'denied',
        });
    }
    showPopupDialog() {
        this.showSettingsPopup?.();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: PrivacyCookiePopupComponent, deps: [{ token: Translations }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: PrivacyCookiePopupComponent, selector: "ng-component", ngImport: i0, template: "<div class=\"container popup__inner gr-10 push-1\">\n  <div class=\"close popup__inner--close\" (click)=\"reject()\">\n    <i class=\"icon-close\" data-popup-close=\"\" style=\"background-image: none\">\n      <svg data-name=\"Ebene 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 29.7 29.7\">\n        <path\n          d=\"M29.7 1.41L28.28 0 14.85 13.44 1.41 0 0 1.41l13.44 13.44L0 28.28l1.41 1.42 13.44-13.44L28.28 29.7l1.42-1.42-13.44-13.43L29.7 1.41z\"\n        ></path>\n      </svg>\n    </i>\n  </div>\n\n  <div class=\"popup__inner__content gr-10 push-1 no-gutter\">\n    <div class=\"popup__inner--title\">\n      {{ translations.XGUIDE.PRIVACY.TITLE | translate }}\n    </div>\n\n    <div class=\"popup__inner--copy text\">\n      {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH1 | translate }}\n      <a (click)=\"showPopupDialog()\">{{ translations.XGUIDE.PRIVACY.CHANGE_SETTINGS_LINK | translate }}</a>\n      {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH2 | translate }}\n      <a [href]=\"translations.XGUIDE.PRIVACY.PRIVACY_POLICY_HREF | translate\" target=\"_blank\">{{\n        translations.XGUIDE.PRIVACY.PRIVACY_POLICY | translate\n      }}</a>\n    </div>\n\n    <div class=\"popup__inner--buttons\">\n      <a (click)=\"accept()\" class=\"accept-button popup__inner--button btn btn-green\">\n        {{ translations.XGUIDE.DIALOG.OK | translate }}\n      </a>\n    </div>\n\n    <div class=\"popup__inner--links\">\n      <a routerLink=\"/about/imprint\">\n        {{ translations.XGUIDE.PRIVACY.IMPRINT | translate }}\n      </a>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;align-items:center;justify-content:center;width:100%}.container{box-shadow:0 0 5px #0004;align-items:center;padding:45px 0}.close{width:15px;height:15px;cursor:pointer}.text{line-height:2rem}.btn{cursor:pointer;font-size:1em!important}a{cursor:pointer}.block-dropdown .btn-dropout,.block-iconList,.block-product-slider-entry,.block-serviceIcon,.card,.card__frame,.filter-checkbox-wrapper,.footer,.gr-0,.gr-1,.gr-10,.gr-11,.gr-12,.gr-12on12,.gr-2,.gr-3,.gr-4,.gr-4on12,.gr-5,.gr-6,.gr-6on12,.gr-7,.gr-8,.gr-8on12,.gr-9,.innoCal-bottom div .content,.innoCal-bottom div .image,.location-list>li,.location-list>li.location-list-doubble,.machine-detail .manual-entry,.marginalContainer,.notification-myTrumpf-small .notification-text,.stepbar,.stepbar.steps-3 .step,.stepbar.steps-4 .step{display:inline-block;min-height:1px;box-sizing:border-box;vertical-align:top;padding-right:15px;padding-left:15px}.gr-10{width:83.3333333333%}.popup__inner{display:flex;position:relative;transition:margin-top .5s ease,opacity .5s ease;max-width:600px;margin:0 auto;background:#fff;flex-direction:column;overflow:auto;max-height:calc(100% - 30px)}*,*:before,*:after{-webkit-box-sizing:inherit;box-sizing:inherit}.popup__inner--close{position:absolute;top:15px;right:15px}.popup__inner--close i{position:static;display:block}.popup__inner--buttons{margin:25px 0;text-align:center}.popup__inner--buttons .popup__inner--button:last-child{margin-right:0}.popup__inner--buttons .popup__inner--button:first-child{margin-left:0}.popup__inner--buttons .popup__inner--button{margin:0 5px}.popup__inner--copy{font-size:16px;color:#747474;margin-bottom:10px}.btn{font-size:.75rem;padding:12px 37px;border:1px solid #131313;background:0 0;color:#131313;transition:color .5s ease,background .5s ease;border-radius:0;line-height:1.5em}.btn,.btn-zoom{text-decoration:none;display:inline-block;text-align:center}.btn,.result-set__numbers,.status,.tagList__removeTagsButton,.tagList__title,strong{font-family:Frutiger-Bold,sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility}.btn,.status,.stufen-card-vision p,.tagNoBtn{text-transform:uppercase}a{color:#727272;transition:color .5s ease;text-decoration:underline;font-family:Frutiger-Light,sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility;background:0 0}.btn-green{border:1px solid transparent;background-color:#bbd03a;color:#fff;fill:#fff}.popup__inner--title{font-size:34px;text-align:center;margin-bottom:15px}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner--title{font-size:28px}}\n"], dependencies: [{ kind: "pipe", type: TranslationBridgePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: PrivacyCookiePopupComponent, decorators: [{
            type: Component,
            args: [{ template: "<div class=\"container popup__inner gr-10 push-1\">\n  <div class=\"close popup__inner--close\" (click)=\"reject()\">\n    <i class=\"icon-close\" data-popup-close=\"\" style=\"background-image: none\">\n      <svg data-name=\"Ebene 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 29.7 29.7\">\n        <path\n          d=\"M29.7 1.41L28.28 0 14.85 13.44 1.41 0 0 1.41l13.44 13.44L0 28.28l1.41 1.42 13.44-13.44L28.28 29.7l1.42-1.42-13.44-13.43L29.7 1.41z\"\n        ></path>\n      </svg>\n    </i>\n  </div>\n\n  <div class=\"popup__inner__content gr-10 push-1 no-gutter\">\n    <div class=\"popup__inner--title\">\n      {{ translations.XGUIDE.PRIVACY.TITLE | translate }}\n    </div>\n\n    <div class=\"popup__inner--copy text\">\n      {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH1 | translate }}\n      <a (click)=\"showPopupDialog()\">{{ translations.XGUIDE.PRIVACY.CHANGE_SETTINGS_LINK | translate }}</a>\n      {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH2 | translate }}\n      <a [href]=\"translations.XGUIDE.PRIVACY.PRIVACY_POLICY_HREF | translate\" target=\"_blank\">{{\n        translations.XGUIDE.PRIVACY.PRIVACY_POLICY | translate\n      }}</a>\n    </div>\n\n    <div class=\"popup__inner--buttons\">\n      <a (click)=\"accept()\" class=\"accept-button popup__inner--button btn btn-green\">\n        {{ translations.XGUIDE.DIALOG.OK | translate }}\n      </a>\n    </div>\n\n    <div class=\"popup__inner--links\">\n      <a routerLink=\"/about/imprint\">\n        {{ translations.XGUIDE.PRIVACY.IMPRINT | translate }}\n      </a>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;align-items:center;justify-content:center;width:100%}.container{box-shadow:0 0 5px #0004;align-items:center;padding:45px 0}.close{width:15px;height:15px;cursor:pointer}.text{line-height:2rem}.btn{cursor:pointer;font-size:1em!important}a{cursor:pointer}.block-dropdown .btn-dropout,.block-iconList,.block-product-slider-entry,.block-serviceIcon,.card,.card__frame,.filter-checkbox-wrapper,.footer,.gr-0,.gr-1,.gr-10,.gr-11,.gr-12,.gr-12on12,.gr-2,.gr-3,.gr-4,.gr-4on12,.gr-5,.gr-6,.gr-6on12,.gr-7,.gr-8,.gr-8on12,.gr-9,.innoCal-bottom div .content,.innoCal-bottom div .image,.location-list>li,.location-list>li.location-list-doubble,.machine-detail .manual-entry,.marginalContainer,.notification-myTrumpf-small .notification-text,.stepbar,.stepbar.steps-3 .step,.stepbar.steps-4 .step{display:inline-block;min-height:1px;box-sizing:border-box;vertical-align:top;padding-right:15px;padding-left:15px}.gr-10{width:83.3333333333%}.popup__inner{display:flex;position:relative;transition:margin-top .5s ease,opacity .5s ease;max-width:600px;margin:0 auto;background:#fff;flex-direction:column;overflow:auto;max-height:calc(100% - 30px)}*,*:before,*:after{-webkit-box-sizing:inherit;box-sizing:inherit}.popup__inner--close{position:absolute;top:15px;right:15px}.popup__inner--close i{position:static;display:block}.popup__inner--buttons{margin:25px 0;text-align:center}.popup__inner--buttons .popup__inner--button:last-child{margin-right:0}.popup__inner--buttons .popup__inner--button:first-child{margin-left:0}.popup__inner--buttons .popup__inner--button{margin:0 5px}.popup__inner--copy{font-size:16px;color:#747474;margin-bottom:10px}.btn{font-size:.75rem;padding:12px 37px;border:1px solid #131313;background:0 0;color:#131313;transition:color .5s ease,background .5s ease;border-radius:0;line-height:1.5em}.btn,.btn-zoom{text-decoration:none;display:inline-block;text-align:center}.btn,.result-set__numbers,.status,.tagList__removeTagsButton,.tagList__title,strong{font-family:Frutiger-Bold,sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility}.btn,.status,.stufen-card-vision p,.tagNoBtn{text-transform:uppercase}a{color:#727272;transition:color .5s ease;text-decoration:underline;font-family:Frutiger-Light,sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility;background:0 0}.btn-green{border:1px solid transparent;background-color:#bbd03a;color:#fff;fill:#fff}.popup__inner--title{font-size:34px;text-align:center;margin-bottom:15px}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner--title{font-size:28px}}\n"] }]
        }], ctorParameters: () => [{ type: Translations }] });

/** DialogHostedView showing a privacy settings popup allowing the user to change privacy settings. */
class PrivacySettingsPopupComponent {
    constructor(translations) {
        this.translations = translations;
        this.closeDialog = new EventEmitter();
        this.cookiesAllowed = false;
    }
    initDialog(currentState) {
        this.cookiesAllowed = currentState;
    }
    save(accept) {
        const payload = accept ? 'accepted' : 'denied';
        this.closeDialog.emit({
            result: DialogResult.Ok,
            payload,
        });
    }
    close() {
        this.closeDialog.emit({ result: DialogResult.Cancel });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: PrivacySettingsPopupComponent, deps: [{ token: Translations }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: PrivacySettingsPopupComponent, selector: "ng-component", ngImport: i0, template: "<div class=\"container popup__inner gr-10 push-1 no-gutter-left no-gutter-right\">\n  <div class=\"popup__inner--close\">\n    <i class=\"icon-close close\" style=\"background-image: none\" (click)=\"close()\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 29.7 29.7\">\n        <path\n          d=\"M29.7 1.41L28.28 0 14.85 13.44 1.41 0 0 1.41l13.44 13.44L0 28.28l1.41 1.42 13.44-13.44L28.28 29.7l1.42-1.42-13.44-13.43L29.7 1.41z\"\n        ></path>\n      </svg>\n    </i>\n  </div>\n\n  <div class=\"popup__inner__content gr-10 push-1 no-gutter\">\n    <div class=\"popup__inner--title\">\n      {{ translations.XGUIDE.PRIVACY.TITLE | translate }}\n    </div>\n\n    <div class=\"popup__inner--copy\">\n      <p>\n        {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH1 | translate }}\n      </p>\n      <p>\n        {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH2 | translate }}\n      </p>\n      <p>\n        {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH3 | translate }}\n      </p>\n    </div>\n\n    <xui-checkbox #checkbox [checked]=\"cookiesAllowed\">\n      <span class=\"label\">\n        {{ translations.XGUIDE.PRIVACY.DETAILED.ALLOW_COOKIES_LABEL | translate }}\n      </span>\n    </xui-checkbox>\n\n    <div class=\"popup__inner--texts-small\">\n      <p>\n        {{ translations.XGUIDE.PRIVACY.DETAILED.FURTHER_DETAILS | translate }}\n\n        <a [href]=\"translations.XGUIDE.PRIVACY.PRIVACY_POLICY_HREF | translate\" target=\"_blank\">\n          {{ translations.XGUIDE.PRIVACY.PRIVACY_POLICY | translate }}\n        </a>\n      </p>\n    </div>\n    <div class=\"popup__inner--buttons\">\n      <a class=\"popup__inner--button btn btn-green\" (click)=\"save(checkbox.checked)\">\n        {{ translations.XGUIDE.DIALOG.SAVE | translate }}\n      </a>\n    </div>\n    <div class=\"popup__inner--links\">\n      <a routerLink=\"/about/imprint\">\n        {{ translations.XGUIDE.PRIVACY.IMPRINT | translate }}\n      </a>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;align-items:center;justify-content:center;width:100%}.container{box-shadow:0 0 5px #0004}.label{text-transform:uppercase}.close{position:absolute;right:15px;top:15px;width:15px;cursor:pointer}.btn{cursor:pointer;font-size:1em!important}.popup__inner{display:flex;font-size:100%;line-height:1.6;min-height:1px;width:83.3333333333%;box-sizing:border-box;vertical-align:top;position:relative;margin:0 auto;background:#fff;transition:margin-top .5s ease,opacity .5s ease;overflow:auto;padding:45px 0 0;max-height:calc(100% - 30px);max-width:600px}.popup--privacy .popup__inner--close{position:absolute;top:15px;right:15px}.popup__inner__content{position:relative;display:inline-block;min-height:1px;box-sizing:border-box;vertical-align:top;left:8.3333333333%;width:83.3333333333%;padding:0}.popup__inner--title{font-size:34px;text-align:center;margin-bottom:15px}.popup__inner--copy{font-size:16px;color:#747474;margin-bottom:10px}.popup__inner--texts-small{font-size:13px;text-transform:uppercase;margin-bottom:5px}.popup__inner--buttons{margin:25px 0;text-align:center}.popup__inner--buttons .btn{font-size:.75rem;padding:12px 37px;background:0 0;transition:color .5s ease,background .5s ease;border-radius:0;line-height:1.5em;text-decoration:none;display:inline-block;text-align:center;text-transform:uppercase;border:1px solid transparent;background-color:#bbd03a;color:#fff;fill:#fff;margin:0 5px}.popup__inner--links{font-size:13px;text-transform:uppercase;margin-bottom:55px}a{color:#727272;transition:color .5s ease;text-decoration:underline;font-family:Frutiger-Light,sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility;background:0 0}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner{direction:ltr}}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner--title{font-size:28px}}\n"], dependencies: [{ kind: "component", type: CheckboxComponent, selector: "xui-checkbox", inputs: ["disabled", "checked"], outputs: ["checkedChange"] }, { kind: "pipe", type: TranslationBridgePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: PrivacySettingsPopupComponent, decorators: [{
            type: Component,
            args: [{ template: "<div class=\"container popup__inner gr-10 push-1 no-gutter-left no-gutter-right\">\n  <div class=\"popup__inner--close\">\n    <i class=\"icon-close close\" style=\"background-image: none\" (click)=\"close()\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 29.7 29.7\">\n        <path\n          d=\"M29.7 1.41L28.28 0 14.85 13.44 1.41 0 0 1.41l13.44 13.44L0 28.28l1.41 1.42 13.44-13.44L28.28 29.7l1.42-1.42-13.44-13.43L29.7 1.41z\"\n        ></path>\n      </svg>\n    </i>\n  </div>\n\n  <div class=\"popup__inner__content gr-10 push-1 no-gutter\">\n    <div class=\"popup__inner--title\">\n      {{ translations.XGUIDE.PRIVACY.TITLE | translate }}\n    </div>\n\n    <div class=\"popup__inner--copy\">\n      <p>\n        {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH1 | translate }}\n      </p>\n      <p>\n        {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH2 | translate }}\n      </p>\n      <p>\n        {{ translations.XGUIDE.PRIVACY.DETAILED.PARAGRAPH3 | translate }}\n      </p>\n    </div>\n\n    <xui-checkbox #checkbox [checked]=\"cookiesAllowed\">\n      <span class=\"label\">\n        {{ translations.XGUIDE.PRIVACY.DETAILED.ALLOW_COOKIES_LABEL | translate }}\n      </span>\n    </xui-checkbox>\n\n    <div class=\"popup__inner--texts-small\">\n      <p>\n        {{ translations.XGUIDE.PRIVACY.DETAILED.FURTHER_DETAILS | translate }}\n\n        <a [href]=\"translations.XGUIDE.PRIVACY.PRIVACY_POLICY_HREF | translate\" target=\"_blank\">\n          {{ translations.XGUIDE.PRIVACY.PRIVACY_POLICY | translate }}\n        </a>\n      </p>\n    </div>\n    <div class=\"popup__inner--buttons\">\n      <a class=\"popup__inner--button btn btn-green\" (click)=\"save(checkbox.checked)\">\n        {{ translations.XGUIDE.DIALOG.SAVE | translate }}\n      </a>\n    </div>\n    <div class=\"popup__inner--links\">\n      <a routerLink=\"/about/imprint\">\n        {{ translations.XGUIDE.PRIVACY.IMPRINT | translate }}\n      </a>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;align-items:center;justify-content:center;width:100%}.container{box-shadow:0 0 5px #0004}.label{text-transform:uppercase}.close{position:absolute;right:15px;top:15px;width:15px;cursor:pointer}.btn{cursor:pointer;font-size:1em!important}.popup__inner{display:flex;font-size:100%;line-height:1.6;min-height:1px;width:83.3333333333%;box-sizing:border-box;vertical-align:top;position:relative;margin:0 auto;background:#fff;transition:margin-top .5s ease,opacity .5s ease;overflow:auto;padding:45px 0 0;max-height:calc(100% - 30px);max-width:600px}.popup--privacy .popup__inner--close{position:absolute;top:15px;right:15px}.popup__inner__content{position:relative;display:inline-block;min-height:1px;box-sizing:border-box;vertical-align:top;left:8.3333333333%;width:83.3333333333%;padding:0}.popup__inner--title{font-size:34px;text-align:center;margin-bottom:15px}.popup__inner--copy{font-size:16px;color:#747474;margin-bottom:10px}.popup__inner--texts-small{font-size:13px;text-transform:uppercase;margin-bottom:5px}.popup__inner--buttons{margin:25px 0;text-align:center}.popup__inner--buttons .btn{font-size:.75rem;padding:12px 37px;background:0 0;transition:color .5s ease,background .5s ease;border-radius:0;line-height:1.5em;text-decoration:none;display:inline-block;text-align:center;text-transform:uppercase;border:1px solid transparent;background-color:#bbd03a;color:#fff;fill:#fff;margin:0 5px}.popup__inner--links{font-size:13px;text-transform:uppercase;margin-bottom:55px}a{color:#727272;transition:color .5s ease;text-decoration:underline;font-family:Frutiger-Light,sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility;background:0 0}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner{direction:ltr}}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner--title{font-size:28px}}\n"] }]
        }], ctorParameters: () => [{ type: Translations }] });

class CookieService {
    get(name, parseJSON = false) {
        let value = Cookies.get(name);
        if (value && parseJSON) {
            const jsonObjectString = toJsonObjectString(value);
            value = JSON.parse(jsonObjectString);
        }
        return value;
    }
    getAll() {
        return Cookies.get();
    }
    set(name, value, opts = {}) {
        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
        Cookies.set(name, stringValue, opts);
    }
    remove(name) {
        Cookies.remove(name);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CookieService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CookieService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CookieService, decorators: [{
            type: Injectable
        }] });

/**
 * Service class dealing with opening dialogs, that show custom views and wait for their results.
 */
class DialogService {
    constructor(overlay) {
        this.overlay = overlay;
        /** @ignore */
        this.isActiveSubject = new BehaviorSubject(false);
        /**
         * This observable determines whether there is a dialog currently opened. It can be used to
         * blur the background of the app-component whenever the value changes to `true`.
         */
        this.isActive = this.isActiveSubject.asObservable();
    }
    /**
     * Opens a new dialog that shows the specified view. This method is awaitable, and returns the dialog's result (if any).
     *
     * **Example**:
     *
     * ~~~ts
     * const dialogResult = await this.dialogService.open(UpdatePersonViewComponent, { name: "Max Muster", age: 42 });
     *
     * if(dialogResult.result === DialogResult.Ok) {
     *    this.person = dialogResult.payload;
     * } else {
     *    alert('User cancelled dialog...');
     * }
     * ~~~
     *
     * ---
     * @param view A view component implementing the `DialogHostedView` interface. This view will be shown inside the dialog.
     * @param initialData (optional) The initial data that is passed to the view.
     */
    async open(view, initialData) {
        return new Promise(resolve => {
            this.showDialog(view, initialData, resolve);
        });
    }
    /**
     * Opens a new dialog and returns a reference object to this dialog. This function is useful whenever you need
     * to control (e.g. closing) the dialog from outside.
     *
     * ---
     *
     * **Example:**
     *
     * ~~~ts
     * const dialogRef = dialog.openWithRef(MyView);
     * settimeout(() => {
     *   console.log('auto-closing dialog after 5 seconds!');
     *   dialogRef.close({ result: DialogResult.Cancel })
     * }, 5000)
     * ~~~
     *
     * ---
     *
     * **Please note:** Forgetting a dialog reference that has not been closed, can cause memory leaks.
     * When obtaining a dialog ref, please make sure, that the reference will be closed at some point in time
     * (either by the shown component's `dialogClose` event emitter, or by manually calling `dialogRef.close(...)`).
     *
     * ---
     * @param view A component implementing the `DialogHostedView` interface. The view will be shown in the dialog.
     * @param initialData (optional) The initial data that is passed to the view.
     */
    openWithRef(view, initialData) {
        let closeEvent;
        const dialogPromise = new Promise(resolve => {
            closeEvent = this.showDialog(view, initialData, resolve);
        });
        return {
            result: dialogPromise,
            close: result => {
                closeEvent.emit(result);
            },
        };
    }
    showDialog(view, initialData, resolve) {
        const overlayRef = this.overlay.create({
            panelClass: 'dialog-overlay',
        });
        const componentPortal = new ComponentPortal(view);
        const componentRef = overlayRef.attach(componentPortal);
        const component = componentRef.instance;
        component.initDialog(initialData);
        this.isActiveSubject.next(true);
        const closeSubscription = component.closeDialog.subscribe((result) => {
            closeSubscription.unsubscribe();
            overlayRef.detach();
            this.isActiveSubject.next(false);
            resolve(result);
        });
        return component.closeDialog;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DialogService, deps: [{ token: i1$2.Overlay }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DialogService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DialogService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1$2.Overlay }] });

/** Service responsible for determining and managing whether it is allowed to track the current user's usage behavior. */
class UsageTrackingPermissionService {
    get trackingAllowed() {
        const trumpfSetting = this.getPrivacySetting(XGUIDE_PRIVACY_COOKIE_NAME);
        // Don't send any analytics if user denied it or we a running in HMI mode
        if (trumpfSetting === 'denied') {
            return false;
        }
        const weldguideSetting = this.getPrivacySetting(XGUIDE_PRIVACY_COOKIE_NAME);
        return weldguideSetting === 'accepted';
    }
    get userAlreadyAsked() {
        const cookieValue = this.getPrivacySetting(XGUIDE_PRIVACY_COOKIE_NAME);
        const isConsentDenied = cookieValue === 'denied';
        const privacySettingsAreSet = !!cookieValue;
        return isConsentDenied || privacySettingsAreSet;
    }
    /** @ignore */
    constructor(cookies, dialog) {
        this.cookies = cookies;
        this.dialog = dialog;
    }
    /**
     * Shows a popup informing the user about privacy choices and cookies.
     * @returns The response of the user, if he accepted or declined.
     */
    async askUserForPermission() {
        this.closeUserPermissionPopup();
        this.permissionDialogRef = this.dialog.openWithRef(PrivacyCookiePopupComponent, {
            showSettingsPopup: async () => {
                await this.showPrivacySettings();
            },
        });
        const dialogResult = await this.permissionDialogRef.result;
        /*
          DialogResult.Ok includes both user decisions: "accepted" and "denied".
          Cancel means, that the user did not decide, but the dialog
          was cancelled for some reason (e.g. by navigation, reload).
         */
        if (dialogResult.result === DialogResult.Ok) {
            const cookieLifespan = this.getCookieLifespan(dialogResult);
            // langju: fragile code. Expects PrivacyCookiePopupComponent to throw if undefined payload.
            this.setPermission(dialogResult.payload, cookieLifespan);
        }
        return dialogResult?.payload ?? 'denied';
    }
    /**
     * Shows a popup to ask the user for usage-tracking permission.
     * @returns The response of the user, if they accepted or declined.
     */
    async showPrivacySettings() {
        this.closeUserPermissionPopup();
        this.settingsDialogRef = this.dialog.openWithRef(PrivacySettingsPopupComponent, this.trackingAllowed);
        const dialogResult = await this.settingsDialogRef.result;
        /*
          DialogResult.Ok includes both user decisions: "accepted" and "denied".
          Cancel means, that the user did not decide, but the dialog
          was cancelled for some reason (e.g. by navigation, reload).
         */
        if (dialogResult.result === DialogResult.Ok) {
            const cookieLifespan = this.getCookieLifespan(dialogResult);
            // langju: fragile code. Expects PrivacySettingsPopupComponent to throw if undefined payload.
            this.setPermission(dialogResult.payload, cookieLifespan);
        }
        else if (!this.userAlreadyAsked) {
            this.askUserForPermission();
        }
        return dialogResult?.payload ?? 'denied';
    }
    /**
     * Closes the permission popup, if it is open.
     */
    closeUserPermissionPopup() {
        this.permissionDialogRef?.close({ result: DialogResult.Cancel });
        this.permissionDialogRef = undefined;
    }
    /**
     * Closes the privacy settings popup, if it is open.
     */
    closePrivacySettingsPopup() {
        this.settingsDialogRef?.close({ result: DialogResult.Cancel });
        this.settingsDialogRef = undefined;
    }
    // TODO: make private and update tests!
    /** @ignore This method is only used for internal purposes and should be considered private. */
    setPermission(value, getLifespan = threeMonthsLifespan) {
        const { created, expires } = getLifespan();
        const privacyCookie = createPrivacyCookie(value, created, expires);
        this.cookies.set(XGUIDE_PRIVACY_COOKIE_NAME, privacyCookie, { expires: new Date(expires) });
    }
    getCookieLifespan(dialogResult) {
        return dialogResult.payload === 'accepted' ? twelveMonthsLifespan : threeMonthsLifespan;
    }
    getPrivacySetting(name) {
        const cookie = this.cookies.get(name, true);
        return cookie?.optionalCookies?.cookies;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingPermissionService, deps: [{ token: CookieService }, { token: DialogService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingPermissionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingPermissionService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CookieService }, { type: DialogService }] });

function removeUndefined(providers) {
    return providers.filter((provider) => provider != null);
}
function whenDefined(provider) {
    return provider.useClass != null ? provider : undefined;
}
function hasType(type) {
    return (value) => typeOf(value, type);
}
function typeOf(val, type) {
    const fn = checks[type];
    return fn(val);
}
function isNullOrUndefined(arg) {
    return arg === null || arg === undefined;
}
// ---------------------------
// Module Internals
// ---------------------------
const checks = {
    array: (val) => Array.isArray(val),
    object: (val) => typeof val === 'object',
    function: (val) => typeof val === 'function',
    string: (val) => typeof val === 'string',
    number: (val) => typeof val === 'number',
    boolean: (val) => typeof val === 'boolean',
};

const LocalStorageToken = new InjectionToken('local-storage');

class LocalStorageService {
    constructor(localStorageInstance) {
        this.localStorageInstance = localStorageInstance;
    }
    /**
     * Gets the information for the given local storage key. If nothing was stored
     * for the specified key, this method returns `undefined`.
     * @param key The local storage key to retrieve the information from.
     */
    get(key, convert) {
        const data = this.localStorageInstance.getItem(key) ?? undefined;
        try {
            return typeOf(convert, 'function') && typeOf(data, 'string') ? convert(data) : data;
        }
        catch {
            return undefined;
        }
    }
    /**
     * Sets the given data for the specified key. The data can be either a string or an object / array.
     * In the latter case the data will be stringified, as the local storage only supports storing string values.
     * @param key The key to set the information for.
     * @param data The data to store for the specified key.
     */
    set(key, data) {
        const parsedData = typeof data !== 'string' ? JSON.stringify(data) : data;
        this.localStorageInstance.setItem(key, parsedData);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: LocalStorageService, deps: [{ token: LocalStorageToken }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: LocalStorageService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LocalStorageToken]
                }] }] });
new LocalStorageService(localStorage).get('', JSON.parse);

const BASE_ROUTE = 'usage-tracking';
const ANONYMOUS_GUID_KEY = 'ANONYMOUS_GUID_KEY';
/** Service responsible for tracking the user's usage behavior. */
class UsageTrackingService {
    get isConfigured() {
        return this.sendImplementation != null;
    }
    constructor(trackingPermission, localStorage, sendImplementation) {
        this.trackingPermission = trackingPermission;
        this.localStorage = localStorage;
        this.sendImplementation = sendImplementation;
    }
    send(messageType, usageTracking) {
        if (!this.trackingPermission.trackingAllowed || !this.isConfigured) {
            return;
        }
        let anonymousGuid = this.localStorage.get(ANONYMOUS_GUID_KEY);
        if (anonymousGuid === undefined || anonymousGuid === null || anonymousGuid === '') {
            anonymousGuid = v4();
            this.localStorage.set(ANONYMOUS_GUID_KEY, anonymousGuid);
        }
        if (this.sendImplementation != null) {
            this.sendImplementation.send(messageType, usageTracking, anonymousGuid);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingService, deps: [{ token: UsageTrackingPermissionService }, { token: LocalStorageService }, { token: UsageTrackingSendImplementationService, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: UsageTrackingPermissionService }, { type: LocalStorageService }, { type: UsageTrackingSendImplementationService, decorators: [{
                    type: Optional
                }] }] });

class ClickUsageTrackingDirective {
    elementClicked() {
        let elementIdentifier = this.xuiClickUsageTracking;
        if (this.specificElementIdentifier) {
            elementIdentifier += `-${this.specificElementIdentifier}`;
        }
        const usageTracking = {
            currentUrl: this.router.url,
            clickedElement: elementIdentifier ?? UNKNOWN,
        };
        const messageType = this.clickElementMessageTypeId;
        this.usageTrackingService.send(messageType, usageTracking);
    }
    constructor(usageTrackingService, router, clickElementMessageTypeId) {
        this.usageTrackingService = usageTrackingService;
        this.router = router;
        this.clickElementMessageTypeId = clickElementMessageTypeId;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ClickUsageTrackingDirective, deps: [{ token: UsageTrackingService }, { token: i2.Router }, { token: ClickElementMessageTypeId }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.5", type: ClickUsageTrackingDirective, selector: "[xuiClickUsageTracking]", inputs: { xuiClickUsageTracking: "xuiClickUsageTracking", specificElementIdentifier: "specificElementIdentifier" }, host: { listeners: { "click": "elementClicked()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ClickUsageTrackingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[xuiClickUsageTracking]',
                }]
        }], ctorParameters: () => [{ type: UsageTrackingService }, { type: i2.Router }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ClickElementMessageTypeId]
                }] }], propDecorators: { xuiClickUsageTracking: [{
                type: Input
            }], specificElementIdentifier: [{
                type: Input
            }], elementClicked: [{
                type: HostListener,
                args: ['click']
            }] } });

/**
 * For XGuide internal use only. Please don't use it in any guide frontend.
 * @internal
 */
class ImageUrlBridgePipe {
    constructor(translationImpl) {
        this.translationImpl = translationImpl;
    }
    transform(value, ...args) {
        return this.translationImpl?.transform(value, ...args) ?? value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ImageUrlBridgePipe, deps: [{ token: ImageUrlPipeImplementation, optional: true }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: ImageUrlBridgePipe, name: "imageUrl" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ImageUrlBridgePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'imageUrl' }]
        }], ctorParameters: () => [{ type: ImageUrlPipeImplementation, decorators: [{
                    type: Optional
                }] }] });

class ArticleCarouselComponent {
    /** @ignore */
    constructor(translations) {
        this.translations = translations;
        /**
         * The set of articles to be shown in the carousel. If the array contains no articles, a "no news" hint text will be shown.
         */
        this.articles = [];
        /** @ignore */
        this.ElementIds = XGUIDE_ELEMENT_IDS;
        /** @ignore */
        this.orderedArticles = [];
        /** @ignore */
        this.isMouseOverArticle = false;
    }
    /** @ignore */
    ngOnInit() {
        // initialize carousel timer
        const carouselRotationTimer = timer(10000, 10000);
        this.timerSubscription = carouselRotationTimer.subscribe(() => {
            this.showNextArticle();
        });
    }
    /** @ignore */
    ngOnDestroy() {
        this.timerSubscription?.unsubscribe();
    }
    /** @ignore */
    ngOnChanges(changes) {
        const articles = 'articles';
        if (changes[articles]) {
            this.orderedArticles = this.orderByDateString(this.articles);
            this.selectedArticle = this.orderedArticles[0];
        }
    }
    /** @ignore */
    select(article) {
        this.selectedArticle = article;
    }
    /** @ignore */
    onArticleMouseEnter() {
        this.isMouseOverArticle = true;
    }
    /** @ignore */
    onArticleMouseLeave() {
        this.isMouseOverArticle = false;
    }
    /** @ignore */
    showNextArticle() {
        // The next article will not be shown automatically if the mouse is over the title or description
        if (this.selectedArticle === undefined || this.isMouseOverArticle)
            return;
        const selectedIndex = this.orderedArticles.indexOf(this.selectedArticle, 0);
        const nextIndex = selectedIndex < this.orderedArticles.length - 1 ? selectedIndex + 1 : 0;
        this.selectedArticle = this.orderedArticles[nextIndex];
    }
    /** @ignore */
    orderByDateString(articles) {
        if (articles == null || articles.length === 0) {
            return articles ?? [];
        }
        const firstArticle = articles[0];
        const firstDate = firstArticle.date;
        const isGermanFormat = firstDate.includes('.');
        return isGermanFormat ? articles.sort(this.sortGermanDate) : articles.sort(this.sortEnglishDate);
    }
    /** @ignore */
    sortEnglishDate(a, b) {
        const sortDescending = a.date.localeCompare(b.date) * -1;
        return sortDescending;
    }
    /** @ignore */
    sortGermanDate(a, b) {
        // 31.12.2020 --> 2020.12.31
        const reversedDateA = reverseString(a.date);
        const reversedDateB = reverseString(b.date);
        const sortDescending = reversedDateA.localeCompare(reversedDateB) * -1;
        return sortDescending;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ArticleCarouselComponent, deps: [{ token: Translations }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: ArticleCarouselComponent, selector: "xui-article-carousel", inputs: { articles: "articles" }, usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"selectedArticle; else noNews\">\n  <div class=\"half img\">\n    <a\n      target=\"_blank\"\n      data-ngtx=\"article-carousel:image-link\"\n      [href]=\"selectedArticle.link\"\n      [xuiClickUsageTracking]=\"ElementIds.DASHBOARD_NEWS_IMAGE\"\n    >\n      <img\n        class=\"article-img\"\n        data-ngtx=\"article-carousel:image\"\n        [alt]=\"selectedArticle.image.description\"\n        [src]=\"selectedArticle.image.src | imageUrl\"\n      />\n    </a>\n  </div>\n  <div class=\"half content\" (mouseleave)=\"onArticleMouseLeave()\" (mouseenter)=\"onArticleMouseEnter()\">\n    <div class=\"article\" data-ngtx=\"article-carousel:article\">\n      <a\n        target=\"_blank\"\n        data-ngtx=\"article-carousel:title\"\n        class=\"title\"\n        [href]=\"selectedArticle.link\"\n        [xuiClickUsageTracking]=\"ElementIds.DASHBOARD_NEWS_TITLE\"\n      >\n        <h3>{{ selectedArticle.title }}</h3>\n      </a>\n\n      <p class=\"date\" data-ngtx=\"article-carousel:date\">{{ selectedArticle.date }}</p>\n      <a\n        target=\"_blank\"\n        data-ngtx=\"article-carousel:description\"\n        class=\"description\"\n        [href]=\"selectedArticle.link\"\n        [xuiClickUsageTracking]=\"ElementIds.DASHBOARD_NEWS_DESCRIPTION\"\n      >\n        <p>{{ selectedArticle.description }}</p>\n      </a>\n    </div>\n\n    <div class=\"pages\">\n      <div\n        *ngFor=\"let article of orderedArticles\"\n        class=\"page\"\n        data-ngtx=\"article-carousel:pagination\"\n        [class.selected]=\"article === selectedArticle\"\n        [xuiClickUsageTracking]=\"ElementIds.DASHBOARD_NEWS_PAGINATION\"\n        (click)=\"select(article)\"\n      ></div>\n    </div>\n  </div>\n</ng-container>\n\n<ng-template #noNews>\n  <div class=\"no-news\" data-ngtx=\"article-carousel:no-news\">\n    {{ translations.XGUIDE.DASHBOARD.NEWS.NO_NEWS | translate }}\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:flex;align-items:stretch}.half{width:50%;box-sizing:border-box}.half:last-child{padding:0 2em 1.5em}.no-news{display:flex;height:100%;width:100%;align-items:center;justify-content:center;font-size:1.5em;color:#707070;padding:16px}.content{display:flex;flex-direction:column;justify-content:space-between}.article a{text-decoration:none;color:#000}.article:hover .title{text-decoration:underline}.title{margin:1em 0 .5em;cursor:pointer}.date{margin:.2em 0;color:#a1a1a1}.description{margin-top:1em 0}.article-img{width:100%;height:100%;object-fit:cover}.pages{display:flex;align-items:flex-end;justify-content:center;margin-top:1em}.page{position:relative;cursor:pointer;margin:0 .2em}.page:after{display:block;content:\"\";height:8px;width:12px;background:#899fb2}.page.selected{cursor:default}.page.selected:after{width:16px;height:12px;background:#285172}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ClickUsageTrackingDirective, selector: "[xuiClickUsageTracking]", inputs: ["xuiClickUsageTracking", "specificElementIdentifier"] }, { kind: "pipe", type: TranslationBridgePipe, name: "translate" }, { kind: "pipe", type: ImageUrlBridgePipe, name: "imageUrl" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ArticleCarouselComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-article-carousel', template: "<ng-container *ngIf=\"selectedArticle; else noNews\">\n  <div class=\"half img\">\n    <a\n      target=\"_blank\"\n      data-ngtx=\"article-carousel:image-link\"\n      [href]=\"selectedArticle.link\"\n      [xuiClickUsageTracking]=\"ElementIds.DASHBOARD_NEWS_IMAGE\"\n    >\n      <img\n        class=\"article-img\"\n        data-ngtx=\"article-carousel:image\"\n        [alt]=\"selectedArticle.image.description\"\n        [src]=\"selectedArticle.image.src | imageUrl\"\n      />\n    </a>\n  </div>\n  <div class=\"half content\" (mouseleave)=\"onArticleMouseLeave()\" (mouseenter)=\"onArticleMouseEnter()\">\n    <div class=\"article\" data-ngtx=\"article-carousel:article\">\n      <a\n        target=\"_blank\"\n        data-ngtx=\"article-carousel:title\"\n        class=\"title\"\n        [href]=\"selectedArticle.link\"\n        [xuiClickUsageTracking]=\"ElementIds.DASHBOARD_NEWS_TITLE\"\n      >\n        <h3>{{ selectedArticle.title }}</h3>\n      </a>\n\n      <p class=\"date\" data-ngtx=\"article-carousel:date\">{{ selectedArticle.date }}</p>\n      <a\n        target=\"_blank\"\n        data-ngtx=\"article-carousel:description\"\n        class=\"description\"\n        [href]=\"selectedArticle.link\"\n        [xuiClickUsageTracking]=\"ElementIds.DASHBOARD_NEWS_DESCRIPTION\"\n      >\n        <p>{{ selectedArticle.description }}</p>\n      </a>\n    </div>\n\n    <div class=\"pages\">\n      <div\n        *ngFor=\"let article of orderedArticles\"\n        class=\"page\"\n        data-ngtx=\"article-carousel:pagination\"\n        [class.selected]=\"article === selectedArticle\"\n        [xuiClickUsageTracking]=\"ElementIds.DASHBOARD_NEWS_PAGINATION\"\n        (click)=\"select(article)\"\n      ></div>\n    </div>\n  </div>\n</ng-container>\n\n<ng-template #noNews>\n  <div class=\"no-news\" data-ngtx=\"article-carousel:no-news\">\n    {{ translations.XGUIDE.DASHBOARD.NEWS.NO_NEWS | translate }}\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:flex;align-items:stretch}.half{width:50%;box-sizing:border-box}.half:last-child{padding:0 2em 1.5em}.no-news{display:flex;height:100%;width:100%;align-items:center;justify-content:center;font-size:1.5em;color:#707070;padding:16px}.content{display:flex;flex-direction:column;justify-content:space-between}.article a{text-decoration:none;color:#000}.article:hover .title{text-decoration:underline}.title{margin:1em 0 .5em;cursor:pointer}.date{margin:.2em 0;color:#a1a1a1}.description{margin-top:1em 0}.article-img{width:100%;height:100%;object-fit:cover}.pages{display:flex;align-items:flex-end;justify-content:center;margin-top:1em}.page{position:relative;cursor:pointer;margin:0 .2em}.page:after{display:block;content:\"\";height:8px;width:12px;background:#899fb2}.page.selected{cursor:default}.page.selected:after{width:16px;height:12px;background:#285172}\n"] }]
        }], ctorParameters: () => [{ type: Translations }], propDecorators: { articles: [{
                type: Input
            }] } });

class DashboardTileComponent {
    constructor() {
        /**
         * Whether the tile is rendered in dark colors.
         */
        this.dark = false;
        /**
         * Whether the tile is an action tile, allowing the user to trigger an action. Defaults to `false`.
         */
        this.isAction = false;
        /**
         * (Optional) The icon for the action. Only takes effect if `isAction` is set to `true.`
         */
        this.actionIcon = 'icon-plus';
        /**
         * Whether the title section should render a shadow. Defaults to `false`.
         */
        this.titleShadow = false;
        /**
         * Emitted whenever the user clicks the action-button. Only applies if `isAction` is set to `true.`
         */
        this.actionClick = new EventEmitter();
    }
    get tileCss() {
        return this.dark ? 'tile blue' : 'tile white';
    }
    get colorCss() {
        return this.dark ? 'blue' : 'white';
    }
    /** @ignore */
    getIconCssClasses(type) {
        if (type === 'action' && this.actionIcon) {
            return { icon: true, [this.actionIcon]: true };
        }
        if (type === 'title' && this.icon) {
            return { icon: true, [this.icon]: true };
        }
        return {};
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DashboardTileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: DashboardTileComponent, selector: "xui-dashboard-tile", inputs: { dark: "dark", isAction: "isAction", actionLabel: "actionLabel", actionIcon: "actionIcon", title: "title", titleShadow: "titleShadow", icon: "icon" }, outputs: { actionClick: "actionClick" }, ngImport: i0, template: "<!-- Normal Tile -->\n<div *ngIf=\"!isAction; else action\" [class]=\"tileCss\" data-ngtx=\"tile:tile\">\n  <div *ngIf=\"title || icon\" class=\"header\" data-ngtx=\"tile:header\" [class.shadow]=\"titleShadow\">\n    <div *ngIf=\"icon\" [ngClass]=\"getIconCssClasses('title')\" data-ngtx=\"tile:icon\"></div>\n    <div class=\"headline\" data-ngtx=\"tile:headline\">{{ title }}</div>\n  </div>\n  <div class=\"content\" data-ngtx=\"tile:content\">\n    <ng-content></ng-content>\n  </div>\n</div>\n\n<ng-template #action>\n  <div class=\"action\" data-ngtx=\"tile:action-tile\">\n    <div [class]=\"colorCss\" data-ngtx=\"tile:action-content\">\n      <!-- Action Button Tile -->\n      <div *ngIf=\"title || icon\" class=\"header\" data-ngtx=\"tile:header\" [class.shadow]=\"titleShadow\">\n        <div *ngIf=\"icon\" [ngClass]=\"getIconCssClasses('title')\" data-ngtx=\"tile:icon\"></div>\n        <div class=\"headline\" data-ngtx=\"tile:headline\">{{ title }}</div>\n      </div>\n    </div>\n\n    <div class=\"outline-tile\" (click)=\"actionClick.emit()\">\n      <div [ngClass]=\"getIconCssClasses('action')\" data-ngtx=\"tile:action-icon\"></div>\n      <div class=\"label\">{{ actionLabel }}</div>\n    </div>\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:block}.tile{display:flex;width:100%;height:100%;flex-direction:column}.content{height:100%;overflow:hidden;box-sizing:border-box;flex:1}.content .loader{margin-top:74px;margin-left:56px;width:128px}.header{display:flex;align-items:center;justify-content:left;height:48px;padding:0 8px 0 6px;width:100%;box-sizing:border-box}.header.shadow{box-shadow:inset 0 -3px 6px -3px #899fb266}.header .headline{margin:0;padding-left:8px;font-size:1.14rem;font-weight:600;line-height:24px}.header .icon{font-size:36px}.blue{display:flex;flex-direction:column;color:#fff;background:#142939;box-shadow:0 2px 4px #0000000d,0 2px 8px #a1a1a166}.blue:focus{border:2px solid #74b9ef}.blue:hover{color:#74b9ef}.white{background:#fff;color:#000;box-shadow:0 2px 4px #0000000d,0 2px 8px #a1a1a166}.white:focus{border:2px solid #363636}.white:hover{color:#363636}.action{display:flex;flex-direction:column;height:100%}.action .header{flex:0 0 auto}.action .outline-tile{flex:1 1 100%!important;min-height:0;cursor:pointer}.outline-tile{padding:0;margin-top:.5em;min-width:240px;width:100%;height:100%;box-sizing:border-box;border:1px solid #a1a1a1;color:#363636;vertical-align:middle;display:flex;flex-direction:column;align-items:center;justify-content:center;-webkit-user-select:none;user-select:none}.outline-tile .icon{display:block;margin:0;font-size:36px}.outline-tile .label{padding-top:5px;font-size:1.14rem;font-weight:700}.outline-tile:focus{border:2px solid #51a7eb}.outline-tile:hover{border-color:#51a7eb;color:#177ac9}.outline-tile:hover .icon{color:#177ac9}.outline-tile:active{border:1px solid #51a7eb;background:#51a7eb1a;color:#177ac9}.outline-tile:active .icon{color:#177ac9}.white-tile-content{background:#fff;box-shadow:0 2px 4px #0000000d,0 2px 8px #a1a1a166;color:#363636}.white-tile-content:focus{border:2px solid #74b9ef}.white-tile-content:hover{color:#177ac9}.white-tile-content.view-only{background:#ffffffb3;pointer-events:none}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DashboardTileComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-dashboard-tile', template: "<!-- Normal Tile -->\n<div *ngIf=\"!isAction; else action\" [class]=\"tileCss\" data-ngtx=\"tile:tile\">\n  <div *ngIf=\"title || icon\" class=\"header\" data-ngtx=\"tile:header\" [class.shadow]=\"titleShadow\">\n    <div *ngIf=\"icon\" [ngClass]=\"getIconCssClasses('title')\" data-ngtx=\"tile:icon\"></div>\n    <div class=\"headline\" data-ngtx=\"tile:headline\">{{ title }}</div>\n  </div>\n  <div class=\"content\" data-ngtx=\"tile:content\">\n    <ng-content></ng-content>\n  </div>\n</div>\n\n<ng-template #action>\n  <div class=\"action\" data-ngtx=\"tile:action-tile\">\n    <div [class]=\"colorCss\" data-ngtx=\"tile:action-content\">\n      <!-- Action Button Tile -->\n      <div *ngIf=\"title || icon\" class=\"header\" data-ngtx=\"tile:header\" [class.shadow]=\"titleShadow\">\n        <div *ngIf=\"icon\" [ngClass]=\"getIconCssClasses('title')\" data-ngtx=\"tile:icon\"></div>\n        <div class=\"headline\" data-ngtx=\"tile:headline\">{{ title }}</div>\n      </div>\n    </div>\n\n    <div class=\"outline-tile\" (click)=\"actionClick.emit()\">\n      <div [ngClass]=\"getIconCssClasses('action')\" data-ngtx=\"tile:action-icon\"></div>\n      <div class=\"label\">{{ actionLabel }}</div>\n    </div>\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:block}.tile{display:flex;width:100%;height:100%;flex-direction:column}.content{height:100%;overflow:hidden;box-sizing:border-box;flex:1}.content .loader{margin-top:74px;margin-left:56px;width:128px}.header{display:flex;align-items:center;justify-content:left;height:48px;padding:0 8px 0 6px;width:100%;box-sizing:border-box}.header.shadow{box-shadow:inset 0 -3px 6px -3px #899fb266}.header .headline{margin:0;padding-left:8px;font-size:1.14rem;font-weight:600;line-height:24px}.header .icon{font-size:36px}.blue{display:flex;flex-direction:column;color:#fff;background:#142939;box-shadow:0 2px 4px #0000000d,0 2px 8px #a1a1a166}.blue:focus{border:2px solid #74b9ef}.blue:hover{color:#74b9ef}.white{background:#fff;color:#000;box-shadow:0 2px 4px #0000000d,0 2px 8px #a1a1a166}.white:focus{border:2px solid #363636}.white:hover{color:#363636}.action{display:flex;flex-direction:column;height:100%}.action .header{flex:0 0 auto}.action .outline-tile{flex:1 1 100%!important;min-height:0;cursor:pointer}.outline-tile{padding:0;margin-top:.5em;min-width:240px;width:100%;height:100%;box-sizing:border-box;border:1px solid #a1a1a1;color:#363636;vertical-align:middle;display:flex;flex-direction:column;align-items:center;justify-content:center;-webkit-user-select:none;user-select:none}.outline-tile .icon{display:block;margin:0;font-size:36px}.outline-tile .label{padding-top:5px;font-size:1.14rem;font-weight:700}.outline-tile:focus{border:2px solid #51a7eb}.outline-tile:hover{border-color:#51a7eb;color:#177ac9}.outline-tile:hover .icon{color:#177ac9}.outline-tile:active{border:1px solid #51a7eb;background:#51a7eb1a;color:#177ac9}.outline-tile:active .icon{color:#177ac9}.white-tile-content{background:#fff;box-shadow:0 2px 4px #0000000d,0 2px 8px #a1a1a166;color:#363636}.white-tile-content:focus{border:2px solid #74b9ef}.white-tile-content:hover{color:#177ac9}.white-tile-content.view-only{background:#ffffffb3;pointer-events:none}\n"] }]
        }], propDecorators: { dark: [{
                type: Input
            }], isAction: [{
                type: Input
            }], actionLabel: [{
                type: Input
            }], actionIcon: [{
                type: Input
            }], title: [{
                type: Input
            }], titleShadow: [{
                type: Input
            }], icon: [{
                type: Input
            }], actionClick: [{
                type: Output
            }] } });

var MediaType;
(function (MediaType) {
    MediaType["IMAGE"] = "image";
    MediaType["VIDEO"] = "video";
})(MediaType || (MediaType = {}));

class MediaBaseComponent {
    /**
     * The media object to show.
     */
    get media() {
        return this._media;
    }
    set media(value) {
        this.hasError = false;
        this._media = value;
    }
    /** @ignore */
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        /** @ignore */
        this.hasError = false;
    }
    /** @ignore */
    handleImageError() {
        this.hasError = true;
        // We need to trigger change detection manually to prevent
        // race conditions from the src setter and the error event.
        // The error event has always higher precedence.
        this.changeDetector.detectChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MediaBaseComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: MediaBaseComponent, selector: "ng-component", inputs: { media: "media" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MediaBaseComponent, decorators: [{
            type: Component,
            args: [{ template: '' }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { media: [{
                type: Input
            }] } });

class PlaceholderComponent {
    constructor() {
        /**
         * The type of the media that should be visualized as a placeholder.
         */
        this.type = MediaType.IMAGE;
    }
    get isVideoPlaceholder() {
        return this.type === MediaType.VIDEO;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: PlaceholderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: PlaceholderComponent, selector: "xui-placeholder", inputs: { type: "type" }, host: { properties: { "class.video": "this.isVideoPlaceholder" } }, ngImport: i0, template: "<!-- Rendered directly into host element via CSS. -->\n", styles: ["@charset \"UTF-8\";:host{display:inline-block;width:32px;height:32px;background:#707070;mask:url(/assets/xguide/private/file_image.svg);-webkit-mask-size:contain;mask-size:contain}:host.video{-webkit-mask-image:url(/assets/xguide/private/file_video.svg);mask-image:url(/assets/xguide/private/file_video.svg)}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: PlaceholderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-placeholder', template: "<!-- Rendered directly into host element via CSS. -->\n", styles: ["@charset \"UTF-8\";:host{display:inline-block;width:32px;height:32px;background:#707070;mask:url(/assets/xguide/private/file_image.svg);-webkit-mask-size:contain;mask-size:contain}:host.video{-webkit-mask-image:url(/assets/xguide/private/file_video.svg);mask-image:url(/assets/xguide/private/file_video.svg)}\n"] }]
        }], propDecorators: { type: [{
                type: Input
            }], isVideoPlaceholder: [{
                type: HostBinding,
                args: ['class.video']
            }] } });

class MediaComponent extends MediaBaseComponent {
    get video() {
        return this.videoPlayerRef?.nativeElement;
    }
    /** @ignore */
    constructor(translations, changeDetectorRef) {
        super(changeDetectorRef);
        this.translations = translations;
        /** @ignore */
        this.MediaType = MediaType;
        /**
         * Determines how the media will be fitted into its container. Possible values are describe by the `MediaViewMode` type.
         * Defaults to "contain".
         */
        this.viewMode = 'contain';
        /**
         * Whether to hide the media-player controls for videos.
         */
        this.hideControls = false;
    }
    /** @ignore */
    ngOnChanges(changes) {
        if (changes.media && this.media?.type === MediaType.VIDEO) {
            this.manuallyUpdateVideoSrc();
        }
    }
    /**
     * Plays the current video-media in fullscreen mode. This only takes effect, if the current media is a video.
     * @returns Whether this action could be executed successfully.
     */
    playFullscreen() {
        const player = this.videoPlayerRef.nativeElement;
        const element = player;
        if (player == null) {
            return false;
        }
        if (element.requestFullscreen) {
            element.requestFullscreen();
        }
        else if (element.mozRequestFullScreen) {
            /* Firefox */
            element.mozRequestFullScreen();
        }
        else if (element.webkitRequestFullscreen) {
            /* Chrome, Safari & Opera */
            element.webkitRequestFullscreen();
        }
        else if (element.msRequestFullscreen) {
            /* IE/Edge */
            element.msRequestFullscreen();
        }
        player.play();
        return true;
    }
    /**
     * Plays the current video media. Only takes effect if the current media is a video.
     * @returns Whether this action could be executed successfully.
     */
    play() {
        const player = this.videoPlayerRef.nativeElement;
        if (player == null) {
            return false;
        }
        player.play();
        return true;
    }
    /**
     * Pauses the current video media. Only takes effect if the current media is a video.
     * @returns Whether this action could be executed successfully.
     */
    pause() {
        const player = this.videoPlayerRef.nativeElement;
        if (player == null) {
            return false;
        }
        player.pause();
        return true;
    }
    /** @ignore */
    manuallyUpdateVideoSrc() {
        if (this.video && this.media) {
            this.video.src = this.media.src;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MediaComponent, deps: [{ token: Translations }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: MediaComponent, selector: "xui-media", inputs: { viewMode: "viewMode", hideControls: "hideControls" }, viewQueries: [{ propertyName: "videoPlayerRef", first: true, predicate: ["player"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"media?.type === MediaType.IMAGE; else video\">\n  <img\n    *ngIf=\"!hasError; else placeholder\"\n    class=\"image non-draggable\"\n    [src]=\"media?.src | imageUrl\"\n    [class.stretch]=\"viewMode === 'cover'\"\n    (error)=\"handleImageError()\"\n  />\n</ng-container>\n\n<ng-template #video>\n  <!-- videos are at same location as images, so imageUrl pipe works as expected: -->\n  <video\n    #player\n    *ngIf=\"!hasError; else placeholder\"\n    class=\"video\"\n    [attr.controls]=\"hideControls ? null : true\"\n    [src]=\"media?.src | imageUrl\"\n    [class.stretch]=\"viewMode === 'cover'\"\n    (error)=\"handleImageError()\"\n  >\n    {{ translations.XGUIDE.MEDIA.VIDEO_NOT_SUPPORTED | translate }}\n  </video>\n</ng-template>\n\n<ng-template #placeholder>\n  <xui-placeholder class=\"placeholder\" [type]=\"media?.type\"></xui-placeholder>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:flex;height:100%;align-items:center;justify-content:center;padding:0;margin:0;border:none;background:#f7f7f7}.image{object-fit:scale-down;max-width:100%;max-height:100%}.video{width:100%;max-height:100%}.stretch{height:100%;object-fit:cover}.placeholder{width:48px;height:48px}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: PlaceholderComponent, selector: "xui-placeholder", inputs: ["type"] }, { kind: "pipe", type: TranslationBridgePipe, name: "translate" }, { kind: "pipe", type: ImageUrlBridgePipe, name: "imageUrl" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MediaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-media', template: "<ng-container *ngIf=\"media?.type === MediaType.IMAGE; else video\">\n  <img\n    *ngIf=\"!hasError; else placeholder\"\n    class=\"image non-draggable\"\n    [src]=\"media?.src | imageUrl\"\n    [class.stretch]=\"viewMode === 'cover'\"\n    (error)=\"handleImageError()\"\n  />\n</ng-container>\n\n<ng-template #video>\n  <!-- videos are at same location as images, so imageUrl pipe works as expected: -->\n  <video\n    #player\n    *ngIf=\"!hasError; else placeholder\"\n    class=\"video\"\n    [attr.controls]=\"hideControls ? null : true\"\n    [src]=\"media?.src | imageUrl\"\n    [class.stretch]=\"viewMode === 'cover'\"\n    (error)=\"handleImageError()\"\n  >\n    {{ translations.XGUIDE.MEDIA.VIDEO_NOT_SUPPORTED | translate }}\n  </video>\n</ng-template>\n\n<ng-template #placeholder>\n  <xui-placeholder class=\"placeholder\" [type]=\"media?.type\"></xui-placeholder>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:flex;height:100%;align-items:center;justify-content:center;padding:0;margin:0;border:none;background:#f7f7f7}.image{object-fit:scale-down;max-width:100%;max-height:100%}.video{width:100%;max-height:100%}.stretch{height:100%;object-fit:cover}.placeholder{width:48px;height:48px}\n"] }]
        }], ctorParameters: () => [{ type: Translations }, { type: i0.ChangeDetectorRef }], propDecorators: { viewMode: [{
                type: Input
            }], hideControls: [{
                type: Input
            }], videoPlayerRef: [{
                type: ViewChild,
                args: ['player']
            }] } });

class GettingStartedTileComponent {
    /** @ignore */
    onFullScreenChange() {
        if (this.document.fullscreenElement) {
            this.player.play();
        }
        else {
            this.player.pause();
        }
    }
    /** @ignore */
    constructor(document, translations) {
        this.document = document;
        this.translations = translations;
        /** @ignore */
        this.tutorialVideo$ = of(undefined);
    }
    /** @ignore */
    playTutorial() {
        if (this.player == null) {
            return;
        }
        this.player.hideControls = false;
        this.player.playFullscreen();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: GettingStartedTileComponent, deps: [{ token: DOCUMENT }, { token: Translations }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: GettingStartedTileComponent, selector: "xui-getting-started-tile", inputs: { videoSrc: "videoSrc" }, host: { listeners: { "mozfullscreenchange": "onFullScreenChange()", "webkitfullscreenchange": "onFullScreenChange()", "fullscreenchange": "onFullScreenChange()" } }, viewQueries: [{ propertyName: "player", first: true, predicate: MediaComponent, descendants: true }], ngImport: i0, template: "<xui-dashboard-tile\n  class=\"tile\"\n  [titleShadow]=\"true\"\n  [title]=\"translations.XGUIDE.DASHBOARD.GETTING_STARTED.TITLE | translate\"\n>\n  <div class=\"stacked\">\n    <xui-media class=\"video\" [media]=\"videoSrc\" [hideControls]=\"true\"></xui-media>\n\n    <xui-button class=\"button\" iconKey=\"Arrow.Dashed.Left\" (click)=\"playTutorial()\">\n      {{ translations.XGUIDE.DASHBOARD.GETTING_STARTED.TAKE_THE_TOUR | translate }}\n    </xui-button>\n  </div>\n</xui-dashboard-tile>\n", styles: [":host{display:block;height:100%}.tile{height:100%}.stacked{display:flex;flex-direction:column;height:100%}.video{flex:1 1 100%;min-height:0}.button{flex:0 0 auto;margin:1em auto 1.2em}\n"], dependencies: [{ kind: "component", type: ButtonComponent, selector: "xui-button", inputs: ["type", "iconKey", "disabled"] }, { kind: "component", type: MediaComponent, selector: "xui-media", inputs: ["viewMode", "hideControls"] }, { kind: "component", type: DashboardTileComponent, selector: "xui-dashboard-tile", inputs: ["dark", "isAction", "actionLabel", "actionIcon", "title", "titleShadow", "icon"], outputs: ["actionClick"] }, { kind: "pipe", type: TranslationBridgePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: GettingStartedTileComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-getting-started-tile', template: "<xui-dashboard-tile\n  class=\"tile\"\n  [titleShadow]=\"true\"\n  [title]=\"translations.XGUIDE.DASHBOARD.GETTING_STARTED.TITLE | translate\"\n>\n  <div class=\"stacked\">\n    <xui-media class=\"video\" [media]=\"videoSrc\" [hideControls]=\"true\"></xui-media>\n\n    <xui-button class=\"button\" iconKey=\"Arrow.Dashed.Left\" (click)=\"playTutorial()\">\n      {{ translations.XGUIDE.DASHBOARD.GETTING_STARTED.TAKE_THE_TOUR | translate }}\n    </xui-button>\n  </div>\n</xui-dashboard-tile>\n", styles: [":host{display:block;height:100%}.tile{height:100%}.stacked{display:flex;flex-direction:column;height:100%}.video{flex:1 1 100%;min-height:0}.button{flex:0 0 auto;margin:1em auto 1.2em}\n"] }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: Translations }], propDecorators: { videoSrc: [{
                type: Input
            }], player: [{
                type: ViewChild,
                args: [MediaComponent]
            }], onFullScreenChange: [{
                type: HostListener,
                args: ['mozfullscreenchange']
            }, {
                type: HostListener,
                args: ['webkitfullscreenchange']
            }, {
                type: HostListener,
                args: ['fullscreenchange']
            }] } });

class WelcomeTileComponent {
    constructor() {
        /**
         * The text to be shown as headline.
         */
        this.headline = '';
        /**
         * The text to be shown as description.
         */
        this.description = '';
        /**
         * The text to be shown in the banner flag.
         */
        this.flagText = '.FOR FREE';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: WelcomeTileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: WelcomeTileComponent, selector: "xui-welcome-tile", inputs: { headline: "headline", description: "description", flagText: "flagText" }, ngImport: i0, template: "<xui-dashboard-tile class=\"tile\" [dark]=\"true\">\n  <picture class=\"hero\">\n    <ng-content></ng-content>\n  </picture>\n\n  <div class=\"description\">\n    <div class=\"title\" data-ngtx=\"welcome-tile:headline\">{{ headline }}</div>\n    <div data-ngtx=\"welcome-tile:description\">{{ description }}</div>\n  </div>\n\n  <div class=\"flag\">\n    <div class=\"for-free\" data-ngtx=\"welcome-tile:flag\">{{ flagText }}</div>\n  </div>\n</xui-dashboard-tile>\n", styles: [":host{position:relative;display:block}:host,.tile,.hero,.hero img{width:100%;height:100%}.tile{overflow:hidden}.description{position:absolute;bottom:.5em;left:.5em;padding:1.5em;font-size:1.2em;max-width:300px;background:#ffffffd9;color:#000}.description .title{font-size:1.5em;font-weight:700;margin-bottom:.5em}.flag{position:absolute;top:1.5em;right:2.5em}.flag .for-free{background:#a4cd5a;color:#fff;font-size:2em;font-weight:700;padding:.8em .7em;-webkit-user-select:none;user-select:none}.flag .for-free:before{position:absolute;bottom:-30px;right:0;content:\"\";width:0;height:0;border-style:solid;border-width:0 30px 30px 0;border-color:transparent #a4cd5a transparent transparent}\n"], dependencies: [{ kind: "component", type: DashboardTileComponent, selector: "xui-dashboard-tile", inputs: ["dark", "isAction", "actionLabel", "actionIcon", "title", "titleShadow", "icon"], outputs: ["actionClick"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: WelcomeTileComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-welcome-tile', changeDetection: ChangeDetectionStrategy.OnPush, template: "<xui-dashboard-tile class=\"tile\" [dark]=\"true\">\n  <picture class=\"hero\">\n    <ng-content></ng-content>\n  </picture>\n\n  <div class=\"description\">\n    <div class=\"title\" data-ngtx=\"welcome-tile:headline\">{{ headline }}</div>\n    <div data-ngtx=\"welcome-tile:description\">{{ description }}</div>\n  </div>\n\n  <div class=\"flag\">\n    <div class=\"for-free\" data-ngtx=\"welcome-tile:flag\">{{ flagText }}</div>\n  </div>\n</xui-dashboard-tile>\n", styles: [":host{position:relative;display:block}:host,.tile,.hero,.hero img{width:100%;height:100%}.tile{overflow:hidden}.description{position:absolute;bottom:.5em;left:.5em;padding:1.5em;font-size:1.2em;max-width:300px;background:#ffffffd9;color:#000}.description .title{font-size:1.5em;font-weight:700;margin-bottom:.5em}.flag{position:absolute;top:1.5em;right:2.5em}.flag .for-free{background:#a4cd5a;color:#fff;font-size:2em;font-weight:700;padding:.8em .7em;-webkit-user-select:none;user-select:none}.flag .for-free:before{position:absolute;bottom:-30px;right:0;content:\"\";width:0;height:0;border-style:solid;border-width:0 30px 30px 0;border-color:transparent #a4cd5a transparent transparent}\n"] }]
        }], propDecorators: { headline: [{
                type: Input
            }], description: [{
                type: Input
            }], flagText: [{
                type: Input
            }] } });

const declarations$b = [TranslationBridgePipe, ImageUrlBridgePipe];
class XGuideConfigurationModule {
    static withConfig(config) {
        return {
            ngModule: XGuideConfigurationModule,
            providers: removeUndefined([
                whenDefined({ provide: TranslationPipeImplementation, useClass: config.translationPipeClass }),
                whenDefined({ provide: ImageUrlPipeImplementation, useClass: config.imageUrlPipeClass }),
                { provide: ApplicationInfo, useClass: createAppInfo(config) },
            ]),
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: XGuideConfigurationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: XGuideConfigurationModule, declarations: [TranslationBridgePipe, ImageUrlBridgePipe], imports: [CommonModule], exports: [TranslationBridgePipe, ImageUrlBridgePipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: XGuideConfigurationModule, providers: [Translations], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: XGuideConfigurationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [declarations$b],
                    providers: [Translations],
                    exports: [declarations$b],
                }]
        }] });
function createAppInfo(config) {
    return class {
        get name() {
            return config.appName;
        }
    };
}

const declarations$a = [MediaComponent, PlaceholderComponent];
class MediaModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MediaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: MediaModule, declarations: [MediaComponent, PlaceholderComponent], imports: [CommonModule, XGuideConfigurationModule], exports: [MediaComponent, PlaceholderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MediaModule, imports: [CommonModule, XGuideConfigurationModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MediaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, XGuideConfigurationModule],
                    declarations: [declarations$a],
                    exports: [declarations$a],
                }]
        }] });

class DialogContainerComponent {
    constructor() {
        /**
         * Whether the dialog frame should render a close button.
         */
        this.closable = false;
        /**
         * Emitted whenever the close button is clicked. Only applies if `closable` is set to `true`.
         */
        this.cancel = new EventEmitter();
    }
    get canBeClosedViaButton() {
        return this.closable != null && this.closable !== false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DialogContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: DialogContainerComponent, selector: "xui-dialog-container", inputs: { closable: "closable" }, outputs: { cancel: "cancel" }, ngImport: i0, template: "<div class=\"container popup__inner gr-10 push-1\" data-part=\"container\">\n  <div\n    *ngIf=\"canBeClosedViaButton\"\n    data-ngtx=\"dialog-container:close-icon\"\n    class=\"close popup__inner--close\"\n    (click)=\"cancel.emit()\"\n  >\n    <i class=\"icon-close\" data-popup-close=\"\" style=\"background-image: none\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 29.7 29.7\">\n        <path\n          d=\"M29.7 1.41L28.28 0 14.85 13.44 1.41 0 0 1.41l13.44 13.44L0 28.28l1.41 1.42 13.44-13.44L28.28 29.7l1.42-1.42-13.44-13.43L29.7 1.41z\"\n        ></path>\n      </svg>\n    </i>\n  </div>\n\n  <div class=\"popup__inner__content gr-10 push-1 no-gutter\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host{display:flex;align-items:center;justify-content:center;width:100%;height:100%}.container{position:relative;box-shadow:0 0 5px #0004}.close{position:absolute;right:15px;top:15px;width:15px;cursor:pointer}.popup__inner{display:flex;font-size:100%;line-height:1.6;min-height:1px;width:83.3333333333%;box-sizing:border-box;vertical-align:top;position:relative;margin:0 auto;background:#fff;transition:margin-top .5s ease,opacity .5s ease;overflow:auto;padding:45px 0 0;max-height:calc(100% - 30px)}.popup--privacy .popup__inner--close{position:absolute;top:15px;right:15px}.popup__inner__content{position:relative;display:inline-block;min-height:1px;box-sizing:border-box;vertical-align:top;left:8.3333333333%;width:83.3333333333%;padding:0}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner{direction:ltr}}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner--title{font-size:28px}}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DialogContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-dialog-container', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"container popup__inner gr-10 push-1\" data-part=\"container\">\n  <div\n    *ngIf=\"canBeClosedViaButton\"\n    data-ngtx=\"dialog-container:close-icon\"\n    class=\"close popup__inner--close\"\n    (click)=\"cancel.emit()\"\n  >\n    <i class=\"icon-close\" data-popup-close=\"\" style=\"background-image: none\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 29.7 29.7\">\n        <path\n          d=\"M29.7 1.41L28.28 0 14.85 13.44 1.41 0 0 1.41l13.44 13.44L0 28.28l1.41 1.42 13.44-13.44L28.28 29.7l1.42-1.42-13.44-13.43L29.7 1.41z\"\n        ></path>\n      </svg>\n    </i>\n  </div>\n\n  <div class=\"popup__inner__content gr-10 push-1 no-gutter\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host{display:flex;align-items:center;justify-content:center;width:100%;height:100%}.container{position:relative;box-shadow:0 0 5px #0004}.close{position:absolute;right:15px;top:15px;width:15px;cursor:pointer}.popup__inner{display:flex;font-size:100%;line-height:1.6;min-height:1px;width:83.3333333333%;box-sizing:border-box;vertical-align:top;position:relative;margin:0 auto;background:#fff;transition:margin-top .5s ease,opacity .5s ease;overflow:auto;padding:45px 0 0;max-height:calc(100% - 30px)}.popup--privacy .popup__inner--close{position:absolute;top:15px;right:15px}.popup__inner__content{position:relative;display:inline-block;min-height:1px;box-sizing:border-box;vertical-align:top;left:8.3333333333%;width:83.3333333333%;padding:0}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner{direction:ltr}}@media (max-width: 1439px) and (min-width: 1025px){.popup__inner--title{font-size:28px}}\n"] }]
        }], propDecorators: { closable: [{
                type: Input
            }], cancel: [{
                type: Output
            }] } });

const declarations$9 = [DialogContainerComponent];
class DialogModule {
    static forRoot() {
        return {
            ngModule: DialogModule,
            providers: [DialogService],
        };
    }
    static forChild() {
        return {
            ngModule: DialogModule,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: DialogModule, declarations: [DialogContainerComponent], imports: [CommonModule, OverlayModule], exports: [DialogContainerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DialogModule, imports: [CommonModule, OverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DialogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule],
                    declarations: [declarations$9],
                    exports: [declarations$9],
                }]
        }] });

const declarations$8 = [
    // <...>
    ClickUsageTrackingDirective,
    PrivacyCookiePopupComponent,
    PrivacySettingsPopupComponent,
];
class UsageTrackingModule {
    static forRoot(config) {
        const providers = removeUndefined([
            CookieService,
            UsageTrackingPermissionService,
            whenDefined({
                provide: UsageTrackingService,
                useClass: config?.usageTrackingService ?? UsageTrackingService,
            }),
            whenDefined({
                provide: UsageTrackingSendImplementationService,
                useClass: config?.usageTrackingSendImplementationService ?? UsageTrackingPermissionService,
            }),
            {
                provide: ClickElementMessageTypeId,
                useValue: config.elementClickedMessageType,
            },
        ]);
        return {
            ngModule: UsageTrackingModule,
            providers,
        };
    }
    static forChild() {
        return { ngModule: UsageTrackingModule };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingModule, declarations: [
            // <...>
            ClickUsageTrackingDirective,
            PrivacyCookiePopupComponent,
            PrivacySettingsPopupComponent], imports: [
            // <...>
            CheckboxModule, DialogModule, XGuideConfigurationModule], exports: [
            // <...>
            ClickUsageTrackingDirective,
            PrivacyCookiePopupComponent,
            PrivacySettingsPopupComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingModule, imports: [
            // <...>
            CheckboxModule,
            DialogModule.forChild(),
            XGuideConfigurationModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: UsageTrackingModule, decorators: [{
            type: NgModule,
            args: [{
                    // implicit: dependencies: DialogModule.forRoot(), LocalStorageModule.forRoot()
                    imports: [
                        // <...>
                        CheckboxModule,
                        DialogModule.forChild(),
                        XGuideConfigurationModule,
                    ],
                    declarations: [declarations$8],
                    exports: [declarations$8],
                }]
        }] });

const declarations$7 = [
    // <this comment forces items to be placed below each other>
    ArticleCarouselComponent,
    DashboardTileComponent,
    WelcomeTileComponent,
    GettingStartedTileComponent,
];
class DashboardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DashboardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: DashboardModule, declarations: [
            // <this comment forces items to be placed below each other>
            ArticleCarouselComponent,
            DashboardTileComponent,
            WelcomeTileComponent,
            GettingStartedTileComponent], imports: [
            // <this comment forces items to be placed below each other>
            CommonModule,
            ButtonModule,
            HttpClientModule,
            MediaModule, UsageTrackingModule, XGuideConfigurationModule], exports: [
            // <this comment forces items to be placed below each other>
            ArticleCarouselComponent,
            DashboardTileComponent,
            WelcomeTileComponent,
            GettingStartedTileComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DashboardModule, imports: [
            // <this comment forces items to be placed below each other>
            CommonModule,
            ButtonModule,
            HttpClientModule,
            MediaModule,
            UsageTrackingModule.forChild(),
            XGuideConfigurationModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DashboardModule, decorators: [{
            type: NgModule,
            args: [{
                    // implicit dependencies: UsageTrackingModule.forRoot()
                    imports: [
                        // <this comment forces items to be placed below each other>
                        CommonModule,
                        ButtonModule,
                        HttpClientModule,
                        MediaModule,
                        UsageTrackingModule.forChild(),
                        XGuideConfigurationModule,
                    ],
                    declarations: [declarations$7],
                    exports: [declarations$7],
                }]
        }] });

class ThumbnailSizeProvider {
}

// Increasing integer for generating unique ids for thumbnail item components.
let nextUniqueId = 0;
class ThumbnailItemComponent extends MediaBaseComponent {
    /**
     * Whether this thumbnail item is currently selected.
     */
    get selected() {
        return this._selected;
    }
    set selected(value) {
        if (this._selected !== value) {
            this._selected = value;
            if (this._selected) {
                this.selectionDispatcher.notify(this.id, undefined);
            }
        }
    }
    /** Gets the size, inherited from the `ThumbnailSizeProvider`. */
    get size() {
        return this.sizeProvider?.size ?? 'm';
    }
    get sizeClass() {
        return this.size === 'l';
    }
    constructor(changeDetector, selectionDispatcher, sizeProvider) {
        super(changeDetector);
        this.selectionDispatcher = selectionDispatcher;
        this.sizeProvider = sizeProvider;
        /** @ignore */
        this.MediaType = MediaType;
        /** @ignore */
        this._selected = false;
        /**
         * The id representing this item.
         */
        this.id = `thumbnail-item-${++nextUniqueId}`;
    }
    /** @ignore */
    handleClick() {
        this.selected = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ThumbnailItemComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i1$3.UniqueSelectionDispatcher }, { token: ThumbnailSizeProvider, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: ThumbnailItemComponent, selector: "xui-thumbnail-item", inputs: { selected: "selected", id: "id" }, host: { listeners: { "click": "handleClick()" }, properties: { "class.selected": "this.selected", "class.large": "this.sizeClass" } }, usesInheritance: true, ngImport: i0, template: "<ng-container *ngIf=\"!hasError; else placeholder\">\n  <xui-media class=\"media\" [media]=\"media\" viewMode=\"cover\" [hideControls]=\"true\"></xui-media>\n</ng-container>\n\n<ng-template #placeholder>\n  <xui-placeholder [type]=\"media?.type\"></xui-placeholder>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:flex;align-items:center;justify-content:center;padding:0;margin:0;border:1px solid #a1a1a1;background:#f7f7f7}:host:focus{border:2px solid #51a7eb}:host:hover{cursor:pointer;border:1px solid #51a7eb}:host:active{border:1px solid #51a7eb}:host.selected{border:1px solid #51a7eb;box-shadow:0 2px 8px #51a7eb66}@media only screen and (max-device-width: 1919px){:host{width:62px;height:48px}:host.large{width:128px;height:99px}}@media only screen and (min-device-width: 1920px){:host{width:74px;height:58px}:host.large{width:140px;height:100px}}.cover{object-fit:cover;width:100%;height:100%}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MediaComponent, selector: "xui-media", inputs: ["viewMode", "hideControls"] }, { kind: "component", type: PlaceholderComponent, selector: "xui-placeholder", inputs: ["type"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ThumbnailItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-thumbnail-item', template: "<ng-container *ngIf=\"!hasError; else placeholder\">\n  <xui-media class=\"media\" [media]=\"media\" viewMode=\"cover\" [hideControls]=\"true\"></xui-media>\n</ng-container>\n\n<ng-template #placeholder>\n  <xui-placeholder [type]=\"media?.type\"></xui-placeholder>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:flex;align-items:center;justify-content:center;padding:0;margin:0;border:1px solid #a1a1a1;background:#f7f7f7}:host:focus{border:2px solid #51a7eb}:host:hover{cursor:pointer;border:1px solid #51a7eb}:host:active{border:1px solid #51a7eb}:host.selected{border:1px solid #51a7eb;box-shadow:0 2px 8px #51a7eb66}@media only screen and (max-device-width: 1919px){:host{width:62px;height:48px}:host.large{width:128px;height:99px}}@media only screen and (min-device-width: 1920px){:host{width:74px;height:58px}:host.large{width:140px;height:100px}}.cover{object-fit:cover;width:100%;height:100%}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i1$3.UniqueSelectionDispatcher }, { type: ThumbnailSizeProvider, decorators: [{
                    type: Optional
                }] }], propDecorators: { selected: [{
                type: HostBinding,
                args: ['class.selected']
            }, {
                type: Input
            }], id: [{
                type: Input
            }], sizeClass: [{
                type: HostBinding,
                args: ['class.large']
            }], handleClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class ThumbnailComponent {
    get selectedItemIndex() {
        if (this.selectedItem) {
            return this.itemsArray.indexOf(this.selectedItem);
        }
        const index = this.itemsArray.findIndex(i => i.selected);
        return index;
    }
    get itemsArray() {
        return this.items?.toArray() ?? [];
    }
    /** @ignore */
    get selectedItem() {
        return this._selectedItem;
    }
    set selectedItem(item) {
        if (this._selectedItem !== item) {
            this._selectedItem = item;
            this.tryScrollThumbnailIntoContainerCenter();
            this.selectedItemChange.emit(this._selectedItem);
        }
    }
    /** @ignore */
    get isScrollingActive() {
        if (this.scrollContainer !== undefined && this.scrollContainer.nativeElement !== undefined) {
            const scrollContainerElement = this.scrollContainer.nativeElement;
            return scrollContainerElement.scrollHeight > scrollContainerElement.clientHeight;
        }
        return false;
    }
    get sizeClass() {
        return this.size === 'l';
    }
    /** @ignore */
    constructor(selectionDispatcher, changeDetectorRef) {
        this.selectionDispatcher = selectionDispatcher;
        this.changeDetectorRef = changeDetectorRef;
        /** Gets or sets the size mode of the thumbnail. */
        this.size = 'm';
        /**
         * Event emitted whenever the selected item changed.
         */
        this.selectedItemChange = new EventEmitter();
    }
    /** @ignore */
    ngAfterContentInit() {
        this.unsubscribeFromSelectionDispatcher = this.selectionDispatcher.listen((id, _) => {
            this.selectItem(this.itemsArray, id);
        });
        const initiallySelectedItem = this.items.find(item => item.selected);
        if (initiallySelectedItem !== undefined) {
            this.selectItem(this.itemsArray, initiallySelectedItem.id);
        }
        // We need to trigger change detection one more time because the scrolling may be active now.
        this.changeDetectorRef.detectChanges();
    }
    /** @ignore */
    ngOnDestroy() {
        this.unsubscribeFromSelectionDispatcher?.();
    }
    /**
     * Determines whether the next item can be selected.
     * @returns Whether the next item can be selected.
     */
    canSelectNextItem() {
        return this.canSelectSiblingItem(+1);
    }
    /**
     * Selects the next item – if possible. You can check that with the `canSelectNextItem()` method.
     */
    selectNextItem() {
        this.selectSiblingItem(+1);
    }
    /**
     * Determines whether the previous item can be selected.
     * @returns Whether the previous item can be selected.
     */
    canSelectPreviousItem() {
        return this.canSelectSiblingItem(-1);
    }
    /**
     * Selects the previous item – if possible. You can check that with the `canSelectPreviousItem()` method.
     */
    selectPreviousItem() {
        this.selectSiblingItem(-1);
    }
    /** @ignore */
    selectSiblingItem(position) {
        const siblingItemIndex = this.selectedItemIndex + position;
        const siblingItem = this.itemsArray[siblingItemIndex];
        if (siblingItem) {
            siblingItem.selected = true;
        }
    }
    /** @ignore */
    canSelectSiblingItem(position) {
        const siblingItemIndex = this.selectedItemIndex + position;
        return this.itemsArray[siblingItemIndex] !== undefined;
    }
    /** @ignore */
    selectItem(items, itemIdToSelect) {
        for (const item of items) {
            if (itemIdToSelect === item.id) {
                if (this.selectedItem !== item) {
                    this.selectedItem = item;
                }
            }
            else {
                item.selected = false;
            }
        }
    }
    /** @ignore */
    tryScrollThumbnailIntoContainerCenter() {
        if (this.scrollContainer !== undefined) {
            const scrollableContainerSize = this.scrollContainer.nativeElement.getBoundingClientRect();
            const availableScrollContainerHeight = scrollableContainerSize.height;
            const thumbnailHtmlElement = this.thumbnailHtmlElements.toArray()[this.selectedItemIndex].nativeElement;
            const thumbnailHtmlElementHeight = thumbnailHtmlElement.getBoundingClientRect().height;
            const verticalSpaceForThumbnail = this.getTotalVerticalSpaceForHtmlElement(thumbnailHtmlElementHeight, getComputedStyle(thumbnailHtmlElement));
            const offsetToSelectedThumbnail = verticalSpaceForThumbnail * this.selectedItemIndex;
            const offsetToCenterSelectedThumnail = offsetToSelectedThumbnail - (availableScrollContainerHeight - thumbnailHtmlElementHeight) / 2;
            // Scrolling may not be available.
            if (this.scrollContainer.nativeElement.scrollTo) {
                this.scrollContainer.nativeElement.scrollTo({
                    behavior: 'smooth',
                    top: offsetToCenterSelectedThumnail,
                });
            }
        }
    }
    /** @ignore */
    getTotalVerticalSpaceForHtmlElement(elementHeight, computedStyle) {
        const margins = this.extractPixelValueFromStyleString(computedStyle.marginTop) +
            this.extractPixelValueFromStyleString(computedStyle.marginBottom);
        return elementHeight + margins;
    }
    /** @ignore */
    extractPixelValueFromStyleString(styleString) {
        if (styleString === undefined) {
            return 0;
        }
        return parseInt(styleString.split('px')[0], 10);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ThumbnailComponent, deps: [{ token: i1$3.UniqueSelectionDispatcher }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: ThumbnailComponent, selector: "xui-thumbnail", inputs: { size: "size" }, outputs: { selectedItemChange: "selectedItemChange" }, host: { properties: { "class.large": "this.sizeClass" }, classAttribute: "interactive-element" }, providers: [
            { provide: UniqueSelectionDispatcher, useClass: UniqueSelectionDispatcher },
            { provide: ThumbnailSizeProvider, useExisting: ThumbnailComponent },
        ], queries: [{ propertyName: "items", predicate: ThumbnailItemComponent, descendants: true, read: ThumbnailItemComponent }, { propertyName: "thumbnailHtmlElements", predicate: ThumbnailItemComponent, descendants: true, read: ElementRef }], viewQueries: [{ propertyName: "scrollContainer", first: true, predicate: ["scrollContainer"], descendants: true }], ngImport: i0, template: "<button\n  class=\"thumbnail btn-icon-only\"\n  [disabled]=\"!canSelectPreviousItem()\"\n  (click)=\"selectPreviousItem()\"\n  *ngIf=\"isScrollingActive\"\n>\n  <xui-icon class=\"icon\" iconKey=\"Chevron.Up\"></xui-icon>\n</button>\n<div #scrollContainer class=\"thumbnails\">\n  <ng-content></ng-content>\n</div>\n<button\n  class=\"thumbnail btn-icon-only\"\n  [disabled]=\"!canSelectNextItem()\"\n  (click)=\"selectNextItem()\"\n  *ngIf=\"isScrollingActive\"\n>\n  <xui-icon class=\"icon\" iconKey=\"Chevron.Down\"></xui-icon>\n</button>\n", styles: ["@charset \"UTF-8\";:host{display:flex;flex-direction:column;align-items:center;justify-content:space-between}@media only screen and (max-device-width: 1919px){:host{width:64px;min-width:64px;height:256px}:host.large{width:128px;min-width:128px;height:296px}}@media only screen and (min-device-width: 1920px){:host{width:77px;min-width:77px;height:256px}:host.large{width:150px;min-width:150px;height:366px}}button{display:inline-flex;justify-content:center;flex-shrink:0;border:none;background:#fff}button xui-icon ::ng-deep svg{fill:#000;stroke:none}button:hover{cursor:pointer}button:hover xui-icon ::ng-deep svg{fill:#51a7eb}button:disabled{cursor:pointer}button:disabled xui-icon ::ng-deep svg{fill:#9a9a9a}button:focus{border:none}.thumbnails{flex-grow:1;flex-shrink:1;display:flex;flex-direction:column;overflow-y:auto}.thumbnails::-webkit-scrollbar{display:none}.thumbnails ::ng-deep xui-thumbnail-item{margin:8px 0;flex-shrink:0}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconComponent, selector: "xui-icon", inputs: ["size", "iconKey", "color", "outlined"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ThumbnailComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-thumbnail', host: {
                        class: 'interactive-element',
                    }, providers: [
                        { provide: UniqueSelectionDispatcher, useClass: UniqueSelectionDispatcher },
                        { provide: ThumbnailSizeProvider, useExisting: ThumbnailComponent },
                    ], template: "<button\n  class=\"thumbnail btn-icon-only\"\n  [disabled]=\"!canSelectPreviousItem()\"\n  (click)=\"selectPreviousItem()\"\n  *ngIf=\"isScrollingActive\"\n>\n  <xui-icon class=\"icon\" iconKey=\"Chevron.Up\"></xui-icon>\n</button>\n<div #scrollContainer class=\"thumbnails\">\n  <ng-content></ng-content>\n</div>\n<button\n  class=\"thumbnail btn-icon-only\"\n  [disabled]=\"!canSelectNextItem()\"\n  (click)=\"selectNextItem()\"\n  *ngIf=\"isScrollingActive\"\n>\n  <xui-icon class=\"icon\" iconKey=\"Chevron.Down\"></xui-icon>\n</button>\n", styles: ["@charset \"UTF-8\";:host{display:flex;flex-direction:column;align-items:center;justify-content:space-between}@media only screen and (max-device-width: 1919px){:host{width:64px;min-width:64px;height:256px}:host.large{width:128px;min-width:128px;height:296px}}@media only screen and (min-device-width: 1920px){:host{width:77px;min-width:77px;height:256px}:host.large{width:150px;min-width:150px;height:366px}}button{display:inline-flex;justify-content:center;flex-shrink:0;border:none;background:#fff}button xui-icon ::ng-deep svg{fill:#000;stroke:none}button:hover{cursor:pointer}button:hover xui-icon ::ng-deep svg{fill:#51a7eb}button:disabled{cursor:pointer}button:disabled xui-icon ::ng-deep svg{fill:#9a9a9a}button:focus{border:none}.thumbnails{flex-grow:1;flex-shrink:1;display:flex;flex-direction:column;overflow-y:auto}.thumbnails::-webkit-scrollbar{display:none}.thumbnails ::ng-deep xui-thumbnail-item{margin:8px 0;flex-shrink:0}\n"] }]
        }], ctorParameters: () => [{ type: i1$3.UniqueSelectionDispatcher }, { type: i0.ChangeDetectorRef }], propDecorators: { size: [{
                type: Input
            }], selectedItemChange: [{
                type: Output
            }], items: [{
                type: ContentChildren,
                args: [ThumbnailItemComponent, { descendants: true, read: ThumbnailItemComponent }]
            }], thumbnailHtmlElements: [{
                type: ContentChildren,
                args: [ThumbnailItemComponent, { descendants: true, read: ElementRef }]
            }], scrollContainer: [{
                type: ViewChild,
                args: ['scrollContainer']
            }], sizeClass: [{
                type: HostBinding,
                args: ['class.large']
            }] } });

const BOLD_REGEX = /\*(.*?)\*/g;
const BOLD_HTML_MARKUP = '<b>$1</b>';
const LINE_BREAK_SYMBOL = '\n';
const LINE_BREAK_REGEX = new RegExp(LINE_BREAK_SYMBOL, 'g');
const LINE_BREAK_HTML_MARKUP = '<br>';
const FORMATTERS = [formatBold, formatNewLines];
/**
 * Takes a string that is similar to markdown and turns it into a formatted HTML string.
 *
 * **Example**:
 *
 * ~~~ts
 * const text = 'This text will be *bold*. \nAlso newlines are possible.';
 * const html = formatText(text);
 * console.log(html); // => "This text will be <b>bold</b>. <br/> Also newlines are possible."
 * ~~~
 *
 * @param text The text to be turned into a formatted HTML string.
 * @returns An HTML-string applying the format as specified in the original, markdown-similar string.
 */
function formatText(text) {
    let result = sanitize(text);
    FORMATTERS.forEach(formatter => {
        result = formatter(result);
    });
    return result;
}
function formatBold(text) {
    // https://regex101.com/r/pj32mY/1
    return text.replace(BOLD_REGEX, BOLD_HTML_MARKUP);
}
function formatNewLines(text) {
    // https://regex101.com/r/pj32mY/2
    return text.replace(LINE_BREAK_REGEX, LINE_BREAK_HTML_MARKUP);
}
function sanitize(text) {
    const definedText = text ?? '';
    if (typeof definedText !== 'string') {
        throw new Error(`formatText: The text to format must be of type "string" but is of type "${typeof definedText}".`);
    }
    return definedText;
}

class TextFormatPipe {
    transform(text) {
        return formatText(text);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TextFormatPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: TextFormatPipe, name: "textFormat" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TextFormatPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'textFormat' }]
        }] });

class MediaGalleryComponent {
    constructor() {
        /**
         * The media objects to be shown in the gallery.
         */
        this.items = [];
        /**
         * The index of the selected media. Defaults to `0`.
         */
        this.selectedIndex = 0;
        /** Event fires whenever the selected media-index changes */
        this.selectedIndexChange = new EventEmitter();
        /** @ignore */
        this.MediaType = MediaType;
    }
    /** @ignore */
    getMediaAtIndex(index) {
        return this.items?.[index];
    }
    /** @ignore */
    emitIndexChange(itemId) {
        const indexFromId = parseInt(itemId, 10);
        this.selectedIndex = indexFromId;
        // event-handlers often re-set the index, so move to next render tick,
        // to avoid expression-changed-after-checked errors.
        requestAnimationFrame(() => {
            this.selectedIndexChange.emit(indexFromId);
        });
    }
    /** @ignore */
    navigateNextWhenImage(media) {
        if (media.type === MediaType.VIDEO) {
            return;
        }
        this.selectedIndex = (this.selectedIndex + 1) % this.items.length;
        // wait for next media to be rendered, then check whether to auto-play potential video
        requestAnimationFrame(() => {
            const current = this.items[this.selectedIndex];
            if (current.type === MediaType.VIDEO) {
                this.currentMedia.play();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MediaGalleryComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: MediaGalleryComponent, selector: "xui-media-gallery", inputs: { items: "items", selectedIndex: "selectedIndex" }, outputs: { selectedIndexChange: "selectedIndexChange" }, viewQueries: [{ propertyName: "currentMedia", first: true, predicate: ["currentMedia"], descendants: true, read: MediaComponent }], ngImport: i0, template: "<div class=\"title-container\">\n  <!-- hint: use an extra ng-container for *ngIf to only remove content,\n       but keep container in DOM for layout. -->\n  <ng-container *ngIf=\"getMediaAtIndex(selectedIndex) as media\">\n    <xui-media\n      #currentMedia\n      class=\"media\"\n      [class.image]=\"media?.type === MediaType.IMAGE\"\n      [media]=\"media\"\n      (click)=\"navigateNextWhenImage(media)\"\n      data-ngtx=\"media-gallery/current-media\"\n    ></xui-media>\n\n    <div\n      *ngIf=\"media.description\"\n      class=\"image-description\"\n      [innerHTML]=\"media.description | textFormat\"\n      data-ngtx=\"media-gallery/description\"\n    ></div>\n  </ng-container>\n</div>\n\n<xui-thumbnail class=\"gallery\" (selectedItemChange)=\"emitIndexChange($event.id)\" data-ngtx=\"media-gallery/thumbnail\">\n  <xui-thumbnail-item\n    *ngFor=\"let thumbnail of items; let index = index\"\n    [id]=\"index.toString()\"\n    [selected]=\"index === selectedIndex\"\n    [media]=\"thumbnail\"\n    data-ngtx=\"media-gallery/thumbnail-item\"\n  ></xui-thumbnail-item>\n</xui-thumbnail>\n", styles: [":host{display:flex;flex-direction:row;max-height:636px}:host .media{flex:1 1 100%;height:0}:host .media.image{cursor:pointer}:host .title-container{display:flex;flex-direction:column;flex:1 1 100%}:host .title-image{background:#f8f8f8;flex:1 1 100%;overflow:hidden}:host .description{column-count:1;column-gap:34px;margin:16px 8px;font-size:16px}:host .image-description{margin:25px;min-height:1px;flex:0 0 auto}:host .gallery{margin:0 0 0 25px;display:flex;flex-direction:column;height:auto}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MediaComponent, selector: "xui-media", inputs: ["viewMode", "hideControls"] }, { kind: "component", type: ThumbnailComponent, selector: "xui-thumbnail", inputs: ["size"], outputs: ["selectedItemChange"] }, { kind: "component", type: ThumbnailItemComponent, selector: "xui-thumbnail-item", inputs: ["selected", "id"] }, { kind: "pipe", type: TextFormatPipe, name: "textFormat" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MediaGalleryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-media-gallery', template: "<div class=\"title-container\">\n  <!-- hint: use an extra ng-container for *ngIf to only remove content,\n       but keep container in DOM for layout. -->\n  <ng-container *ngIf=\"getMediaAtIndex(selectedIndex) as media\">\n    <xui-media\n      #currentMedia\n      class=\"media\"\n      [class.image]=\"media?.type === MediaType.IMAGE\"\n      [media]=\"media\"\n      (click)=\"navigateNextWhenImage(media)\"\n      data-ngtx=\"media-gallery/current-media\"\n    ></xui-media>\n\n    <div\n      *ngIf=\"media.description\"\n      class=\"image-description\"\n      [innerHTML]=\"media.description | textFormat\"\n      data-ngtx=\"media-gallery/description\"\n    ></div>\n  </ng-container>\n</div>\n\n<xui-thumbnail class=\"gallery\" (selectedItemChange)=\"emitIndexChange($event.id)\" data-ngtx=\"media-gallery/thumbnail\">\n  <xui-thumbnail-item\n    *ngFor=\"let thumbnail of items; let index = index\"\n    [id]=\"index.toString()\"\n    [selected]=\"index === selectedIndex\"\n    [media]=\"thumbnail\"\n    data-ngtx=\"media-gallery/thumbnail-item\"\n  ></xui-thumbnail-item>\n</xui-thumbnail>\n", styles: [":host{display:flex;flex-direction:row;max-height:636px}:host .media{flex:1 1 100%;height:0}:host .media.image{cursor:pointer}:host .title-container{display:flex;flex-direction:column;flex:1 1 100%}:host .title-image{background:#f8f8f8;flex:1 1 100%;overflow:hidden}:host .description{column-count:1;column-gap:34px;margin:16px 8px;font-size:16px}:host .image-description{margin:25px;min-height:1px;flex:0 0 auto}:host .gallery{margin:0 0 0 25px;display:flex;flex-direction:column;height:auto}\n"] }]
        }], propDecorators: { items: [{
                type: Input
            }], selectedIndex: [{
                type: Input
            }], selectedIndexChange: [{
                type: Output
            }], currentMedia: [{
                type: ViewChild,
                args: ['currentMedia', { read: MediaComponent }]
            }] } });

const declarations$6 = [
    // <...>
    MediaGalleryComponent,
    TextFormatPipe,
    ThumbnailComponent,
    ThumbnailItemComponent,
];
class GalleryModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: GalleryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: GalleryModule, declarations: [
            // <...>
            MediaGalleryComponent,
            TextFormatPipe,
            ThumbnailComponent,
            ThumbnailItemComponent], imports: [CommonModule, MediaModule, IconModule], exports: [
            // <...>
            MediaGalleryComponent,
            TextFormatPipe,
            ThumbnailComponent,
            ThumbnailItemComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: GalleryModule, imports: [CommonModule, MediaModule, IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: GalleryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MediaModule, IconModule],
                    declarations: [declarations$6],
                    exports: [declarations$6],
                }]
        }] });

/** **Please use the `forRoot()` method.** This module is supposed to be imported only **once** – into the AppModule. */
class LocalStorageModule {
    static forRoot() {
        return {
            ngModule: LocalStorageModule,
            providers: [
                LocalStorageService,
                {
                    provide: LocalStorageToken,
                    useValue: localStorage,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: LocalStorageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: LocalStorageModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: LocalStorageModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: LocalStorageModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

/**
 * The menu-tab is only used inside the menu container.
 * The displayed icon is defined via the iconKey property.
 * Adding the `selected` class to the components css classes sets the selected state.
 * The menu-tabs displayed text is provided via the DOM content of the component.
 *
 * ```html
 * <xui-menu>
 *  <xui-menu-tab iconKey="House" [class.selected]="true">Dashboard</xui-menu-tab>
 *  ...
 * </xui-menu>
 * ```
 */
class MenuTabComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MenuTabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: MenuTabComponent, selector: "xui-menu-tab", inputs: { iconKey: "iconKey" }, host: { classAttribute: "tab-mainmenu interactive-element" }, ngImport: i0, template: "<xui-icon\n  *ngIf=\"iconKey != undefined\"\n  data-ngtx=\"menu-tab:icon\"\n  [iconKey]=\"iconKey\"\n  [class.icon-only]=\"label.textContent === ''\"\n></xui-icon>\n\n<div class=\"content\" #label>\n  <ng-content></ng-content>\n</div>\n", styles: ["@charset \"UTF-8\";:host.dashboard{width:82px;box-sizing:border-box;padding:unset;box-shadow:unset}:host.dashboard xui-icon{width:47px;height:47px;margin:auto}:host:not(.dashboard) xui-icon{margin-right:8px}:host xui-icon ::ng-deep svg{fill:#fff;stroke:#fff}:host:focus{border:none}:host.selected xui-icon ::ng-deep svg{fill:#74b9ef;stroke:#74b9ef}:host.selected.dashboard{background:#285172}@media only screen and (max-width: 950px){.content{display:none}xui-icon{margin-right:8px}}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconComponent, selector: "xui-icon", inputs: ["size", "iconKey", "color", "outlined"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MenuTabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-menu-tab', host: { class: 'tab-mainmenu interactive-element' }, template: "<xui-icon\n  *ngIf=\"iconKey != undefined\"\n  data-ngtx=\"menu-tab:icon\"\n  [iconKey]=\"iconKey\"\n  [class.icon-only]=\"label.textContent === ''\"\n></xui-icon>\n\n<div class=\"content\" #label>\n  <ng-content></ng-content>\n</div>\n", styles: ["@charset \"UTF-8\";:host.dashboard{width:82px;box-sizing:border-box;padding:unset;box-shadow:unset}:host.dashboard xui-icon{width:47px;height:47px;margin:auto}:host:not(.dashboard) xui-icon{margin-right:8px}:host xui-icon ::ng-deep svg{fill:#fff;stroke:#fff}:host:focus{border:none}:host.selected xui-icon ::ng-deep svg{fill:#74b9ef;stroke:#74b9ef}:host.selected.dashboard{background:#285172}@media only screen and (max-width: 950px){.content{display:none}xui-icon{margin-right:8px}}\n"] }]
        }], propDecorators: { iconKey: [{
                type: Input
            }] } });

/**
 * The menu wraps menu-tabs.
 *
 * ```html
 * <xui-menu>
 *  ...
 *  <xui-menu-tab ..>Text</xui-menu-tab>
 *  ...
 * </xui-menu>
 * ```
 */
class MenuComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: MenuComponent, selector: "xui-menu", host: { classAttribute: "main-menu" }, ngImport: i0, template: "<ng-content></ng-content>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-menu', host: { class: 'main-menu' }, template: "<ng-content></ng-content>\n" }]
        }] });

/**
 * Sub-menu-sub-tabs are nested in sub-menu-tabs.
 * The label can be set via the {@param label} property.
 * It can be selected by default by using the {@param selected} flag.
 */
class SubMenuSubTabComponent {
    constructor() {
        /** @ignore */
        this._selected = false;
        /**
         * The text to display as label for this tab.
         */
        this.label = '';
        /**
         * Event emitted whenever the selection of this tab changes.
         */
        this.selectedChange = new EventEmitter();
    }
    /**
     * Whether this tab is selected.
     */
    get selected() {
        return this._selected;
    }
    set selected(value) {
        if (value !== this.selected) {
            this._selected = value;
            this.selectedChange.emit(value);
        }
    }
    /** @ignore */
    onClick(e) {
        e.stopPropagation();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SubMenuSubTabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: SubMenuSubTabComponent, selector: "xui-sub-menu-sub-tab", inputs: { label: "label", selected: "selected" }, outputs: { selectedChange: "selectedChange" }, host: { listeners: { "click": "onClick($event)" }, properties: { "class.selected": "this.selected" } }, ngImport: i0, template: "<span class=\"sub-menu-sub-label\" data-ngtx=\"sub-menu-sub-tab:label\">{{ label }}</span>\n", styles: ["@charset \"UTF-8\";:host{display:flex;align-items:center;height:77px;border-right:1px solid #000;border-bottom:1px solid #000;padding:0 16px}:host:active{box-shadow:inset 4px 0 #51a7eb}:host.selected{box-shadow:inset 4px 0 #51a7eb;border-right:none;border-bottom:1px solid #000;background-color:#fff;color:#142939;font-weight:700}.sub-menu-sub-label{margin-left:30px;text-overflow:ellipsis;overflow:hidden;line-height:initial}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SubMenuSubTabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-sub-menu-sub-tab', template: "<span class=\"sub-menu-sub-label\" data-ngtx=\"sub-menu-sub-tab:label\">{{ label }}</span>\n", styles: ["@charset \"UTF-8\";:host{display:flex;align-items:center;height:77px;border-right:1px solid #000;border-bottom:1px solid #000;padding:0 16px}:host:active{box-shadow:inset 4px 0 #51a7eb}:host.selected{box-shadow:inset 4px 0 #51a7eb;border-right:none;border-bottom:1px solid #000;background-color:#fff;color:#142939;font-weight:700}.sub-menu-sub-label{margin-left:30px;text-overflow:ellipsis;overflow:hidden;line-height:initial}\n"] }]
        }], propDecorators: { label: [{
                type: Input
            }], selected: [{
                type: HostBinding,
                args: ['class.selected']
            }, {
                type: Input
            }], selectedChange: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

/**
 * The sub-menu-tab is only used inside the sub-menu.
 * It can nest sub-menu-sub-tabs that are hidden by default.
 * It can be selected by default by using the {@param selected} flag.
 * It can be opened by default by using the {@param open} flag.
 * The label is set via the {@param label} property.
 * Clicking the icon opens the sub-menu-tab and reveals the sub-menu-sub-tabs.
 *
 * ```html
 * <xui-sub-menu>
 *  <xui-sub-menu-tab label="Text">
 *    ...
 *    <xui-sub-menu-sub-tab ...></xui-sub-menu-sub-tab>
 *    ...
 *  </xui-sub-menu-tab>
 *  ...
 * </xui-sub-menu>
 * ```
 */
class SubMenuTabComponent {
    constructor() {
        /**
         * Whether this component shows its child-tabs.
         */
        this.open = false;
        /**
         * The text to show as a label for this control.
         */
        this.label = '';
        /**
         * Whether this tab is currently selected.
         */
        this.selected = false;
    }
    get hasChildren() {
        return this.subTabs?.length > 0;
    }
    /** @ignore */
    ngAfterContentInit() {
        this.subTabs.forEach(tab => this.checkSelection(tab.selected));
        const subTabSelections = merge(...this.subTabs.map(tab => tab.selectedChange.asObservable()));
        this.selectionSubscription = subTabSelections.subscribe(someSelected => this.checkSelection(someSelected));
    }
    /** @ignore */
    ngOnDestroy() {
        this.selectionSubscription?.unsubscribe();
    }
    /** @ignore */
    toggle() {
        this.open = !this.open;
    }
    /** @ignore */
    checkSelection(selected) {
        if (selected) {
            this.open = true;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SubMenuTabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: SubMenuTabComponent, selector: "xui-sub-menu-tab", inputs: { open: "open", label: "label", selected: "selected" }, queries: [{ propertyName: "subTabs", predicate: SubMenuSubTabComponent }], ngImport: i0, template: "<div class=\"sub-menu-button\" data-ngtx=\"sub-menu-tab:button\" [class.selected]=\"selected\">\n  <span class=\"sub-menu-label\" data-ngtx=\"sub-menu-tab:label\">{{ label }}</span>\n  <span\n    class=\"icon sub-menu-icon icon-chevron-down\"\n    data-ngtx=\"sub-menu-tab:arrow-icon\"\n    [style.visibility]=\"hasChildren ? 'visible' : 'hidden'\"\n    [class.open]=\"open\"\n    (click)=\"$event.stopPropagation(); toggle()\"\n  ></span>\n</div>\n<div *ngIf=\"open\" class=\"sub-menu-sub-tabs\" data-ngtx=\"sub-menu-tab:contents\">\n  <ng-content></ng-content>\n</div>\n", styles: ["@charset \"UTF-8\";:host{display:flex;flex-direction:column;cursor:pointer;width:100%}.sub-menu-button{display:flex;align-items:center;justify-content:space-between;height:77px;border-right:1px solid #000;border-bottom:1px solid #000;padding:0 12px}.sub-menu-button:active{box-shadow:inset 4px 0 #51a7eb}.sub-menu-button.selected{box-shadow:inset 4px 0 #51a7eb;border-right:none;background-color:#fff;color:#142939;font-weight:700}.sub-menu-label{margin-left:8px;text-overflow:ellipsis;overflow:hidden;line-height:initial}.sub-menu-icon{font-size:32px}.sub-menu-icon.open{transform:rotate(180deg)}.sub-menu-icon:hover{color:#177ac9}.sub-menu-sub-tabs{width:100%;box-sizing:border-box}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SubMenuTabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-sub-menu-tab', template: "<div class=\"sub-menu-button\" data-ngtx=\"sub-menu-tab:button\" [class.selected]=\"selected\">\n  <span class=\"sub-menu-label\" data-ngtx=\"sub-menu-tab:label\">{{ label }}</span>\n  <span\n    class=\"icon sub-menu-icon icon-chevron-down\"\n    data-ngtx=\"sub-menu-tab:arrow-icon\"\n    [style.visibility]=\"hasChildren ? 'visible' : 'hidden'\"\n    [class.open]=\"open\"\n    (click)=\"$event.stopPropagation(); toggle()\"\n  ></span>\n</div>\n<div *ngIf=\"open\" class=\"sub-menu-sub-tabs\" data-ngtx=\"sub-menu-tab:contents\">\n  <ng-content></ng-content>\n</div>\n", styles: ["@charset \"UTF-8\";:host{display:flex;flex-direction:column;cursor:pointer;width:100%}.sub-menu-button{display:flex;align-items:center;justify-content:space-between;height:77px;border-right:1px solid #000;border-bottom:1px solid #000;padding:0 12px}.sub-menu-button:active{box-shadow:inset 4px 0 #51a7eb}.sub-menu-button.selected{box-shadow:inset 4px 0 #51a7eb;border-right:none;background-color:#fff;color:#142939;font-weight:700}.sub-menu-label{margin-left:8px;text-overflow:ellipsis;overflow:hidden;line-height:initial}.sub-menu-icon{font-size:32px}.sub-menu-icon.open{transform:rotate(180deg)}.sub-menu-icon:hover{color:#177ac9}.sub-menu-sub-tabs{width:100%;box-sizing:border-box}\n"] }]
        }], propDecorators: { open: [{
                type: Input
            }], label: [{
                type: Input
            }], selected: [{
                type: Input
            }], subTabs: [{
                type: ContentChildren,
                args: [SubMenuSubTabComponent]
            }] } });

/**
 * The sub-menu wraps sub-menu-tabs.
 *
 * ```html
 * <xui-sub-menu>
 *  ...
 *  <xui-sub-menu-tab ..>
 *    ...
 *    <xui-sub-menu-sub-tab ...></xui-sub-menu-sub-tab>
 *    ...
 *  </xui-sub-menu-tab>
 *  ...
 * </xui-sub-menu>
 * ```
 */
class SubMenuComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SubMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: SubMenuComponent, selector: "xui-sub-menu", ngImport: i0, template: "<div class=\"content interactive-element\">\n  <ng-content></ng-content>\n</div>\n<div class=\"spacer\"></div>\n", styles: ["@charset \"UTF-8\";:host{display:flex;flex-direction:column;min-width:200px;max-width:245px;height:100%}.spacer{box-sizing:border-box;flex:1 1 100%;height:0;border-right:1px solid black;pointer-events:none;width:200px}.content{display:flex;flex-direction:column;flex:1 0 auto;overflow:auto}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SubMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-sub-menu', template: "<div class=\"content interactive-element\">\n  <ng-content></ng-content>\n</div>\n<div class=\"spacer\"></div>\n", styles: ["@charset \"UTF-8\";:host{display:flex;flex-direction:column;min-width:200px;max-width:245px;height:100%}.spacer{box-sizing:border-box;flex:1 1 100%;height:0;border-right:1px solid black;pointer-events:none;width:200px}.content{display:flex;flex-direction:column;flex:1 0 auto;overflow:auto}\n"] }]
        }] });

/**
 * Creates a mail-to link to the specified receivers.
 * @param receiver The receiver email address
 * @param cc The CC receiver address
 * @param subject The e-mail subject
 */
function mailToLink(receiver, cc, subject) {
    if (!receiver) {
        throw new Error(`Invalid email address given for receiver: "${receiver}"`);
    }
    validateMailAddresses(receiver, cc);
    const link = `mailto:${receiver}`;
    const linkWithCC = addParamIfDefined('cc', cc, link);
    const urlSafeSubject = subject ? encodeURIComponent(subject) : undefined;
    return addParamIfDefined('subject', urlSafeSubject, linkWithCC);
}
function addParamIfDefined(name, value, link) {
    if (!name || !value) {
        return link;
    }
    let updatedLink = link;
    if (updatedLink.includes('?')) {
        updatedLink += '&';
    }
    else {
        updatedLink += '?';
    }
    updatedLink += name + '=' + value;
    return updatedLink;
}
function validateMailAddresses(...emails) {
    emails.forEach(email => {
        if (email === undefined) {
            return;
        }
        if (!email.includes('@')) {
            throw new Error(`Email address does not include a domain: ${email}`);
        }
    });
}

class MailToMenuTabComponent {
    constructor(translations) {
        this.translations = translations;
    }
    get mailLink() {
        if (this.mailTo) {
            return mailToLink(this.mailTo, this.carbonCopy, this.theme);
        }
        return '';
    }
    onClick() {
        this.mailAnchor?.nativeElement.dispatchEvent(new MouseEvent('click'));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MailToMenuTabComponent, deps: [{ token: Translations }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: MailToMenuTabComponent, selector: "xui-mail-to-menu-tab", inputs: { mailTo: "mailTo", carbonCopy: "carbonCopy", theme: "theme" }, host: { listeners: { "click": "onClick()" }, classAttribute: "tab-mainmenu interactive-element" }, viewQueries: [{ propertyName: "mailAnchor", first: true, predicate: ["mailAnchor"], descendants: true }], ngImport: i0, template: "<xui-icon\n  data-ngtx=\"mail-to-menu-tab:icon\"\n  iconKey=\"Envelope\"\n></xui-icon>\n\n<div #label class=\"content menu-text\">\n  {{ translations.XGUIDE.MENU.CONTACT_US_BUTTON | translate }}\n</div>\n\n<a #mailAnchor [href]=\"mailLink\" data-ngtx=\"hidden-anchor\" hidden></a>\n", styles: ["@charset \"UTF-8\";:host xui-icon{margin-right:8px}:host xui-icon ::ng-deep svg{fill:#bbd03a;stroke:#bbd03a}:host .menu-text{color:#bbd03a}:host:focus{border:none}:host.selected xui-icon ::ng-deep svg{fill:#74b9ef;stroke:#74b9ef}:host.selected.dashboard{background:#285172}@media only screen and (max-width: 950px){.content{display:none}xui-icon{margin-right:8px}}\n"], dependencies: [{ kind: "component", type: IconComponent, selector: "xui-icon", inputs: ["size", "iconKey", "color", "outlined"] }, { kind: "pipe", type: TranslationBridgePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MailToMenuTabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-mail-to-menu-tab', host: { class: 'tab-mainmenu interactive-element' }, template: "<xui-icon\n  data-ngtx=\"mail-to-menu-tab:icon\"\n  iconKey=\"Envelope\"\n></xui-icon>\n\n<div #label class=\"content menu-text\">\n  {{ translations.XGUIDE.MENU.CONTACT_US_BUTTON | translate }}\n</div>\n\n<a #mailAnchor [href]=\"mailLink\" data-ngtx=\"hidden-anchor\" hidden></a>\n", styles: ["@charset \"UTF-8\";:host xui-icon{margin-right:8px}:host xui-icon ::ng-deep svg{fill:#bbd03a;stroke:#bbd03a}:host .menu-text{color:#bbd03a}:host:focus{border:none}:host.selected xui-icon ::ng-deep svg{fill:#74b9ef;stroke:#74b9ef}:host.selected.dashboard{background:#285172}@media only screen and (max-width: 950px){.content{display:none}xui-icon{margin-right:8px}}\n"] }]
        }], ctorParameters: () => [{ type: Translations }], propDecorators: { mailTo: [{
                type: Input
            }], carbonCopy: [{
                type: Input
            }], theme: [{
                type: Input
            }], mailAnchor: [{
                type: ViewChild,
                args: ['mailAnchor']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

const declarations$5 = [
    // <wrap items below>
    MenuComponent,
    MenuTabComponent,
    SubMenuComponent,
    SubMenuSubTabComponent,
    SubMenuTabComponent,
    MailToMenuTabComponent,
];
class MenuModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: MenuModule, declarations: [
            // <wrap items below>
            MenuComponent,
            MenuTabComponent,
            SubMenuComponent,
            SubMenuSubTabComponent,
            SubMenuTabComponent,
            MailToMenuTabComponent], imports: [CommonModule, IconModule, XGuideConfigurationModule], exports: [
            // <wrap items below>
            MenuComponent,
            MenuTabComponent,
            SubMenuComponent,
            SubMenuSubTabComponent,
            SubMenuTabComponent,
            MailToMenuTabComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MenuModule, imports: [CommonModule, IconModule, XGuideConfigurationModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: MenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, IconModule, XGuideConfigurationModule],
                    declarations: [declarations$5],
                    exports: [declarations$5],
                }]
        }] });

const ValueConverters = {
    toBool: (input) => (input === 'true' ? true : false),
    toFloat: (input) => parseFloat(input),
    toInt: (input) => parseInt(input, 10),
    toObject: JSON.parse,
};
/**
 * Creates a schema for every property existing on the specified object and adds them to the given schema-map.
 * This function is intended to be used as argument of an `Object.entries(obj).forEach()` call.
 *
 * ---
 *
 * **Example:**
 * ~~~ts
 * const obj = { test: 42, ok: true };
 * const schemas = new Map<string, Schema>();
 *
 * Object.entries(obj).forEach(createAndAddSchema(schemas));
 * ~~~
 * @param schemas The schema-map to add the property schemas to.
 */
function createAndAddSchema(schemas) {
    return ([key, value]) => {
        const schema = createSchema(value);
        schemas.set(key, schema);
    };
}
/**
 * Creates a suitable schema for the given value and returns it.
 * @param value The value to create a schema for.
 */
function createSchema(value) {
    return {
        defaultValue: value,
        converterFn: getConverterFn(value),
    };
}
/**
 * Finds and returns a suitable converter function for the given value.
 * @param value The value to get a suitable converter function for.
 */
function getConverterFn(value) {
    switch (typeof value) {
        case 'boolean':
            return ValueConverters.toBool;
        case 'number':
            const hasDecimals = value.toString().includes('.');
            return hasDecimals ? ValueConverters.toFloat : ValueConverters.toInt;
        case 'object':
            return ValueConverters.toObject;
        default:
            return undefined;
    }
}

/**
 * Provides an api to read and write sections of the application-settings.
 *
 * ---
 * **Please Note:** This class is intended to be built by the `SettingsService`'s
 * `access` method:
 *
 * ~~~ts
 * class MyViewComponent {
 *   constructor(service: SettingsService) {
 *     this.settings = service.access<MySettings>('my-settings', {
 *          // default values:
 *          mySetting1: 'defaultValue',
 *          mySetting2: true,
 *     });
 *   }
 * }
 * ~~~
 */
class SettingsAccessor {
    get changes$() {
        return this.settings$.pipe(map(() => this.get()));
    }
    constructor(namespace, namespaceSchema, settings$, saveSettings) {
        this.namespace = namespace;
        this.namespaceSchema = namespaceSchema;
        this.settings$ = settings$;
        this.saveSettings = saveSettings;
    }
    update(values) {
        const namespace = this.namespace;
        const current = this.settings$.getValue() ?? {};
        this.updateAndEnsureCorrectTypes(current, values, namespace);
        this.settings$.next(current);
        this.saveSettings();
    }
    get(property) {
        const namespace = this.namespace;
        const namespaceSchemaMap = this.namespaceSchema;
        const settingsInNamespace = this.settings$.getValue()[namespace];
        const settingEntries = this.getSettingsAsEntries(settingsInNamespace, namespaceSchemaMap);
        return property
            ? this.getSetting(property, settingEntries, namespaceSchemaMap)
            : this.getAllSettings(settingEntries, namespaceSchemaMap);
    }
    getAllSettings(settingEntries, schemaMap) {
        const result = {};
        for (const [name] of settingEntries) {
            const setting = this.getSetting(name, settingEntries, schemaMap);
            result[name] = setting;
        }
        return result;
    }
    getSetting(name, settingEntries, namespaceSchemaMap) {
        const settingSchema = namespaceSchemaMap.get(name);
        const schemaDefaultValue = settingSchema?.defaultValue;
        const valueFromUserSetting = settingEntries.find(([settingName]) => settingName === name)?.[1];
        const value = valueFromUserSetting ?? schemaDefaultValue;
        // setting schema or value and default value not set
        if ([settingSchema, value].some(isNullOrUndefined)) {
            return;
        }
        // after check above schema is defined:
        const schema = settingSchema;
        let result = value;
        // if value is string it may need to be converted to type defined in schema
        if (typeof value === 'string' && schema.converterFn) {
            result = schema.converterFn(result);
        }
        return result;
    }
    getSettingsAsEntries(settingsInNamespace, namespaceSchemaMap) {
        const entries = settingsInNamespace
            ? Object.entries(settingsInNamespace)
            : Array.from(namespaceSchemaMap.entries()).map(([name, value]) => {
                return [name, value.defaultValue];
            });
        return entries;
    }
    updateAndEnsureCorrectTypes(current, values, namespace) {
        Object.entries(values).forEach(([name, value]) => {
            const schema = this.namespaceSchema.get(name);
            if (!schema) {
                throw new Error(`SettingsAccessor.update: Will not write unknown setting "${name}".`);
            }
            const correctType = typeof value === 'string' && schema.converterFn ? schema.converterFn(value) : value;
            if (current[namespace] === undefined) {
                current[namespace] = {};
            }
            const cloned = typeof correctType === 'object' ? { ...correctType } : correctType;
            current[namespace][name] = cloned;
        });
    }
}

class SettingsInfo {
}

/**
 * Service responsible for providing access to designated settings in local storage.
 *
 * ---
 * **Example:**
 * ~~~ts
 * const accessor = settingsService.access<CalcSettings>('calculator', {
 *    // settings with default values:
 *    hourlyRate: 123,
 *    withTaxes: true,
 * });
 *
 * accessor.get('hourlyRate'); // => current value of setting
 * accessor.get(); // current settings object
 * ~~~
 */
class SettingsService {
    get localStorageKey() {
        return this.settings.localStorageKey;
    }
    constructor(localStorage, settings) {
        this.localStorage = localStorage;
        this.settings = settings;
        this.namespaceSchemas = new Map();
        this._settings$ = new BehaviorSubject({});
        this.loadSettings();
    }
    access(namespace, defaultValues) {
        const settingsSchemas = new Map();
        Object.entries(defaultValues).forEach(createAndAddSchema(settingsSchemas));
        this.namespaceSchemas.set(namespace, settingsSchemas);
        return new SettingsAccessor(namespace, settingsSchemas, this._settings$, this.saveSettings.bind(this));
    }
    loadSettings() {
        const settings = this.localStorage.get(this.localStorageKey, JSON.parse) ?? {};
        this._settings$.next(settings);
    }
    saveSettings() {
        const settings = this._settings$.getValue();
        this.localStorage.set(this.localStorageKey, settings);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SettingsService, deps: [{ token: LocalStorageService }, { token: SettingsInfo }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SettingsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SettingsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: LocalStorageService }, { type: SettingsInfo }] });

class SettingsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SettingsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: SettingsModule, imports: [XGuideConfigurationModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SettingsModule, providers: [
            {
                provide: SettingsInfo,
                useFactory: (info) => createSettingsInfo(info),
                deps: [ApplicationInfo],
            },
        ], imports: [XGuideConfigurationModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: SettingsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [XGuideConfigurationModule],
                    providers: [
                        {
                            provide: SettingsInfo,
                            useFactory: (info) => createSettingsInfo(info),
                            deps: [ApplicationInfo],
                        },
                    ],
                }]
        }] });
function createSettingsInfo(app) {
    return class {
        get localStorageKey() {
            return `${app.name}:settings`;
        }
    };
}

/** The `TabsComponent` displays a horizontal list of selectable tabs. */
class TabsComponent {
    constructor() {
        /**
         * The list of tabs to show.
         */
        this.tabs = [];
        /**
         * Whether the tab labels should be translated.
         */
        this.translateTabs = false;
        /**
         * Whether this control ignores user-interaction.
         */
        this.disabled = false;
        /**
         * Event emitted whenever the selected tab changed.
         */
        this.selectedTabChange = new EventEmitter();
    }
    /** @ignore */
    onTabChange(tab) {
        if (this.disabled) {
            return;
        }
        if (this.selectedTab !== tab) {
            this.selectedTab = tab;
            this.selectedTabChange.emit(tab);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TabsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: TabsComponent, selector: "xui-tabs", inputs: { tabs: "tabs", translateTabs: "translateTabs", disabled: "disabled", selectedTab: "selectedTab" }, outputs: { selectedTabChange: "selectedTabChange" }, host: { classAttribute: "submenu" }, ngImport: i0, template: "<ng-container\n  *ngFor=\"let tab of tabs; let index = index\"\n  [ngTemplateOutlet]=\"tabItem\"\n  [ngTemplateOutletContext]=\"{ $implicit: tab }\"\n></ng-container>\n\n<!-- Need to declare tab inline, to not break styleguide -->\n<ng-template let-tab #tabItem>\n  <!-- ATOM: Tab -->\n  <div\n    class=\"tab-submenu\"\n    data-ngtx=\"tab-submenu:submenu\"\n    (click)=\"onTabChange(tab)\"\n    [class.disabled]=\"disabled\"\n    [class.selected]=\"tab === selectedTab\"\n  >\n    <div class=\"content\">\n      <span>\n        <ng-container *ngIf=\"translateTabs; else pureText\">\n          {{ tab.label | translate }}\n        </ng-container>\n\n        <ng-template #pureText>\n          {{ tab.label }}\n        </ng-template>\n      </span>\n    </div>\n  </div>\n</ng-template>\n", styles: [":host{display:flex;align-items:center;-webkit-user-select:none;user-select:none}.disabled{pointer-events:none}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: TranslationBridgePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TabsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-tabs', host: {
                        class: 'submenu',
                    }, template: "<ng-container\n  *ngFor=\"let tab of tabs; let index = index\"\n  [ngTemplateOutlet]=\"tabItem\"\n  [ngTemplateOutletContext]=\"{ $implicit: tab }\"\n></ng-container>\n\n<!-- Need to declare tab inline, to not break styleguide -->\n<ng-template let-tab #tabItem>\n  <!-- ATOM: Tab -->\n  <div\n    class=\"tab-submenu\"\n    data-ngtx=\"tab-submenu:submenu\"\n    (click)=\"onTabChange(tab)\"\n    [class.disabled]=\"disabled\"\n    [class.selected]=\"tab === selectedTab\"\n  >\n    <div class=\"content\">\n      <span>\n        <ng-container *ngIf=\"translateTabs; else pureText\">\n          {{ tab.label | translate }}\n        </ng-container>\n\n        <ng-template #pureText>\n          {{ tab.label }}\n        </ng-template>\n      </span>\n    </div>\n  </div>\n</ng-template>\n", styles: [":host{display:flex;align-items:center;-webkit-user-select:none;user-select:none}.disabled{pointer-events:none}\n"] }]
        }], propDecorators: { tabs: [{
                type: Input
            }], translateTabs: [{
                type: Input
            }], disabled: [{
                type: Input
            }], selectedTab: [{
                type: Input
            }], selectedTabChange: [{
                type: Output
            }] } });

const declarations$4 = [TabsComponent];
class TabsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: TabsModule, declarations: [TabsComponent], imports: [CommonModule, XGuideConfigurationModule], exports: [TabsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TabsModule, imports: [CommonModule, XGuideConfigurationModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TabsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, XGuideConfigurationModule],
                    declarations: [declarations$4],
                    exports: [declarations$4],
                }]
        }] });

/**
 * The tag component provided with text via the DOM content of the component
 * acts like a button with hover, disabled, focus and active states.
 * If the removable flag is set, a remove button is rendered and these interactions are only possible with the button itself.
 * A click on the button in the `removable` state emits an remove event.
 * ```html
 * <xui-tag [disabled]="false" [removable]="true" (remove)="doSomething()">
 *  Wärmeleitnaht
 * </xui-tag>
 * ```
 */
class TagComponent {
    constructor() {
        /**
         * Whether the control ignores user-interaction.
         */
        this.disabled = false;
        /**
         * If the `TagComponent` is removable, the component renders a remove-button.
         */
        this.removable = false;
        /**
         * Emitted whenever the tag should be removed.
         */
        this.remove = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TagComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: TagComponent, selector: "xui-tag", inputs: { disabled: "disabled", removable: "removable" }, outputs: { remove: "remove" }, host: { properties: { "class.disabled": "this.disabled", "class.removable": "this.removable" }, classAttribute: "interactive-element" }, ngImport: i0, template: "<ng-content></ng-content>\n\n<div *ngIf=\"removable\" class=\"remove\" data-ngtx=\"tag:remove\" (click)=\"remove.emit()\">\n  <xui-icon iconKey=\"Cross\" color=\"#fff\" size=\"xs\"></xui-icon>\n</div>\n", styles: ["@charset \"UTF-8\";:host{outline:none;display:inline-flex;border:1px solid #000;border-radius:16px;background:#fff;padding:7px 12px 6px;font-size:16px;font-weight:700;line-height:17px;letter-spacing:0}:host.removable{position:relative;padding:8px 40px 7px 12px;color:#fff;background:#285172;border:none}:host.removable.disabled{pointer-events:none;cursor:default;background:#a1a1a1}:host.removable.disabled .remove{background:transparent}:host:not(.removable){cursor:pointer}:host:not(.removable).disabled{pointer-events:none;cursor:default;color:#a1a1a1;border-color:#a1a1a1}:host:not(.removable):hover{color:#177ac9;border-color:#177ac9;box-shadow:0 2px 8px #51a7eb40}:host:not(.removable):active{color:#177ac9;background:#51a7eb1a;border-color:#51a7eb;box-shadow:0 2px 8px #51a7eb40}:host:not(.removable):focus{border-color:#177ac9;box-shadow:0 2px 8px #51a7eb40}.remove{cursor:pointer;height:100%;position:absolute;right:0;top:0;border-radius:0 16px 16px 0;display:flex;align-items:center;background:#285172}.remove xui-icon{padding:0 8px}.remove:hover{background:#51a7eb}.remove:active{background:#177ac9;box-shadow:0 2px 8px #51a7eb40}.remove:focus{box-sizing:border-box;border:1px solid #51a7eb;background:transparent;box-shadow:0 2px 8px #51a7eb99}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconComponent, selector: "xui-icon", inputs: ["size", "iconKey", "color", "outlined"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TagComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-tag', host: {
                        class: 'interactive-element',
                    }, template: "<ng-content></ng-content>\n\n<div *ngIf=\"removable\" class=\"remove\" data-ngtx=\"tag:remove\" (click)=\"remove.emit()\">\n  <xui-icon iconKey=\"Cross\" color=\"#fff\" size=\"xs\"></xui-icon>\n</div>\n", styles: ["@charset \"UTF-8\";:host{outline:none;display:inline-flex;border:1px solid #000;border-radius:16px;background:#fff;padding:7px 12px 6px;font-size:16px;font-weight:700;line-height:17px;letter-spacing:0}:host.removable{position:relative;padding:8px 40px 7px 12px;color:#fff;background:#285172;border:none}:host.removable.disabled{pointer-events:none;cursor:default;background:#a1a1a1}:host.removable.disabled .remove{background:transparent}:host:not(.removable){cursor:pointer}:host:not(.removable).disabled{pointer-events:none;cursor:default;color:#a1a1a1;border-color:#a1a1a1}:host:not(.removable):hover{color:#177ac9;border-color:#177ac9;box-shadow:0 2px 8px #51a7eb40}:host:not(.removable):active{color:#177ac9;background:#51a7eb1a;border-color:#51a7eb;box-shadow:0 2px 8px #51a7eb40}:host:not(.removable):focus{border-color:#177ac9;box-shadow:0 2px 8px #51a7eb40}.remove{cursor:pointer;height:100%;position:absolute;right:0;top:0;border-radius:0 16px 16px 0;display:flex;align-items:center;background:#285172}.remove xui-icon{padding:0 8px}.remove:hover{background:#51a7eb}.remove:active{background:#177ac9;box-shadow:0 2px 8px #51a7eb40}.remove:focus{box-sizing:border-box;border:1px solid #51a7eb;background:transparent;box-shadow:0 2px 8px #51a7eb99}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.disabled']
            }], removable: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.removable']
            }], remove: [{
                type: Output
            }] } });

const declarations$3 = [TagComponent];
class TagModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TagModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: TagModule, declarations: [TagComponent], imports: [CommonModule, IconModule], exports: [TagComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TagModule, imports: [CommonModule, IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TagModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, IconModule],
                    declarations: [declarations$3],
                    exports: [declarations$3],
                }]
        }] });

class TooltipComponent {
    constructor() {
        /** @ignore */
        this.defaultAnchorPositionClass = 'top-right';
        /** @ignore */
        this.defaultPosition = {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
        };
        /** @ignore For internal purposes only. Do not bind to this property. */
        this._showTooltip = false;
        /** @ignore For internal purposes only. Do not bind to this property. */
        this._tooltipPosition = 'top right';
        /** @ignore */
        this._positions = [];
        /** @ignore */
        this._infoTipClasses = this.getInfoTipCssClasses();
    }
    /** @ignore */
    updatePosition() {
        this._infoTipClasses = this.getInfoTipCssClasses();
        const position = this.getTooltipPosition();
        this._positions = [position];
    }
    /** @ignore */
    getInfoTipCssClasses() {
        return this.addPosition('infotip ');
    }
    /** @ignore */
    addPosition(classes) {
        if (!this._tooltipPosition) {
            return classes + this.defaultAnchorPositionClass;
        }
        const isTop = this._tooltipPosition.includes('top');
        const isLeft = this._tooltipPosition.includes('left');
        let result = classes;
        if (isTop && isLeft) {
            result += 'top-left';
        }
        else if (isTop && !isLeft) {
            result += 'top-right';
        }
        else if (!isTop && isLeft) {
            result += 'bottom-left';
        }
        else {
            result += 'bottom-right';
        }
        return result;
    }
    /** @ignore */
    getTooltipPosition() {
        if (!this._tooltipPosition) {
            return this.defaultPosition;
        }
        switch (this._tooltipPosition) {
            case 'top left':
                return {
                    originX: 'end',
                    originY: 'top',
                    overlayX: 'end',
                    overlayY: 'bottom',
                };
            case 'top right':
                return {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                };
            case 'bottom left':
                return {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                };
            case 'bottom right':
                return {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                };
            default:
                console.warn('invalid tooltip position given:', this._tooltipPosition);
                return this.defaultPosition;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TooltipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: TooltipComponent, selector: "xui-tooltip", inputs: { _tooltipTriggerRef: "_tooltipTriggerRef", _showTooltip: "_showTooltip", _tooltipPosition: "_tooltipPosition" }, ngImport: i0, template: "<ng-template\n  cdkConnectedOverlay\n  [cdkConnectedOverlayOrigin]=\"_tooltipTriggerRef\"\n  [cdkConnectedOverlayOpen]=\"_showTooltip\"\n  [cdkConnectedOverlayPositions]=\"_positions\"\n>\n  <div [ngClass]=\"_infoTipClasses\" data-ngtx=\"tooltip:root\">\n    <div class=\"text-container\" data-ngtx=\"tooltip:text-container\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:none}.infotip{z-index:300;margin:0;max-width:350px;max-height:unset;font-size:1rem;line-height:1.4rem}.infotip.top-left,.infotip.top-right{margin-top:3px}.infotip.bottom-left,.infotip.bottom-right{margin-bottom:3px}.infotip:after,.infotip:before{display:none!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.CdkConnectedOverlay, selector: "[cdk-connected-overlay], [connected-overlay], [cdkConnectedOverlay]", inputs: ["cdkConnectedOverlayOrigin", "cdkConnectedOverlayPositions", "cdkConnectedOverlayPositionStrategy", "cdkConnectedOverlayOffsetX", "cdkConnectedOverlayOffsetY", "cdkConnectedOverlayWidth", "cdkConnectedOverlayHeight", "cdkConnectedOverlayMinWidth", "cdkConnectedOverlayMinHeight", "cdkConnectedOverlayBackdropClass", "cdkConnectedOverlayPanelClass", "cdkConnectedOverlayViewportMargin", "cdkConnectedOverlayScrollStrategy", "cdkConnectedOverlayOpen", "cdkConnectedOverlayDisableClose", "cdkConnectedOverlayTransformOriginOn", "cdkConnectedOverlayHasBackdrop", "cdkConnectedOverlayLockPosition", "cdkConnectedOverlayFlexibleDimensions", "cdkConnectedOverlayGrowAfterOpen", "cdkConnectedOverlayPush", "cdkConnectedOverlayDisposeOnNavigation"], outputs: ["backdropClick", "positionChange", "attach", "detach", "overlayKeydown", "overlayOutsideClick"], exportAs: ["cdkConnectedOverlay"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-tooltip', template: "<ng-template\n  cdkConnectedOverlay\n  [cdkConnectedOverlayOrigin]=\"_tooltipTriggerRef\"\n  [cdkConnectedOverlayOpen]=\"_showTooltip\"\n  [cdkConnectedOverlayPositions]=\"_positions\"\n>\n  <div [ngClass]=\"_infoTipClasses\" data-ngtx=\"tooltip:root\">\n    <div class=\"text-container\" data-ngtx=\"tooltip:text-container\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";:host{display:none}.infotip{z-index:300;margin:0;max-width:350px;max-height:unset;font-size:1rem;line-height:1.4rem}.infotip.top-left,.infotip.top-right{margin-top:3px}.infotip.bottom-left,.infotip.bottom-right{margin-bottom:3px}.infotip:after,.infotip:before{display:none!important}\n"] }]
        }], propDecorators: { _tooltipTriggerRef: [{
                type: Input
            }], _showTooltip: [{
                type: Input
            }], _tooltipPosition: [{
                type: Input
            }] } });

class TooltipDirective {
    /** @ignore */
    static { this.defaultDelay = 200; }
    get nativeElement() {
        return this.elementRef?.nativeElement;
    }
    /** @ignore */
    constructor(elementRef) {
        this.elementRef = elementRef;
        /**
         * The tooltip component reference to use as visual template.
         */
        this.tooltip = undefined;
        /**
         * The amount of time in milliseconds to wait before showing the tooltip.
         */
        this.delay = TooltipDirective.defaultDelay;
        /**
         * The preferred tooltip position relative to the tooltip-decorated element to show the tooltip at.
         */
        this.tooltipPosition = 'top right';
        this.triggerRef = new CdkOverlayOrigin(elementRef);
    }
    /** @ignore */
    ngOnChanges(changes) {
        const tooltipKey = 'tooltip';
        const tooltipPosKey = 'tooltipPosition';
        const delayKey = 'delay';
        if (tooltipKey in changes) {
            this.tooltip._tooltipTriggerRef = this.triggerRef;
            this.tooltip._showTooltip = false;
        }
        if (tooltipPosKey in changes) {
            this.updateTooltipPosition();
        }
        if (delayKey in changes) {
            this.attachToHostEvents();
        }
    }
    /** @ignore */
    ngOnInit() {
        this.updateTooltipPosition();
        this.attachToHostEvents();
    }
    /** @ignore */
    ngOnDestroy() {
        this.subscriptions?.unsubscribe();
    }
    /** Programmatically shows the tooltip. */
    show() {
        this.tooltip._showTooltip = true;
    }
    /** Programmatically hides the tooltip. */
    hide() {
        this.tooltip._showTooltip = false;
    }
    /** Programmatically toggles the tooltip. */
    toggle() {
        this.tooltip._showTooltip = !this.tooltip._showTooltip;
    }
    /** @ignore */
    updateTooltipPosition() {
        this.tooltip._tooltipPosition = this.tooltipPosition;
        this.tooltip.updatePosition();
    }
    /** @ignore */
    attachToHostEvents() {
        this.subscriptions?.unsubscribe();
        this.subscriptions = new Subscription();
        const leave$ = fromEvent(this.nativeElement, 'mouseout');
        const enter$ = fromEvent(this.nativeElement, 'mouseover');
        const hover$ = enter$.pipe(delay(this.delay), takeUntil(leave$), repeat());
        this.subscriptions.add(hover$.subscribe(() => this.show()));
        this.subscriptions.add(leave$.subscribe(() => this.hide()));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TooltipDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.5", type: TooltipDirective, selector: "[xuiTooltip]", inputs: { tooltip: ["xuiTooltip", "tooltip"], delay: ["xuiTooltipDelay", "delay"], tooltipPosition: ["xuiTooltipPosition", "tooltipPosition"] }, exportAs: ["xuiTooltip"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[xuiTooltip]',
                    exportAs: 'xuiTooltip',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { tooltip: [{
                type: Input,
                args: ['xuiTooltip']
            }], delay: [{
                type: Input,
                args: ['xuiTooltipDelay']
            }], tooltipPosition: [{
                type: Input,
                args: ['xuiTooltipPosition']
            }] } });

const declarations$2 = [TooltipDirective, TooltipComponent];
class TooltipModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: TooltipModule, declarations: [TooltipDirective, TooltipComponent], imports: [CommonModule, OverlayModule], exports: [TooltipDirective, TooltipComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TooltipModule, imports: [CommonModule, OverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule],
                    declarations: [declarations$2],
                    exports: [declarations$2],
                }]
        }] });

/**
 * Allows you to bind against a view model property in order to manipulate it via value-transforms.
 * Value-transforms are other components such as `TimeConverterComponent` or `NumberFormatterComponent`
 * that can be applied by adding them as children to this component.
 *
 * Each value transform can transform and "transform back" the value given. Moreover this component
 * lets you add validation to your bound value. Whenever the user changes your transformed value,
 * this particular value then gets validated before being transformed back. For the case that validation fails,
 * you can provide a fallback value that is used then.
 *
 * ---
 *
 * ~~~html
 * <xui-value #ageValue [(binding)]="age" [fallbackValue]="18" [xuiValidateNumber]="{ greaterThanOrEqual: 18 }">
 *   <!-- insert value-transforms here: -->
 *   <xui-number-formatter [maxDecimals]="0"></xui-number-formatter>
 * </xui-value>
 *
 * <xui-input-field [(text)]="ageValue.value"></xui-input-field>
 * ~~~
 *
 * ---
 * **Please note:** The order of the value-transforms is important, as it is the exact
 * same order, in which the transformations will get applied.
 * ---
 * **Please note:** This component will not actually render anything, but is
 * completely invisible to the user.
 * ---
 * **Please note:** For more details see the `README.md`.
 */
class ValueComponent {
    constructor() {
        this.bindingChange = new EventEmitter();
        this.valueChange = new EventEmitter();
        this.unitChange = new EventEmitter();
        this.transforms = [];
        this.transformsReversed = [];
        this.validators = [];
    }
    ngDoCheck() {
        const hasValueChanged = this.cachedValue !== this.value;
        const hasUnitChanged = this.cachedUnit !== this.unit;
        if (hasValueChanged) {
            this.cachedValue = this.value;
            requestAnimationFrame(() => this.applyViewToData());
        }
        if (hasUnitChanged) {
            this.cachedUnit = this.unit;
            requestAnimationFrame(() => this.unitChange.emit(this.unit));
        }
    }
    ngOnChanges(changes) {
        if (changes['binding']) {
            requestAnimationFrame(() => this.applyDataToView());
        }
        if (changes['fallbackValue']) {
            this.updateFallbackValue(changes['fallbackValue'].firstChange);
        }
    }
    ngAfterViewInit() {
        if (this.unit == null) {
            const unit = this.transforms.find(t => !!t.unit)?.unit;
            if (unit) {
                this.setUnit(unit);
            }
        }
    }
    register(transform) {
        this.transforms.push(transform);
        this.transformsReversed = [...this.transforms].reverse();
    }
    unregister(transform) {
        this.transforms = this.transforms.filter(t => t !== transform);
        this.transformsReversed = [...this.transforms].reverse();
    }
    registerValidator(validator) {
        this.validators.push(validator);
    }
    unregisterValidator(validator) {
        this.validators = this.validators.filter(v => v !== validator);
    }
    forceUpdateView(value) {
        requestAnimationFrame(() => {
            this.value = value;
            this.valueChange.emit(this.value);
        });
    }
    transform(value) {
        let result = value;
        try {
            for (const transformer of this.transforms) {
                result = transformer.transform(result);
                if (this.debug != null)
                    console.log('trans', transformer.constructor.name, result);
            }
        }
        catch (err) {
            result = this.fallbackValue;
        }
        return result;
    }
    transformBack(value) {
        let result = value;
        try {
            for (const transformer of this.transformsReversed) {
                result = transformer.transformBack(result);
                if (this.debug != null)
                    console.log('back', transformer.constructor.name, result);
            }
        }
        catch (err) {
            result = this.fallbackValueView;
        }
        return result;
    }
    setUnit(unit) {
        requestAnimationFrame(() => {
            this.unit = unit;
            this.unitChange.emit(unit);
        });
    }
    isValid(value) {
        return this.validators
            .filter(validator => validator.canValidate(value))
            .every(validator => validator.isValid(value));
    }
    applyDataToView() {
        const value = this.transform(this.binding);
        if (this.value !== value) {
            this.value = value;
            this.valueChange.emit(this.value);
        }
    }
    applyViewToData() {
        const isValueValid = this.isValid(this.value);
        const binding = isValueValid ? this.transformBack(this.value) : this.fallbackValue;
        if (!isValueValid && this.fallbackValueView) {
            this.forceUpdateView(this.fallbackValueView);
        }
        if (this.binding !== binding) {
            this.binding = binding;
            this.bindingChange.emit(this.binding);
        }
    }
    updateFallbackValue(firstChange) {
        if (firstChange) {
            // wait for value transforms to be registered
            requestAnimationFrame(() => this.doUpdateFallbackValueNow());
        }
        else {
            this.doUpdateFallbackValueNow();
        }
    }
    doUpdateFallbackValueNow() {
        this.fallbackValueView = this.transform(this.fallbackValue);
        this.checkFallbackValidity();
    }
    checkFallbackValidity() {
        if (!this.isValid(this.fallbackValue)) {
            console.warn(`fallbackValue does not pass validators: ${this.fallbackValue}`);
        }
        if (!this.isValid(this.fallbackValueView)) {
            console.warn(`fallbackValueView does not pass validators: ${this.fallbackValueView}`);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ValueComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: ValueComponent, selector: "xui-value", inputs: { debug: "debug", binding: "binding", value: "value", fallbackValue: "fallbackValue", unit: "unit" }, outputs: { bindingChange: "bindingChange", valueChange: "valueChange", unitChange: "unitChange" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ValueComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-value', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], propDecorators: { debug: [{
                type: Input
            }], binding: [{
                type: Input
            }], bindingChange: [{
                type: Output
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], fallbackValue: [{
                type: Input
            }], unit: [{
                type: Input
            }], unitChange: [{
                type: Output
            }] } });

/**
 * Template component for creating custom transform components that work with the `xui-value` component.
 * In order to use this component just extend from it and implement the abstract methods.
 */
class TransformComponent {
    constructor(value) {
        this.value = value;
    }
    ngOnInit() {
        this.value.register(this);
    }
    ngOnDestroy() {
        this.value.unregister(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TransformComponent, deps: [{ token: ValueComponent }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: TransformComponent, selector: "xui-transform", ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TransformComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-transform', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], ctorParameters: () => [{ type: ValueComponent }] });

/**
 * Template component for creating custom, number-converter transform-components that work with the `xui-value` component.
 * In order to use this component just extend from it and implement the abstract methods.
 *
 * ---
 * **Please note:** Converters extending this component will automatically update the unit of the parent's `xui-value` component
 * to the currently active `targetUnit`. If you want to change this behavior, override the `ngOnChanges` method of this class with
 * your own implementation.
 */
class ConvertComponent extends TransformComponent {
    constructor() {
        super(...arguments);
        this.targetUnit = 'unknown';
        this.knownConverters = [];
    }
    transform(value) {
        return this.doConvert(value, 'to-view');
    }
    transformBack(value) {
        return this.doConvert(value, 'to-data');
    }
    ngOnChanges(changes) {
        const unitKey = 'targetUnit';
        if (unitKey in changes) {
            this.value.setUnit(this.targetUnit);
        }
    }
    getConverter(targetUnit, direction) {
        const converter = this.knownConverters.find((c) => c.to === targetUnit);
        if (converter == null) {
            console.warn(`Could not find any suitable converter for target-unit "${targetUnit}" in`, this.knownConverters);
        }
        return direction === 'to-view' ? converter?.convert : converter?.convertBack;
    }
    doConvert(value, direction) {
        if (value == null) {
            return value;
        }
        const asNumber = parseFloat(value);
        const converter = this.getConverter(this.targetUnit, direction);
        const converted = converter?.(asNumber) ?? asNumber;
        if (isNaN(converted)) {
            throw new Error(`value was not a valid number`);
        }
        return converted.toString();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ConvertComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: ConvertComponent, selector: "xui-convert", inputs: { targetUnit: "targetUnit" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ConvertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-convert', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], propDecorators: { targetUnit: [{
                type: Input
            }] } });

/**
 * Template component for creating custom, formatting transform-components that work with the `xui-value` component.
 * In order to use this component just extend from it and implement the abstract methods.
 */
class FormatComponent extends TransformComponent {
    transformBack(value) {
        const formatted = this.transform(value);
        if (formatted && formatted !== value) {
            this.value.forceUpdateView(formatted);
        }
        return formatted;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: FormatComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: FormatComponent, selector: "xui-format", usesInheritance: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: FormatComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-format', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }] });

var SystemOfUnits;
(function (SystemOfUnits) {
    SystemOfUnits["Metric"] = "Metric";
    SystemOfUnits["Imperial"] = "Imperial";
})(SystemOfUnits || (SystemOfUnits = {}));
var Currency;
(function (Currency) {
    Currency["Euro"] = "Euro";
    Currency["USDollar"] = "USDollar";
})(Currency || (Currency = {}));

const SHARED_SETTINGS_NAMESPACE = 'shared';
const DEFAULT_APP_SETTINGS = {
    unitSystem: SystemOfUnits.Metric,
    currency: Currency.Euro,
};

/**
 * A length-converter that works with the `<lsb-value>` component.
 *
 * ---
 *
 * ~~~html
 * <xui-value #seamLengthInCentimeterOrInch [binding]="seamLengthInMillimeters">
 *   <xui-length-converter metricTargetUnit="cm" imperialTargetUnit="inch"></lsb-length-converter>
 * <xui-value>
 *
 * <p>Seam length: {{ seamLengthInCentimeterOrInch.value }} {{ seamLengthInCentimeterOrInch.unit }}.</p>
 * ~~~
 * ---
 * **Please note:** This converter is dependent on the current app-settings.
 *
 * When the user setting for the unit-system is set to `SystemOfUnits.Imperial`, this converter
 * will use the `imperialTargetUnit` as conversion target.
 *
 * If the unit-system setting is set to `SystemOfUnits.Metric`, the `metricTargetUnit` is used as conversion
 * target.
 *
 * **This setting-dependency is also why using the `targetUnit` binding will throw an error.**.
 * Please always use the both input-properties `metricTargetUnit` and `imperialTargetUnit` as shown
 * in the example above.
 *
 * ---
 * **Please note:** This converter will auto-update the `unit` property of its parent `xui-value` component
 * to the current active conversion-target unit. This way you can easily bind to the `xui-value`'s unit as
 * shown in the code example above.
 */
class LengthConverterComponent extends ConvertComponent {
    constructor(value, settingsService) {
        super(value);
        this.metricTargetUnit = 'mm';
        this.imperialTargetUnit = 'inch';
        this.knownConverters = [...LengthConverters];
        this.settings = settingsService.access(SHARED_SETTINGS_NAMESPACE, DEFAULT_APP_SETTINGS);
    }
    get unit() {
        return this.systemOfUnits === SystemOfUnits.Metric
            ? this.metricTargetUnit
            : this.imperialTargetUnit;
    }
    get systemOfUnits() {
        return this.settings.get('unitSystem');
    }
    ngOnChanges() {
        if (this.targetUnit !== 'unknown') {
            throw new Error(`LengthConverter: Please use the "metricTargetUnit" and "imperialTargetUnit" @Inputs instead of "targetUnit".`);
        }
    }
    getConverter(_, direction) {
        const converter = this.systemOfUnits === SystemOfUnits.Metric
            ? this.knownConverters.find((c) => c.to === this.metricTargetUnit)
            : this.knownConverters.find((c) => c.to === this.imperialTargetUnit);
        return direction === 'to-view' ? converter?.convert : converter?.convertBack;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: LengthConverterComponent, deps: [{ token: ValueComponent }, { token: SettingsService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: LengthConverterComponent, selector: "xui-length-converter", inputs: { metricTargetUnit: "metricTargetUnit", imperialTargetUnit: "imperialTargetUnit" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: LengthConverterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-length-converter', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], ctorParameters: () => [{ type: ValueComponent }, { type: SettingsService }], propDecorators: { metricTargetUnit: [{
                type: Input
            }], imperialTargetUnit: [{
                type: Input
            }] } });
// -----------------------------------------
// ---------- Module Internals -------------
// -----------------------------------------
/* 1 inch = 2.54 cm */
const cmInchFactor = 2.54;
const LengthConverters = [
    // Length
    {
        from: 'mm',
        to: 'mm',
        convert: (mm) => mm,
        convertBack: (mm) => mm,
    },
    {
        from: 'mm',
        to: 'cm',
        convert: (mm) => mm / 10,
        convertBack: (cm) => cm * 10,
    },
    {
        from: 'mm',
        to: 'inch',
        convert: (mm) => mm / 10 / cmInchFactor,
        convertBack: (inch) => inch * cmInchFactor * 10,
    },
    // LengthRate
    {
        from: 'mm/s',
        to: 'mm/s',
        convert: (mmPerSec) => mmPerSec,
        convertBack: (mmPerSec) => mmPerSec,
    },
    {
        from: 'mm/s',
        to: 'm/min',
        convert: (mmPerSec) => (mmPerSec / 1000) * 60,
        convertBack: (mPerMin) => (mPerMin * 1000) / 60,
    },
    {
        from: 'mm/s',
        to: 'inch/min',
        convert: (mmPerSec) => (mmPerSec / 10 / cmInchFactor) * 60,
        convertBack: (inchPerMin) => (inchPerMin * cmInchFactor * 10) / 60,
    },
];

/**
 * A currency-converter that works with the `<xui-value>` component:
 *
 * ---
 *
 * ~~~html
 * <xui-value #priceInCents [binding]="priceInEur">
 *   <xui-currency-converter targetUnit="eur-cents"></xui-currency-converter>
 * </xui-value>
 *
 * <p>This product costs {{ priceInCents.value }} cents.</p>
 * ~~~
 */
class CurrencyConverterComponent extends ConvertComponent {
    constructor() {
        super(...arguments);
        this.targetUnit = 'eur';
        this.knownConverters = [...CurrencyConverters, ...RateConverters];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CurrencyConverterComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: CurrencyConverterComponent, selector: "xui-currency-converter", inputs: { targetUnit: "targetUnit" }, usesInheritance: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: CurrencyConverterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-currency-converter', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], propDecorators: { targetUnit: [{
                type: Input
            }] } });
// -----------------------------------------
// ---------- Module Internals -------------
// -----------------------------------------
const RateConverters = [
    {
        from: 'eur/sec',
        to: 'eur/sec',
        convert: (eurPerSec) => eurPerSec,
        convertBack: (eurPerSec) => eurPerSec,
    },
    {
        from: 'eur/sec',
        to: 'eur/min',
        convert: (eurPerSec) => eurPerSec * 60,
        convertBack: (eurPerSec) => eurPerSec / 60,
    },
    {
        from: 'eur/sec',
        to: 'eur/hours',
        convert: (eurPerSec) => eurPerSec * 3600,
        convertBack: (eurPerSec) => eurPerSec / 3600,
    },
];
const CurrencyConverters = [
    {
        from: 'eur',
        to: 'eur',
        convert: (eur) => eur,
        convertBack: (eur) => eur,
    },
    {
        from: 'eur',
        to: 'eur-cents',
        convert: (eur) => eur * 100,
        convertBack: (eur) => eur / 100,
    },
];

/**
 * A time-converter that works with the `<xui-value>` component:
 *
 * ---
 *
 * ~~~html
 * <xui-value #durationInMinutes [binding]="durationInSeconds">
 *   <xui-time-converter targetUnit="min"></xui-time-converter>
 * </xui-value>
 *
 * <p>Ready in {{ durationInMinutes.value }} minutes.</p>
 * ~~~
 */
class TimeConverterComponent extends ConvertComponent {
    constructor() {
        super(...arguments);
        this.knownConverters = [...TimeConverters];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TimeConverterComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: TimeConverterComponent, selector: "xui-time-converter", usesInheritance: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: TimeConverterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-time-converter', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }] });
// -----------------------------------------
// ---------- Module Internals -------------
// -----------------------------------------
const TimeConverters = [
    {
        from: 'sec',
        to: 'sec',
        convert: (sec) => sec,
        convertBack: (sec) => sec,
    },
    {
        from: 'sec',
        to: 'min',
        convert: (sec) => sec / 60,
        convertBack: (min) => min * 60,
    },
    {
        from: 'sec',
        to: 'hours',
        convert: (sec) => sec / 3600,
        convertBack: (hours) => hours * 3600,
    },
];

/**
 * Template directive for creating custom, value-validator directives that work with the `xui-value` component.
 * In order to use this directive just extend from it and implement the abstract methods.
 *
 * ---
 *
 * ~~~html
 * <xui-value
 *   [(binding)]="age"
 *   [xuiValidateNumber]="{ greaterThanOrEqual: 0, lessThan: 120 }"
 *   [fallback]="0"
 *   #validated
 * ></xui-value>
 *
 * <xui-input-field [(text)]="validated.value"></xui-input-field>
 * <p>Your age is: {{ age }}€ </p>
 * ~~~
 */
class ValidatorBase {
    constructor(value) {
        this.value = value;
    }
    ngOnInit() {
        this.value.registerValidator(this);
    }
    ngOnDestroy() {
        this.value.unregisterValidator(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ValidatorBase, deps: [{ token: ValueComponent, host: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.5", type: ValidatorBase, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ValidatorBase, decorators: [{
            type: Directive,
            args: [{}]
        }], ctorParameters: () => [{ type: ValueComponent, decorators: [{
                    type: Host
                }] }] });

/**
 * A number-validator that works with the `<xui-value>` component:
 *
 * ---
 *
 * ~~~html
 * <xui-value
 *   [(binding)]="age"
 *   [xuiValidateNumber]="{ greaterThanOrEqual: 0, lessThan: 120 }"
 *   [fallback]="0"
 *   #validated
 * ></xui-value>
 *
 * <xui-input-field [(text)]="validated.value"></xui-input-field>
 * <p>Your age is: {{ age }} </p>
 * ~~~
 */
class ValidateNumberDirective extends ValidatorBase {
    constructor() {
        super(...arguments);
        this.opts = {};
    }
    isValid(value) {
        if (value == null) {
            return false;
        }
        const asNumber = +value;
        if (isNaN(asNumber)) {
            return false;
        }
        if (this.opts.lessThan != null && asNumber >= this.opts.lessThan) {
            return false;
        }
        if (this.opts.lessThanOrEqual != null && asNumber > this.opts.lessThanOrEqual) {
            return false;
        }
        if (this.opts.greaterThan != null && asNumber <= this.opts.greaterThan) {
            return false;
        }
        if (this.opts.greaterThanOrEqual != null && asNumber < this.opts.greaterThanOrEqual) {
            return false;
        }
        return true;
    }
    canValidate(value) {
        const valueType = typeof value;
        return valueType === 'string' || valueType === 'number';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ValidateNumberDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.5", type: ValidateNumberDirective, selector: "[xuiValidateNumber]", inputs: { opts: ["xuiValidateNumber", "opts"] }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ValidateNumberDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[xuiValidateNumber]',
                }]
        }], propDecorators: { opts: [{
                type: Input,
                args: ['xuiValidateNumber']
            }] } });

/**
 * A value-validator that works with the `<xui-value>` component:
 *
 * ---
 *
 * ~~~html
 * <xui-value
 *   [(binding)]="name"
 *   xuiValidateRequired
 *   [fallback]="John Doe"
 *   #validated
 * ></xui-value>
 *
 * <xui-input-field [(text)]="validated.value"></xui-input-field>
 * <p> Welcome {{ name }}! </p>
 * ~~~
 */
class ValidateRequiredDirective extends ValidatorBase {
    constructor() {
        super(...arguments);
        this.required = true;
    }
    canValidate() {
        // do not validate, if required is set to false => optional
        return this.required;
    }
    isValid(value) {
        return value != null && value !== '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ValidateRequiredDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.5", type: ValidateRequiredDirective, selector: "[xuiValidateRequired]", inputs: { required: ["xuiRequired", "required"] }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ValidateRequiredDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[xuiValidateRequired]',
                }]
        }], propDecorators: { required: [{
                type: Input,
                args: ['xuiRequired']
            }] } });

/**
 * A number-formatter that works with the `<xui-value>` component:
 *
 * ---
 *
 * ~~~html
 * <xui-value #priceWithWithOnlyTwoDecimals [binding]="priceWithManyDecimals">
 *   <xui-number-formatter [maxDecimals]="2"></xui-number-formatter>
 * </xui-value>
 *
 * <p>Price: {{ priceWithWithOnlyTwoDecimals.value }}€ </p>
 * ~~~
 */
class NumberFormatterComponent extends FormatComponent {
    constructor() {
        super(...arguments);
        this.maxDecimals = 2;
    }
    transform(value) {
        if (value == null) {
            return value;
        }
        const fixedDecimals = parseFloat(value).toFixed(this.maxDecimals);
        return this.unit ? `${fixedDecimals} ${this.unit}` : fixedDecimals;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: NumberFormatterComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: NumberFormatterComponent, selector: "xui-number-formatter", inputs: { maxDecimals: "maxDecimals", unit: "unit" }, usesInheritance: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: NumberFormatterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-number-formatter', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], propDecorators: { maxDecimals: [{
                type: Input
            }], unit: [{
                type: Input
            }] } });

/**
 * A value-transformer that works with the `<xui-value>` component.
 * This component should not be used as it is just a workaround for edge-cases.
 * It works with side-effects and is not considered best practice.
 *
 * @deprecated This component should not be used as it is just a workaround for edge-cases. It works with side-effects and is not considered best practice.
 */
class ApplyValueComponent extends TransformComponent {
    transform(value) {
        if (this.target == null) {
            return;
        }
        const cloned = { ...this.target, [this.property]: value };
        return cloned;
    }
    transformBack(value) {
        throw new Error('Method not supported.');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ApplyValueComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: ApplyValueComponent, selector: "xui-apply-value", inputs: { property: "property", target: "target" }, usesInheritance: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ApplyValueComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-apply-value', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], propDecorators: { property: [{
                type: Input
            }], target: [{
                type: Input
            }] } });

/**
 * A weight-converter that works with the `<xui-value>` component.
 *
 * ---
 *
 * ~~~html
 * <xui-value #seamWeightInLbsOrKg [binding]="seamWeightInKg">
 *   <xui-weight-converter metricTargetUnit="cm" imperialTargetUnit="inch"></xui-weight-converter>
 * <xui-value>
 *
 * <p>Seam weight: {{ seamWeightInLbsOrKg.value }} {{ seamWeightInLbsOrKg.unit }}.</p>
 * ~~~
 * ---
 * **Please note:** This converter is dependent on the current app-settings.
 *
 * When the user setting for the unit-system is set to `SystemOfUnits.Imperial`, this converter
 * will use the `imperialTargetUnit` as conversion target.
 *
 * If the unit-system setting is set to `SystemOfUnits.Metric`, the `metricTargetUnit` is used as conversion
 * target.
 *
 * **This setting-dependency is also why using the `targetUnit` binding will throw an error.**.
 * Please always use the both input-properties `metricTargetUnit` and `imperialTargetUnit` as shown
 * in the example above.
 *
 * ---
 * **Please note:** This converter will auto-update the `unit` property of its parent `xui-value` component
 * to the current active conversion-target unit. This way you can easily bind to the `xui-value`'s unit as
 * shown in the code example above.
 */
class WeightConverterComponent extends ConvertComponent {
    constructor(value, settingsService) {
        super(value);
        this.metricTargetUnit = 'kg';
        this.imperialTargetUnit = 'lbs';
        this.knownConverters = [...weightConverters];
        this.settings = settingsService.access(SHARED_SETTINGS_NAMESPACE, DEFAULT_APP_SETTINGS);
    }
    get unit() {
        return this.systemOfUnits === SystemOfUnits.Metric
            ? this.metricTargetUnit
            : this.imperialTargetUnit;
    }
    get systemOfUnits() {
        return this.settings.get('unitSystem');
    }
    ngOnChanges() {
        if (this.targetUnit !== 'unknown') {
            throw new Error(`weightConverter: Please use the "metricTargetUnit" and "imperialTargetUnit" @Inputs instead of "targetUnit".`);
        }
    }
    getConverter(_, direction) {
        const converter = this.systemOfUnits === SystemOfUnits.Metric
            ? this.knownConverters.find((c) => c.to === this.metricTargetUnit)
            : this.knownConverters.find((c) => c.to === this.imperialTargetUnit);
        return direction === 'to-view' ? converter?.convert : converter?.convertBack;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: WeightConverterComponent, deps: [{ token: ValueComponent }, { token: SettingsService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: WeightConverterComponent, selector: "xui-weight-converter", inputs: { metricTargetUnit: "metricTargetUnit", imperialTargetUnit: "imperialTargetUnit" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: WeightConverterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-weight-converter', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], ctorParameters: () => [{ type: ValueComponent }, { type: SettingsService }], propDecorators: { metricTargetUnit: [{
                type: Input
            }], imperialTargetUnit: [{
                type: Input
            }] } });
// -----------------------------------------
// ---------- Module Internals -------------
// -----------------------------------------
/* 1 kg = 2.20462262 lbs */
const kgLbsFactor = 2.20462262;
const weightConverters = [
    // weight
    {
        from: 'kg',
        to: 'kg',
        convert: (kg) => kg,
        convertBack: (kg) => kg,
    },
    {
        from: 'kg',
        to: 'lbs',
        convert: (kg) => kg * kgLbsFactor,
        convertBack: (lbs) => lbs / kgLbsFactor,
    }
];

class PressureConverterComponent extends ConvertComponent {
    get unit() {
        return this.systemOfUnits === SystemOfUnits.Metric
            ? this.metricTargetUnit
            : this.imperialTargetUnit;
    }
    constructor(value, settingsService) {
        super(value);
        this.metricTargetUnit = 'N/mm²';
        this.imperialTargetUnit = 'psi';
        this.settings = settingsService.access(SHARED_SETTINGS_NAMESPACE, DEFAULT_APP_SETTINGS);
    }
    get systemOfUnits() {
        return this.settings.get('unitSystem');
    }
    getConverter(_, direction) {
        if (this.systemOfUnits === SystemOfUnits.Metric) {
            return oneToOneConverter;
        }
        else {
            return direction === 'to-view' ? convertMetricToImperial : convertImperialToMetric;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: PressureConverterComponent, deps: [{ token: ValueComponent }, { token: SettingsService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: PressureConverterComponent, selector: "xui-pressure-converter", inputs: { metricTargetUnit: "metricTargetUnit", imperialTargetUnit: "imperialTargetUnit" }, usesInheritance: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: PressureConverterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-pressure-converter', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], ctorParameters: () => [{ type: ValueComponent }, { type: SettingsService }], propDecorators: { metricTargetUnit: [{
                type: Input
            }], imperialTargetUnit: [{
                type: Input
            }] } });
// 1 N/mm²= 145.038 psi => 1 psi = 0.006894745 N/mm²
const psiFactor = 0.006894745;
const convertMetricToImperial = (m) => m * psiFactor;
const convertImperialToMetric = (m) => m / psiFactor;
const oneToOneConverter = (m) => m;

class ForceConverterComponent extends ConvertComponent {
    get unit() {
        return this.systemOfUnits === SystemOfUnits.Metric
            ? this.metricTargetUnit
            : this.imperialTargetUnit;
    }
    constructor(value, settingsService) {
        super(value);
        this.metricTargetUnit = 'kN';
        this.imperialTargetUnit = 'ton';
        this.knownConverters = [...ForceConverters];
        this.settings = settingsService.access(SHARED_SETTINGS_NAMESPACE, DEFAULT_APP_SETTINGS);
    }
    get systemOfUnits() {
        return this.settings.get('unitSystem');
    }
    getConverter(_, direction) {
        const converter = this.systemOfUnits === SystemOfUnits.Metric
            ? this.knownConverters.find((c) => c.to === this.metricTargetUnit)
            : this.knownConverters.find((c) => c.to === this.imperialTargetUnit);
        return direction === 'to-view' ? converter?.convert : converter?.convertBack;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ForceConverterComponent, deps: [{ token: ValueComponent }, { token: SettingsService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: ForceConverterComponent, selector: "xui-force-converter", inputs: { metricTargetUnit: "metricTargetUnit", imperialTargetUnit: "imperialTargetUnit" }, usesInheritance: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: ForceConverterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-force-converter', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:none!important}\n"] }]
        }], ctorParameters: () => [{ type: ValueComponent }, { type: SettingsService }], propDecorators: { metricTargetUnit: [{
                type: Input
            }], imperialTargetUnit: [{
                type: Input
            }] } });
// 1kN = 0.1124 ton force => 1 tonForce = 8.896797 kN
const tonForceFactor = 8.896797;
// Default is kN
const ForceConverters = [
    {
        from: 'kN',
        to: 'kN',
        convert: (kiloNewton) => kiloNewton,
        convertBack: (kiloNewton) => kiloNewton,
    },
    {
        from: 'kN',
        to: 'N',
        convert: (kiloNewton) => kiloNewton * 1000,
        convertBack: (newton) => newton / 1000,
    },
    {
        from: 'kN',
        to: 'ton',
        convert: (kiloNewton) => kiloNewton * tonForceFactor,
        convertBack: (tonForce) => tonForce / tonForceFactor,
    },
];

const declarations$1 = [
    // Base Components
    ValueComponent,
    ConvertComponent,
    FormatComponent,
    TransformComponent,
    // Converters
    CurrencyConverterComponent,
    LengthConverterComponent,
    TimeConverterComponent,
    WeightConverterComponent,
    // Validators
    ValidateNumberDirective,
    ValidateRequiredDirective,
    // Formatters
    NumberFormatterComponent,
    // Utility
    ApplyValueComponent,
    PressureConverterComponent,
    ForceConverterComponent,
];
class DataTransformModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DataTransformModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: DataTransformModule, declarations: [
            // Base Components
            ValueComponent,
            ConvertComponent,
            FormatComponent,
            TransformComponent,
            // Converters
            CurrencyConverterComponent,
            LengthConverterComponent,
            TimeConverterComponent,
            WeightConverterComponent,
            // Validators
            ValidateNumberDirective,
            ValidateRequiredDirective,
            // Formatters
            NumberFormatterComponent,
            // Utility
            ApplyValueComponent,
            PressureConverterComponent,
            ForceConverterComponent], imports: [CommonModule], exports: [
            // Base Components
            ValueComponent,
            ConvertComponent,
            FormatComponent,
            TransformComponent,
            // Converters
            CurrencyConverterComponent,
            LengthConverterComponent,
            TimeConverterComponent,
            WeightConverterComponent,
            // Validators
            ValidateNumberDirective,
            ValidateRequiredDirective,
            // Formatters
            NumberFormatterComponent,
            // Utility
            ApplyValueComponent,
            PressureConverterComponent,
            ForceConverterComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DataTransformModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: DataTransformModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [declarations$1],
                    exports: [declarations$1],
                }]
        }] });

/**
 * The `WizardComponent` is a tabbed, dialog-like view, which provides basic step-navigation functionalities.
 * It also supports translation of the tab-labels. This functionality is optional, though.
 *
 * This component is not ment to be used with the `DialogService.open` method directly, but should
 * be included in the template of a `DialogHostedView` component, which then in turn can be used
 * with the `DialogService`.
 *
 * ---
 *
 * **Example:**
 *
 * - `my-dialog-hosted-view.html`
 * ~~~html
 * <lsb-wizard [tabs]="myDialogSteps" [(selectedTab)]="currentStep" (finishWizard)="closeMyDialog()">
 *    <lsb-my-first-step *ngIf="currentStep.label === 'Step 1'"></lsb-my-first-step>
 *    <lsb-my-second-step *ngIf="currentStep.label === 'Step 2'"></lsb-my-second-step>
 * </lsb-wizard>
 * ~~~
 */
class WizardComponent {
    get isFirstTab() {
        return this.tabs.indexOf(this.selectedTab) === 0;
    }
    get isLastTab() {
        return this.tabs.indexOf(this.selectedTab) >= this.tabs.length - 1;
    }
    /** @ignore */
    constructor(translations) {
        this.translations = translations;
        /** The list of tabs to be rendered. */
        this.tabs = [];
        /** Whether to translate the tab labels. */
        this.translateTabs = false;
        /** Whether the finish button is enabled. */
        this.canFinish = true;
        /** Emitted whenever the selected tab changes. */
        this.selectedTabChange = new EventEmitter();
        /** Emitted whenever the wizard was finished, i.e. the user clicked the finish button. */
        this.finishWizard = new EventEmitter();
    }
    /** @ignore */
    ngOnInit() {
        if (!this.selectedTab) {
            this.selectedTab = this.tabs[0];
        }
    }
    /**
     * Programmatically navigates to the next tab.
     */
    navigateNext() {
        const selectedTabIndex = this.tabs.indexOf(this.selectedTab);
        const newIndex = Math.min(this.tabs.length - 1, selectedTabIndex + 1);
        this.onTabChange(this.tabs[newIndex]);
    }
    /**
     * Programmatically navigates to the previous tab.
     */
    navigateBack() {
        const selectedTabIndex = this.tabs.indexOf(this.selectedTab);
        const newIndex = Math.max(0, selectedTabIndex - 1);
        this.onTabChange(this.tabs[newIndex]);
    }
    /** @ignore */
    onTabChange(tab) {
        this.selectedTab = tab;
        this.selectedTabChange.emit(tab);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: WizardComponent, deps: [{ token: Translations }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.5", type: WizardComponent, selector: "xui-wizard", inputs: { tabs: "tabs", selectedTab: "selectedTab", translateTabs: "translateTabs", canFinish: "canFinish" }, outputs: { selectedTabChange: "selectedTabChange", finishWizard: "finishWizard" }, ngImport: i0, template: "<div class=\"wizard-overlay\">\n  <div class=\"wizard\">\n    <div class=\"wizard-top\">\n      <xui-tabs\n        [tabs]=\"tabs\"\n        [translateTabs]=\"translateTabs\"\n        [selectedTab]=\"selectedTab\"\n        (selectedTabChange)=\"onTabChange($event)\"\n      ></xui-tabs>\n    </div>\n\n    <div class=\"content\">\n      <ng-content></ng-content>\n    </div>\n\n    <div class=\"wizard-bottom\">\n      <!-- ORGANISM: Footer -->\n      <div class=\"footer\">\n        <div class=\"footer-btn-container\">\n          <xui-button [disabled]=\"isFirstTab\" (click)=\"navigateBack()\" data-ngtx=\"wizard:back\">\n            {{ translations.XGUIDE.DIALOG.BACK | translate }}\n          </xui-button>\n        </div>\n        <div class=\"footer-btn-container\">\n          <div class=\"button-container\">\n            <xui-button type=\"secondary\" (click)=\"finishWizard.emit('canceled')\" data-ngtx=\"wizard:cancel\">\n              {{ translations.XGUIDE.DIALOG.CANCEL | translate }}\n            </xui-button>\n          </div>\n          <div class=\"button-container\">\n            <xui-button\n              type=\"secondary\"\n              [disabled]=\"isLastTab && !canFinish\"\n              (click)=\"isLastTab ? finishWizard.emit('finished') : navigateNext()\"\n              data-ngtx=\"wizard:next\"\n            >\n              {{ (isLastTab ? translations.XGUIDE.DIALOG.FINISH : translations.XGUIDE.DIALOG.NEXT) | translate }}\n            </xui-button>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: ["@charset \"UTF-8\";:host{display:block;width:100%;height:100%;overflow:hidden}.wizard-overlay{display:flex;width:100%;height:100%;z-index:100}.wizard{display:flex;flex-direction:column;margin:auto;box-shadow:0 0 6px #899fb2;box-sizing:border-box;justify-items:stretch;position:relative;max-height:85vh;top:4.5vh}@media only screen and (max-device-width: 1919px){.wizard{width:915px;height:655px}}@media only screen and (min-device-width: 1920px){.wizard{width:1098px;height:786px}}.content{flex-grow:1;padding:12px;background:#fff;overflow-y:auto}.button-label{-webkit-user-select:none;user-select:none}\n"], dependencies: [{ kind: "component", type: ButtonComponent, selector: "xui-button", inputs: ["type", "iconKey", "disabled"] }, { kind: "component", type: TabsComponent, selector: "xui-tabs", inputs: ["tabs", "translateTabs", "disabled", "selectedTab"], outputs: ["selectedTabChange"] }, { kind: "pipe", type: TranslationBridgePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: WizardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'xui-wizard', template: "<div class=\"wizard-overlay\">\n  <div class=\"wizard\">\n    <div class=\"wizard-top\">\n      <xui-tabs\n        [tabs]=\"tabs\"\n        [translateTabs]=\"translateTabs\"\n        [selectedTab]=\"selectedTab\"\n        (selectedTabChange)=\"onTabChange($event)\"\n      ></xui-tabs>\n    </div>\n\n    <div class=\"content\">\n      <ng-content></ng-content>\n    </div>\n\n    <div class=\"wizard-bottom\">\n      <!-- ORGANISM: Footer -->\n      <div class=\"footer\">\n        <div class=\"footer-btn-container\">\n          <xui-button [disabled]=\"isFirstTab\" (click)=\"navigateBack()\" data-ngtx=\"wizard:back\">\n            {{ translations.XGUIDE.DIALOG.BACK | translate }}\n          </xui-button>\n        </div>\n        <div class=\"footer-btn-container\">\n          <div class=\"button-container\">\n            <xui-button type=\"secondary\" (click)=\"finishWizard.emit('canceled')\" data-ngtx=\"wizard:cancel\">\n              {{ translations.XGUIDE.DIALOG.CANCEL | translate }}\n            </xui-button>\n          </div>\n          <div class=\"button-container\">\n            <xui-button\n              type=\"secondary\"\n              [disabled]=\"isLastTab && !canFinish\"\n              (click)=\"isLastTab ? finishWizard.emit('finished') : navigateNext()\"\n              data-ngtx=\"wizard:next\"\n            >\n              {{ (isLastTab ? translations.XGUIDE.DIALOG.FINISH : translations.XGUIDE.DIALOG.NEXT) | translate }}\n            </xui-button>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: ["@charset \"UTF-8\";:host{display:block;width:100%;height:100%;overflow:hidden}.wizard-overlay{display:flex;width:100%;height:100%;z-index:100}.wizard{display:flex;flex-direction:column;margin:auto;box-shadow:0 0 6px #899fb2;box-sizing:border-box;justify-items:stretch;position:relative;max-height:85vh;top:4.5vh}@media only screen and (max-device-width: 1919px){.wizard{width:915px;height:655px}}@media only screen and (min-device-width: 1920px){.wizard{width:1098px;height:786px}}.content{flex-grow:1;padding:12px;background:#fff;overflow-y:auto}.button-label{-webkit-user-select:none;user-select:none}\n"] }]
        }], ctorParameters: () => [{ type: Translations }], propDecorators: { tabs: [{
                type: Input
            }], selectedTab: [{
                type: Input
            }], translateTabs: [{
                type: Input
            }], canFinish: [{
                type: Input
            }], selectedTabChange: [{
                type: Output
            }], finishWizard: [{
                type: Output
            }] } });

const declarations = [WizardComponent];
class WizardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: WizardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.5", ngImport: i0, type: WizardModule, declarations: [WizardComponent], imports: [CommonModule, ButtonModule, TabsModule, XGuideConfigurationModule], exports: [WizardComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: WizardModule, imports: [CommonModule, ButtonModule, TabsModule, XGuideConfigurationModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.5", ngImport: i0, type: WizardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ButtonModule, TabsModule, XGuideConfigurationModule],
                    declarations: [declarations],
                    exports: [declarations],
                }]
        }] });

/*
 * Public API Surface of xguide.
 * All features that should be exposed to public MUST be exported here.
 * All parts of a feature (modules, services, components, ...) needs to
 * be exported; use a separated `public-api.ts` file for that and simply
 * export it here:
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ApplyValueComponent, ArticleCarouselComponent, ButtonComponent, ButtonModule, CaptionLabelComponent, CaptionLabelListComponent, CaptionLabelModule, CardComponent, CardModule, CheckboxComponent, CheckboxModule, ClickUsageTrackingDirective, ComboboxComponent, ComboboxModule, ConvertComponent, CookieService, Currency, CurrencyConverterComponent, DashboardModule, DashboardTileComponent, DataTransformModule, DialogContainerComponent, DialogModule, DialogResult, DialogService, DropDownModule, DropdownComponent, DropdownItemComponent, ForceConverterComponent, FormatComponent, GalleryModule, GettingStartedTileComponent, IconComponent, IconModule, ImageUrlBridgePipe, InputFieldComponent, InputFieldModule, LengthConverterComponent, LocalStorageModule, LocalStorageService, MailToMenuTabComponent, MediaBaseComponent, MediaComponent, MediaGalleryComponent, MediaModule, MediaType, MenuComponent, MenuModule, MenuTabComponent, NumberFormatterComponent, PlaceholderComponent, PressureConverterComponent, PrivacyCookiePopupComponent, PrivacySettingsPopupComponent, SettingsAccessor, SettingsInfo, SettingsModule, SettingsService, SubMenuComponent, SubMenuSubTabComponent, SubMenuTabComponent, SystemOfUnits, TabsComponent, TabsModule, TagComponent, TagModule, TextFormatPipe, ThumbnailComponent, ThumbnailItemComponent, TimeConverterComponent, TooltipComponent, TooltipDirective, TooltipModule, TransformComponent, TranslationBridgePipe, UsageTrackingModule, UsageTrackingPermissionService, UsageTrackingSendImplementationService, UsageTrackingService, ValidateNumberDirective, ValidateRequiredDirective, ValueComponent, WeightConverterComponent, WelcomeTileComponent, WizardComponent, WizardModule, XGUIDE_PRIVACY_COOKIE_NAME, XGuideConfigurationModule, formatText, mailToLink };
